import { logger } from "@/lib/logger";
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

// Translation resources
const resources = {
  en: {
    translation: {
      common: {
        welcome: 'Welcome',
        loading: 'Loading...',
        save: 'Save',
        cancel: 'Cancel',
        submit: 'Submit',
        search: 'Search',
        filter: 'Filter',
        success: 'Success',
        error: 'Error',
        back: 'Back',
        next: 'Next',
        previous: 'Previous',
        close: 'Close',
        open: 'Open',
        view: 'View',
        edit: 'Edit',
        delete: 'Delete',
        confirm: 'Confirm',
        yes: 'Yes',
        no: 'No',
      },
      navigation: {
        home: 'Home',
        trials: 'Clinical Trials',
        about: 'About Us',
        regions: 'Regions',
        conditions: 'Conditions',
        contact: 'Contact',
        login: 'Login',
        register: 'Register',
        dashboard: 'Dashboard',
        profile: 'Profile',
      },
      trials: {
        clinicalTrial: 'Clinical Trial',
        enrollment: 'Enrollment',
        recruiting: 'Recruiting',
        phase1: 'Phase 1',
        phase2: 'Phase 2',
        phase3: 'Phase 3',
        phase4: 'Phase 4',
        eligibility: 'Eligibility Criteria',
        sponsor: 'Sponsor',
        location: 'Location',
        studyType: 'Study Type',
        status: 'Status',
        findTrials: 'Find Trials',
        matchMe: 'Match Me',
        learnMore: 'Learn More',
      },
      forms: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        dateOfBirth: 'Date of Birth',
        gender: 'Gender',
        medicalCondition: 'Medical Condition',
        currentMedications: 'Current Medications',
        allergies: 'Allergies',
        required: 'Required',
      },
      // Trial Site portal translations
      trialSite: {
        // Tab labels
        tabs: {
          dashboard: 'Dashboard',
          ecrf: 'eCRF',
          ecoa: 'eCOA',
          analytics: 'Analytics',
          staff: 'Staff',
          patients: 'Patients',
          recruitment: 'CRM',
          econsent: 'eConsent',
          safety: 'Safety/PV',
          ip: 'IRT / IP Mgmt',
          queries: 'Queries',
          regulatory: 'Regulatory',
          rfis: 'RFIs',
          inspections: 'Inspections',
          settings: 'Settings',
        },
        // Header & navigation
        header: {
          backToSiteDashboard: 'Back to Site Dashboard',
          siteManagement: 'Trial Site Management',
          activeContext: 'Active Context',
          organization: 'Organization',
        },
        // Tooltips for tabs
        tooltips: {
          dashboard: 'Your main page. See patient numbers, visit progress, and important alerts.',
          ecrf: 'Electronic forms where you record trial data for each patient visit.',
          ecoa: 'Health questionnaires that patients fill in themselves about how they feel.',
          analytics: 'Charts and numbers showing how well your site is doing.',
          staff: 'Add team members, set their passwords, and check their training.',
          patients: 'All your patients. Add new ones, check their status, and manage records.',
          recruitment: 'Find and enrol patients for trials. Track where each patient is in the process.',
          econsent: 'Get patient consent electronically. They sign on screen or via WhatsApp.',
          safety: 'Report health problems that happen during the trial. Very important — do this quickly.',
          ip: 'Manage study medicine. Track what was received, given out, and returned.',
          queries: 'Questions from the sponsor about your data. Open each one and fix the issue.',
          regulatory: 'Upload and manage required documents like ethics approvals and staff CVs.',
          rfis: 'Information requests from the sponsor. Answer them here.',
          inspections: 'Prepare for audits and inspections. Check if all documents are ready.',
          settings: 'Change your site name, logo, and other settings.',
        },
        // Dashboard-specific
        dashboard: {
          quickActions: 'Quick Actions',
          quickActionsDescription: 'Common tasks and shortcuts for site management',
          triggerMatching: 'Trigger My Site Matching',
          triggering: 'Triggering...',
          viewPatientRoster: 'View Patient Roster',
          visitSchedule: 'Visit Schedule',
          myAssessments: 'My Assessments',
          trainingVideos: 'Training Videos',
          viewTrainingManual: 'View Training Manual',
          dctInfrastructure: 'DCT Infrastructure',
          activeTrials: 'Active Trials',
          paidAddons: 'Paid Add-on Modules',
          paidAddonsDescription: 'Premium features to enhance your site\'s clinical and revenue operations',
          enrollmentForecast: 'Enrollment Forecast',
          enrollmentForecastDescription: 'AI-powered predictions for trial enrollment completion',
          patientManagementPro: 'Patient Management Pro',
          activeTrialsBilling: 'Active Trials & Billing',
          dctModule: 'DCT Module',
          inspectionReadiness: 'Inspection Readiness',
        },
        // Common UI elements
        common: {
          pending: 'Pending',
          inProgress: 'In Progress',
          submitted: 'Submitted',
          accepted: 'Accepted',
          overdue: 'Overdue',
          noSiteSelected: 'No site selected',
          questionnaires: 'Questionnaires',
          completed: 'Completed',
          totalAssigned: 'Total Assigned',
          assignments: 'Assignments',
          instrumentLibrary: 'Instrument Library',
          templates: 'Templates',
          research: 'Research',
          questionnaireBuilder: 'Questionnaire Builder',
          userManual: 'User Manual',
          ecoaManagement: 'eCOA Management',
          ecoaDescription: 'Electronic Clinical Outcome Assessments for patients and observers',
          inbox: 'Inbox',
          requestsForInformation: 'Requests for Information',
          rfisDescription: 'Manage and respond to sponsor RFIs and feasibility questionnaires',
          logExternalRfi: 'Log External RFI',
          siteSettings: 'Site Settings',
          siteSettingsDescription: 'Update site details and configure DCT modules',
          siteDetails: 'Site Details',
          editSiteDetails: 'Edit Site Details',
          configureDctModules: 'Configure DCT Modules',
          saveChanges: 'Save Changes',
          saving: 'Saving...',
          siteName: 'Site Name',
          city: 'City',
          country: 'Country',
          notSet: 'Not set',
        },
        // Investigator Dashboard
        investigator: {
          totalPatients: 'Total Patients',
          visitCompletion: 'Visit Completion',
          protocolDeviations: 'Protocol Deviations',
          adverseEvents: 'Adverse Events',
          requiresReview: 'Requires review',
          enrollmentStatus: 'Enrollment Status',
          screening: 'Screening',
          active: 'Active',
          withdrawn: 'Withdrawn',
          aeSeverity: 'AE Severity Distribution',
          mild: 'Mild',
          moderate: 'Moderate',
          severe: 'Severe',
          lifeThreatening: 'Life-threatening',
          recentActivity: 'Recent Site Activity',
          noRecentActivity: 'No recent activity',
          patientEnrolled: 'Patient Enrolled',
          patientAssigned: 'Patient Assigned',
        },
        // Analytics Dashboard
        analytics: {
          siteAnalyticsDashboard: 'Site Analytics Dashboard',
          performanceMetrics: 'Comprehensive performance metrics across all trials',
          performanceMetricsFor: 'Performance metrics for',
          refresh: 'Refresh',
          exportReport: 'Export Report',
          screeningConversion: 'Screening Conversion',
          visitCompletion: 'Visit Completion',
          protocolQuality: 'Protocol Quality',
          documentCompliance: 'Document Compliance',
          excellent: 'Excellent',
          needsImprovement: 'Needs improvement',
          onTime: 'on-time',
          openQueries: 'open queries',
          good: 'Good',
          reviewRequired: 'Review Required',
          monthlyRecruitment: 'Monthly Recruitment',
          patientsEnrolledLast30: 'Patients enrolled in last 30 days',
          totalEnrolled: 'Total Enrolled',
          activeAndCompleted: 'Active and completed patients',
          dropoutRate: 'Dropout Rate',
          withinAcceptableRange: 'Within acceptable range',
          aboveIndustryAverage: 'Above industry average',
          enrollmentTrends: 'Enrollment Trends',
          enrollmentTrendsDesc: 'Patient enrollment over the last 6 months',
          noEnrollmentData: 'No enrollment data available',
          visitStatusDistribution: 'Visit Status Distribution',
          visitStatusDesc: 'Current status of all patient visits',
          noVisitData: 'No visit data available',
          protocolDeviationsChart: 'Protocol Deviations',
          deviationsBySeverity: 'Breakdown by severity',
          noProtocolDeviations: 'No Protocol Deviations',
          excellentCompliance: 'Excellent protocol compliance',
          siteQualityScore: 'Site Quality Score',
          overallAssessment: 'Overall performance assessment',
          visitCompliance: 'Visit Compliance',
          queryResolution: 'Query Resolution',
          documentCompletion: 'Document Completion',
          onTimeVisits: 'On-Time Visits',
          overallPerformance: 'Overall Site Performance',
          exceptional: 'Exceptional',
          fair: 'Fair',
          failedToLoad: 'Failed to Load Analytics',
          retry: 'Retry',
          analyticsRefreshed: 'Analytics Refreshed',
          dataRefreshed: 'Data refreshed successfully.',
          allTrialsRefreshed: 'All trials data has been refreshed.',
          enrolled: 'Enrolled',
          scheduled: 'Scheduled',
          missed: 'Missed',
          major: 'Major',
          minor: 'Minor',
          queryResolutionLabel: 'Query resolution',
          screened: 'screened',
          visits: 'visits',
        },
        // Patient Roster
        roster: {
          visits: 'Visits',
          addPatient: 'Add Patient',
          bulkUpload: 'Bulk Upload',
          sendInvite: 'Send Invite',
          matchAllPatients: 'Match All Patients',
          thisMonth: 'This Month',
          verified: 'Verified',
          pendingLogin: 'Pending Login',
          matched: 'Matched',
          profileComplete: 'Profile Complete',
          total: 'Total',
          searchPlaceholder: 'Search by name or email...',
          loadingMore: 'Loading more...',
          loadingPatients: 'Loading patients...',
          errorLoadingPatients: 'Error loading patients',
          showing: 'Showing',
          of: 'of',
          patients: 'patients',
          patient: 'Patient',
          type: 'Type',
          contact: 'Contact',
          loginStatus: 'Login Status',
          healthProfile: 'Health Profile',
          consentStatus: 'Consent Status',
          trialMatching: 'Trial Matching',
          workflow: 'Workflow',
          actions: 'Actions',
          notSent: 'Not Sent',
          pending: 'Pending',
          complete: 'Complete',
          incomplete: 'Incomplete',
          notMatched: 'Not Matched',
          runMatch: 'Run Match',
          match: 'Match',
          open: 'Open',
          noContactInfo: 'No contact info',
          noPatients: 'No patients yet',
          noMatchingPatients: 'No patients found matching your search',
          tryAgain: 'Try Again',
          reloadPage: 'Reload Page',
        },
        // Staff Manager
        staff: {
          siteStaff: 'Site Staff',
          manageTeam: 'Manage the team members assigned to this trial site',
          addStaffMember: 'Add Staff Member',
          staffEmail: 'Staff Email',
          emailPlaceholder: 'staff@example.com',
          emailHint: 'If the user doesn\'t have an account, they\'ll receive an invitation email',
          firstName: 'First Name (Optional)',
          lastName: 'Last Name (Optional)',
          role: 'Role',
          selectRole: 'Select a role',
          adding: 'Adding...',
          addStaff: 'Add Staff',
          cancel: 'Cancel',
          loadingStaff: 'Loading staff...',
          errorLoadingStaff: 'Error loading staff members',
          name: 'Name',
          email: 'Email',
          compliance: 'Compliance',
          status: 'Status',
          active: 'Active',
          inactive: 'Inactive',
          noStaff: 'No staff members assigned yet',
          invitationSent: 'Invitation sent',
          staffAdded: 'Staff member added',
          failedToAdd: 'Failed to add staff',
          staffRemoved: 'Staff member removed',
          staffRemovedDesc: 'The staff member has been marked as inactive.',
          failedToRemove: 'Failed to remove staff',
          missingInfo: 'Missing information',
          provideEmailAndRole: 'Please provide both email and role',
          attestationRequired: 'Attestation required',
          attestationHint: 'Please confirm the staff member has been informed about data processing practices',
        },
        // Dashboard add-on cards (hardcoded strings in SiteDashboardTab)
        addons: {
          clinical: 'Clinical',
          revenue: 'Revenue',
          decentralized: 'Decentralized',
          complianceLabel: 'Compliance',
          patientManagementDesc: 'Complete OMOP mapping, diversity demographics, detailed medical conditions',
          omopSdtm: 'OMOP/SDTM data standardization',
          advancedDemographics: 'Advanced demographics tracking',
          medicalHistory: 'Medical history management',
          billingDesc: 'Manage active trials, protocol fee schedules, billable events, invoicing',
          trialActivation: 'Trial activation & management',
          feeSchedule: 'Protocol fee schedule setup',
          automatedInvoicing: 'Automated invoicing',
          dctDesc: 'Virtual visits, remote monitoring, digital consent, and telehealth integration',
          telehealth: 'Telehealth video consultations',
          remoteMonitoring: 'Remote patient monitoring',
          digitalConsent: 'Digital consent platform',
          inspectionDesc: 'Pre-inspection checklists, document gap analysis, and mock inspection training',
          ichGcpChecklist: '20+ ICH-GCP checklist items',
          etmfGapAnalysis: 'Automated eTMF gap analysis',
          mockInspection: 'Mock inspection Q&A training',
          offlineTooltip: 'Download patient data, forms, and protocols for offline field work in areas with no connectivity',
        },
        // Per-tab content strings extracted from individual tab files
        tabContent: {
          patients: {
            description: 'All people registered at your site. Click a name to see their details, visits, and trial matches.',
          },
          staff: {
            description: 'People who work at your site. Add new staff, change roles, and manage training certificates.',
          },
          regulatory: {
            description: 'Required documents and approvals for the government. Keep study setup, TMF documents, and data management up to date.',
            studySetup: 'Study Setup',
            tmfDocuments: 'TMF & Documents',
            dataManagement: 'Data Management',
            oversightStaff: 'Oversight & Staff',
          },
          safety: {
            description: 'Report and track if a patient feels unwell from the study medicine. Record side effects, review cases, and submit safety reports.',
            adverseEvents: 'Adverse Events',
            adverseEventsShort: 'AE/SAE',
            reviewReporting: 'Review & Reporting',
            reviewShort: 'Review',
            medications: 'Medications',
            compliance: 'Compliance',
          },
          ip: {
            title: 'IP Management & Randomization',
            help: 'Manage Investigational Product (IP) inventory, Interactive Response Technology (IRT/RTSM) for randomization and supply management, and central laboratory kit tracking.',
            learnMore: 'This module covers three core areas: IRT/RTSM for drug kit randomization, dispensing, and emergency unblinding; IP Inventory for drug accountability and storage compliance; and Central Lab for specimen kit tracking and biomarker results management. All operations are logged for ICH-GCP §5.14 compliance.',
            irtRtsm: 'IRT / RTSM',
            ipInventory: 'IP Inventory',
            centralLab: 'Central Lab',
          },
          ecoa: {
            tipQuestionnaires: 'Total active questionnaires available for assignment',
            tipPending: 'Assignments awaiting completion by patients or observers',
            tipCompleted: 'Successfully submitted questionnaires with scored results',
            tipTotal: 'Total questionnaire assignments across all patients',
            builderHelp: "Create custom eCOA questionnaires from scratch or customize imported instruments for your trial's specific needs.",
            builderDesc: 'Create custom eCOA questionnaires or import from the library',
          },
          etmf: {
            siteEtmfTitle: 'Site eTMF Documents',
            headerNote: "View and track your site's essential documents filed against the DIA TMF Reference Model (v3.3). Documents uploaded here or via the CRO's AI Filing panel appear organized by zone. Site-specific docs include IRB approvals, staff CVs, delegation logs, and training records.",
            overallCompleteness: 'Overall Completeness',
            totalDocuments: 'Total Documents',
            zonesWithGaps: 'Zones with Gaps',
            acrossZones: 'Across {{n}} of 13 zones',
            zonesBelowExpected: 'Zones below expected document count',
            zoneCompletenessTitle: 'Zone Completeness — DIA TMF v3.3',
            zoneLabel: 'Zone',
            docsCount: '{{actual}}/{{expected}} docs',
            docsFiled: '{{actual}} of {{expected}} expected documents filed',
            clickToFilter: 'Click to filter documents by this zone',
            siteDocuments: 'Site Documents',
            searchPlaceholder: 'Search documents...',
            loading: 'Loading documents...',
            noDocsFound: 'No documents found',
            emptyHintNone: 'Documents filed via the CRO AI Filing panel or uploaded directly will appear here.',
            emptyHintFiltered: 'Try adjusting your search or zone filter.',
            colDocument: 'Document',
            colType: 'Type',
            colZone: 'Zone',
            colStatus: 'Status',
            colDate: 'Date',
            unfiled: 'Unfiled',
          },
          dashboard: {
            fieldWorker: 'Field Worker',
            fieldWorkerTooltip: 'Simplified mobile interface for field data collection — vitals, eCOA, documents, AI diagnosis',
          },
          settings: {
            title: 'Site Settings',
            selectCountry: 'Select country',
            statusActive: 'Active',
            reasonPlaceholder: 'Describe the reason for this change...',
            brandingUpdatedTitle: 'Branding updated',
            brandingUpdatedDesc: 'Your site branding has been updated successfully',
            complianceInfra: 'Compliance & Infrastructure',
            assessmentsLifecycle: 'Assessments & Lifecycle',
             totRequiresOrg: 'Transfer of Obligations requires an organization link.',
          },
        },
        // Per-component UI chrome strings (Batch 2)
        components: {
          rosterStats: {
            activationRate: 'Activation Rate',
            profileCompletionRate: 'Profile Completion Rate',
            verifiedSummary: '{{verified}} of {{total}} patients verified',
            profileSummary: '{{complete}} of {{total}} patients have complete profiles',
          },
          rosterActions: {
            consentForm: 'Consent Form',
            scanPatientForm: 'Scan Patient Form',
          },
          portalStatus: {
            active: 'Portal Active',
            invited: 'Invite Sent',
            pending: 'Pending',
            noAccess: 'No Portal',
          },
          trialSelector: {
            loading: 'Loading trials...',
            filterLabel: 'Trial Filter:',
            placeholder: 'Select a trial',
            allTrials: 'All Trials',
            aggregate: 'Aggregate',
          },
          readinessBadge: {
            trialReady: 'Trial Ready',
            scoreTitle: 'Trial Readiness Score',
            scoreLevel: '{{level}} ({{score}}/100)',
            assessmentValue: 'Assessment Value',
            assessmentValueDesc: 'This assessment is worth $5K-$30K in CRO consulting fees',
            readinessLevel: 'Trial Readiness: {{level}}',
            scoredAs: 'Scored as: {{type}}',
            breakdownTitle: 'Score Breakdown:',
            infrastructure: 'Infrastructure:',
            regulatory: 'Regulatory:',
            staffing: 'Staffing:',
            facility: 'Facility:',
            experience: 'Experience:',
            siteType: {
              hospital: 'Hospital / Medical Center',
              dayClinic: 'Day Clinic / Outpatient',
              researchCenter: 'Research Center',
              communityHealth: 'Community Health Center',
            },
          },
          flowPipeline: {
            title: 'Trial Flow Pipeline',
            total: '{{n}} Total',
            screenRate: 'Screen Rate',
            enrollRate: 'Enroll Rate',
            randomizeRate: 'Randomize Rate',
            completionRate: 'Completion Rate',
            stages: {
              matched: 'Matched',
              screening: 'Screening',
              consent: 'Consented',
              enrolled: 'Enrolled',
              randomized: 'Randomized',
              active: 'Active',
              completed: 'Completed',
              billed: 'Billed',
            },
          },
          featureGate: {
            checkingAccess: 'Checking access...',
            upgradeRequired: 'Upgrade Required',
            featureRequiresTier: 'This feature requires the',
            subscriptionTier: 'subscription tier.',
            perMonth: '/month',
            currentlyOn: "You're currently on the",
            tierSuffix: 'tier.',
            upgradeSubscription: 'Upgrade Subscription',
          },
          offlineCache: {
            title: 'Offline Cache Status',
            online: 'Online',
            offline: 'Offline',
            description: 'Monitor cached data and pending synchronization',
            pendingSyncOne: '{{n}} item pending sync',
            pendingSyncOther: '{{n}} items pending sync',
            syncNow: 'Sync Now',
            conflictsOne: '{{n}} sync conflict detected',
            conflictsOther: '{{n}} sync conflicts detected',
            cachedItems: 'Cached Items',
            offlinePatients: 'Offline Patients',
            queuedFiles: 'Queued Files',
            queuedActions: 'Queued Actions',
            lastCached: 'Last cached: {{time}}',
            expiredOne: '{{n}} expired item in cache',
            expiredOther: '{{n}} expired items in cache',
            refresh: 'Refresh',
            clearCache: 'Clear Cache',
            clearTitle: 'Clear All Cached Data?',
            clearDesc: "This will remove all cached data from this device. Pending syncs will NOT be affected. You'll need to prepare for offline again before going to the field.",
            cancel: 'Cancel',
            syncing: 'Syncing...',
            syncAll: 'Sync All ({{n}} items)',
            allSynced: 'All data synced',
            cannotSyncOffline: 'Cannot sync while offline',
            syncCompleted: 'Sync completed',
            syncFailed: 'Sync failed',
            cacheCleared: 'Cache cleared',
            clearFailed: 'Failed to clear cache',
          },
          prepareOffline: {
            ready: 'Ready',
            stale: 'Stale',
            offlineReady: 'Offline Ready',
            prepareOffline: 'Prepare Offline',
            title: 'Offline Preparation',
            description: 'Download patient data, forms, and protocols for offline field work',
            patients: '{{n}} Patients',
            consentForms: '{{n}} Consent Forms',
            ecrfTemplates: '{{n}} eCRF Templates',
            protocols: '{{n}} Protocols',
            lastPrepared: 'Last prepared: {{time}}',
            preparing: 'Preparing...',
            refreshData: 'Refresh Data',
            prepareForOffline: 'Prepare for Offline',
            clearTitle: 'Clear Offline Data?',
            clearDesc: "This will remove all cached data including patients, forms, and protocols. You'll need to prepare again before going offline.",
            cancel: 'Cancel',
            clearData: 'Clear Data',
            offlineWarning: "You're offline. Connect to prepare data.",
          },
          croSync: {
            loading: 'Loading...',
            notManaged: 'Not managed by CRO',
            neverSynced: 'Never synced',
            managedBy: 'Managed by {{name}}',
            lastSync: 'Last sync: {{time}}',
            activeTrials: 'Active trials: {{n}}',
            triggerSync: 'Trigger manual sync',
            croManaged: 'CRO Managed',
          },
          inviteLink: {
            title: 'Site Invite Link',
            description: 'Create a reusable registration link for this trial site that can be shared via bulk emails or marketing materials.',
            customSlug: 'Custom URL Slug',
            slugPlaceholder: 'e.g., cape-town-research',
            slugHelp: 'Use lowercase letters, numbers, and hyphens only',
            autoGenerate: 'Auto-generate from site name',
            updating: 'Updating...',
            updateLink: 'Update Invite Link',
            createLink: 'Create Invite Link',
            registrationLink: 'Registration Link',
            copyToClipboard: 'Copy to clipboard',
            previewPage: 'Preview registration page',
            sendInvitation: 'Send Invitation',
            sendInvitationDesc: 'Send a templated invitation directly from the system via Email, SMS, or WhatsApp.',
            email: 'Email',
            sms: 'SMS',
            whatsapp: 'WhatsApp',
            recipientNameLabel: 'Recipient Name (optional)',
            recipientNamePlaceholder: 'e.g., John Doe',
            emailAddress: 'Email Address',
            phoneNumber: 'Phone Number',
            sending: 'Sending...',
            sendVia: 'Send via {{channel}}',
            qrCode: 'QR Code',
            downloadPng: 'Download PNG',
            qrHelp: 'Print this QR code for posters, flyers, or clinic displays',
            howToUse: 'How to use:',
            howSendDirect: 'Send invitations directly via Email, SMS, or WhatsApp above',
            howCopyLink: 'Copy the link for bulk emails or marketing materials',
            howPrintQr: 'Print the QR code for clinic posters or marketing materials',
            howAutoAssign: 'Patients will register directly and be auto-assigned to {{name}}',
            notConfigured: 'Not configured yet',
            // Toast titles
            downloaded: 'Downloaded!',
            qrSaved: 'QR code saved as PNG',
            noLink: 'No Link Available',
            configureSlugFirst: 'Please configure the invite slug first.',
            copied: 'Copied!',
            copiedDesc: 'Invite link copied to clipboard',
            invalidSlug: 'Invalid Slug',
            invalidSlugDesc: 'Please enter a valid slug.',
            slugExists: 'Slug Already Exists',
            slugExistsDesc: 'This slug is already in use. Please choose a different one.',
            success: 'Success',
            updateSuccess: 'Invite link updated successfully',
            error: 'Error',
            updateFailed: 'Failed to update invite link',
            noInviteLink: 'No Invite Link',
            configureFirst: 'Configure the invite slug first.',
            validationError: 'Validation Error',
            invitationSent: 'Invitation Sent!',
            inviteSentDesc: 'Invite sent via {{channel}} to {{contact}}',
            failedToSend: 'Failed to Send',
            failedToSendDesc: 'Could not send the invitation. Please try again.',
          },
        },
      },
      cdst: {
        title: 'AI Clinical Decision Support',
        patientIntake: 'Patient Information',
        chiefComplaint: 'Chief Complaint',
        dangerSigns: 'Danger Signs',
        triageResult: 'Triage Result',
        emergency: 'Emergency',
        urgent: 'Urgent',
        standard: 'Standard',
        preventive: 'Preventive',
        referTo: 'Refer to',
        trialEligible: 'Eligible for Clinical Trial',
        suspectedCondition: 'Suspected Condition',
        recommendedAction: 'Recommended Action',
        korogwe: 'Korogwe District',
        handeni: 'Handeni District',
        offlineAssessment: 'Offline Assessment',
        fullAiAssessment: 'Full AI Assessment',
        connectivityRequired: 'Internet connectivity needed for full diagnosis',
        fever: 'Fever',
        cough: 'Cough',
        diarrhea: 'Diarrhea',
        headache: 'Headache',
        bodyPain: 'Body Pain',
        vomiting: 'Vomiting',
        breathingDifficulty: 'Breathing Difficulty',
        rash: 'Rash',
        convulsions: 'Convulsions',
        lethargy: 'Lethargy',
      },
      onboarding: {
        welcomePatient: 'Welcome to PTC BRIDGE™',
        welcomePatientDesc: 'Your platform for finding and participating in clinical trials',
        welcomePatientContent: 'We\'ll guide you through the key features to help you get started on your clinical trial journey.',
        completeProfile: 'Complete Your Profile',
        completeProfileDesc: 'Build your health profile for better trial matching',
        completeProfileContent: 'Add your medical conditions, medications, and demographic information. A complete profile helps our AI find the most relevant trials for you.',
        findTrials: 'Find Matching Trials',
        findTrialsDesc: 'Discover clinical trials using AI-powered matching',
        applyToTrials: 'Apply to Trials',
        applyToTrialsDesc: 'Express interest and track your applications',
        stayConnected: 'Stay Connected',
        stayConnectedDesc: 'Receive updates and manage communications',
        welcomeSiteStaff: 'Welcome to Trial Site Management',
        welcomeSiteStaffDesc: 'Your platform for managing clinical trial operations',
        welcomeSiteStaffContent: 'We\'ll guide you through the essential tools for managing patients, visits, documents, and billing.',
        dashboardOverview: 'Dashboard Overview',
        dashboardOverviewDesc: 'Monitor key metrics and quick actions',
        patientManagement: 'Patient Management',
        patientManagementDesc: 'Manage your patient roster and enrollment',
        visitScheduling: 'Visit Scheduling',
        visitSchedulingDesc: 'Schedule and track patient visits',
        docsRegulatory: 'Documents & Regulatory',
        docsRegulatoryDesc: 'Manage consent forms and lab results',
        activeBilling: 'Active Trials & Billing',
        activeBillingDesc: 'Protocol fee schedules and invoicing',
        skipTutorial: 'Skip tutorial',
        getStarted: 'Get Started',
        replayOnboarding: 'Replay Onboarding Tutorial',
        onboardingReset: 'Onboarding reset',
        onboardingResetDesc: 'Refresh the page to see the onboarding tutorial again.',
      },
      checklist: {
        gettingStarted: 'Getting Started',
        siteSetup: 'Getting Started — Site Setup',
        phcFirstSteps: 'Getting Started — Your First Steps',
        complete: 'complete',
        allSet: 'All set! You\'re ready to go.',
        dismiss: 'Dismiss',
        addFirstPatient: 'Add your first patient',
        addFirstPatientDesc: 'Register or invite a patient to your trial site roster.',
        inviteStaff: 'Invite a staff member',
        inviteStaffDesc: 'Add coordinators, investigators, or nurses to your team.',
        scheduleVisit: 'Schedule a patient visit',
        scheduleVisitDesc: 'Create a screening, follow-up, or study completion visit.',
        setupAutoRules: 'Set up form auto-assignment rules',
        setupAutoRulesDesc: 'Configure which eCRF/eCOA forms are auto-assigned when patients change stage.',
        registerFirstPatient: 'Register your first patient',
        registerFirstPatientDesc: 'Add a new patient — capture their name, date of birth, and consent.',
        completeConsultation: 'Complete a consultation',
        completeConsultationDesc: 'Record symptoms, examination, diagnosis, and treatment plan for a patient.',
        syncData: 'Sync your data',
        syncDataDesc: 'Upload the data you collected offline to the central server.',
        completeTraining: 'Complete a BlueCloudX training module',
        completeTrainingDesc: 'Learn clinical trial skills with free online courses.',
      },
      emptyStates: {
        noPatients: 'No patients enrolled yet',
        noPatientsDesc: 'Add your first patient to start managing their trial participation.',
        noVisits: 'No visits scheduled',
        noVisitsDesc: 'Schedule a screening, follow-up, or study completion visit for a patient.',
        noForms: 'No forms assigned',
        noFormsDesc: 'Assign eCRF or eCOA forms to patients manually or set up auto-assignment rules.',
        noTrials: 'No active trials',
        noTrialsDesc: 'Add an active trial from your sponsor contracts to begin enrollment.',
        noData: 'No data yet',
        noDataDesc: 'Data will appear here as you use the platform.',
      },
      tours: {
        takeTour: 'Take the Tour',
        replayTour: 'Replay Tour',
        siteTour: 'Site Tour',
        phcTour: 'PHC Tour',
        croTour: 'CRO Tour',
      },
      chw: {
        aiDiagnosis: 'Use this to help identify what illness a patient may have. Answer the questions about symptoms and the AI will suggest possible diagnoses using WHO IMCI guidelines. Always confirm with your supervisor for serious cases.',
        medicalInfo: 'Record the patient\'s health history here — existing conditions (like diabetes or HIV), current medications, and any allergies.',
        recordVitals: 'Measure and record the patient\'s blood pressure, temperature, weight, height, and heart rate. Do this at every visit.',
        labResults: 'Enter laboratory test results here, or use the camera to scan a printed lab report.',
        screening: 'Administer validated clinical screening questionnaires. These can be given in local languages. Follow the prompts carefully.',
        prescriptions: 'Dispense medications from the clinic formulary. Select the drug, dosage, and duration. Always check for allergies before dispensing.',
        growth: 'Track child growth using WHO standards. Enter weight, height, and head circumference.',
        immunizations: 'View and update the child\'s immunization schedule. Record vaccines given and schedule the next visit.',
        postnatalCare: 'Follow the WHO postnatal care schedule for mothers and newborns.',
        ncd: 'Manage patients with chronic conditions using WHO PEN guidelines.',
        consultation: 'Start a full clinical consultation for this patient.',
      },
      // ===== Namespaces backported from PT to fix EN-side missing keys =====
      // These are referenced by PatientDashboard.tsx, TrialSiteDetail.tsx, SiteRFIsTab.tsx
      // and others. Without them, English users see raw key strings.
      consent: {
        informedConsent: 'Informed Consent',
        dataPrivacy: 'Data Privacy',
        personalDataRights: 'Personal Data Rights',
        dataProcessing: 'Data Processing',
        consentRequired: 'Consent Required',
        crossBorderTransfer: 'Cross-Border Transfer',
        title: 'Consent Form',
        readCarefully: 'Read Carefully',
        signature: 'Signature',
        agreeToTerms: 'I Agree to the Terms',
        signHere: 'Sign Here',
        dateOfConsent: 'Date of Consent',
        witnessSignature: 'Witness Signature',
        consentGiven: 'Consent Given',
        consentPending: 'Consent Pending',
        consentWithdrawn: 'Consent Withdrawn',
      },
      matching: {
        foundTrials: 'Trials Found',
        matchScore: 'Match Score',
        eligibilityMatch: 'Eligibility Match',
        viewDetails: 'View Details',
        applyNow: 'Apply Now',
        noMatches: 'No Matches Found',
        refineSearch: 'Refine Search',
        highMatch: 'High Match',
        mediumMatch: 'Medium Match',
        lowMatch: 'Low Match',
      },
      siteStaff: {
        patients: 'Patients',
        visits: 'Visits',
        enrollment: 'Enrollment',
        activeTrials: 'Active Trials',
        scheduleVisit: 'Schedule Visit',
        viewPatient: 'View Patient',
        patientRecords: 'Patient Records',
        addPatient: 'Add Patient',
        patientList: 'Patient List',
        upcomingVisits: 'Upcoming Visits',
        completedVisits: 'Completed Visits',
        missedVisits: 'Missed Visits',
      },
      dashboard: {
        overview: 'Overview',
        myTrials: 'My Trials',
        upcomingVisits: 'Upcoming Visits',
        messages: 'Messages',
        documents: 'Documents',
        settings: 'Settings',
        notifications: 'Notifications',
        quickActions: 'Quick Actions',
        recentActivity: 'Recent Activity',
        helpCenter: 'Help Center',
      },
      errors: {
        genericError: 'An error occurred',
        networkError: 'Network error. Check your connection.',
        authError: 'Authentication error. Please sign in again.',
        validationError: 'Validation error. Check your data.',
        notFound: 'Not found',
        forbidden: 'Access denied',
        serverError: 'Server error. Please try again later.',
        sessionExpired: 'Session expired. Please sign in again.',
        invalidEmail: 'Invalid email format',
        invalidPhone: 'Invalid phone format',
        requiredField: 'This field is required',
      },
      notifications: {
        newMessage: 'New Message',
        visitReminder: 'Visit Reminder',
        matchFound: 'Match Found',
        enrollmentApproved: 'Enrollment Approved',
        documentReady: 'Document Ready',
        consentRequired: 'Consent Required',
        profileIncomplete: 'Profile Incomplete',
        trialUpdate: 'Trial Update',
        medicationReminder: 'Medication Reminder',
        appointmentConfirmed: 'Appointment Confirmed',
        appointmentCancelled: 'Appointment Cancelled',
        markAsRead: 'Mark as Read',
      },
      epidemic: {
        title: 'AI Epidemic Modelling',
        description: 'Forecasts, early warnings, and what-if simulations from your surveillance data.',
        disclaimer: 'Model estimate only. Not a diagnosis or clinical decision tool. Uses anonymous, aggregated counts.',
        offline: 'Offline cache',
        tabs: {
          forecast: 'Forecast',
          anomaly: 'Early Warning',
          hotspot: 'Hotspots',
          simulator: 'What-if',
          signals: 'Signals',
          bias: 'Fairness',
        },
        signals: {
          title: 'External signals',
          description: 'WHO AFRO bulletins, climate, and mobility cached for this country.',
          empty: 'No external signals cached yet. Run the fetcher functions to populate.',
        },
        bias: {
          title: 'Fairness check',
          description: 'Are alerts spread fairly across districts? Big gaps may mean missed outbreaks.',
          runs: 'Runs reviewed',
          events: 'Events',
          subgroups: 'Subgroups',
          burdenRatio: 'Alert burden ratio',
          alerts: 'alerts',
          empty: 'Not enough runs yet.',
          warning: 'Large alert imbalance detected. Some districts may be under-monitored.',
        },
      },
      medical: {
        diagnosis: 'Diagnosis',
        treatment: 'Treatment',
        medication: 'Medication',
        dosage: 'Dosage',
        frequency: 'Frequency',
        sideEffects: 'Side Effects',
        allergies: 'Allergies',
        medicalHistory: 'Medical History',
        currentConditions: 'Current Conditions',
        familyHistory: 'Family History',
      },
      admin: {
        userManagement: 'User Management',
        siteManagement: 'Site Management',
        trialManagement: 'Trial Management',
        reports: 'Reports',
        analytics: 'Analytics',
        systemHealth: 'System Health',
        auditLog: 'Audit Log',
        permissions: 'Permissions',
        configuration: 'Configuration',
        maintenance: 'Maintenance',
      },
      regulatoryStatus: {
        ethicsCommittee: 'Ethics Committee',
        regulatorySubmission: 'Regulatory Submission',
        complianceStatus: 'Compliance Status',
        documentSubmission: 'Document Submission',
        approvalPending: 'Approval Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        underReview: 'Under Review',
        expirationDate: 'Expiration Date',
        renewalRequired: 'Renewal Required',
      },
      phc: {
        nav: {
          home: 'Home',
          register: 'Register',
          sync: 'Sync',
          help: 'Help',
          referrals: 'Referrals',
          aefi: 'Report AEFI',
          stats: 'Stats',
          supervisor: 'Supervisor',
          surveillance: 'Surveillance',
          dhis2: 'DHIS2',
          settings: 'Settings',
          logout: 'Logout',
          helpTooltip: 'Find help articles, training guides, and step-by-step instructions. Works offline too!',
          surveillanceTooltip: 'Open the government surveillance dashboard for real-time disease monitoring and outbreak alerts',
          dhis2Tooltip: 'View DHIS2 health intelligence dashboard',
        },
        toasts: {
          notAuthenticated: 'Not authenticated',
          syncFailed: 'Sync failed',
          syncComplete: 'Sync complete',
          syncCompleteDesc: 'All data is up to date.',
          syncError: 'Sync error',
          unknownError: 'Unknown error',
          signedOut: 'Signed out successfully',
          signOutError: 'Error signing out',
          surveillanceUnavailable: 'Surveillance portal not available',
          surveillanceUnavailableDesc: 'No government surveillance portal is linked to your facility.',
        },
        knowledgeBase: {
          title: 'Help & Knowledge Base - PHC',
          description: 'Find help articles, training guides, and step-by-step instructions for community health workers.',
          headerTitle: 'Help & Training',
        },
      },
      patientSelfTriage: {
        seo: {
          title: 'Check my symptoms | Patient Self-Triage',
          description: 'Free, private symptom check. Works offline. Connects you to your nearest clinic.',
          resultTitle: 'Your check-up result | Health Passport',
        },
        back: 'Back',
        disclaimer: 'This is a self-check, <strong>not a diagnosis</strong>. If you feel very sick, go to the clinic now or call your community health worker.',
        voice: {
          cantRead: 'Cannot read? Speak instead.',
          guide: 'Talk in English or Swahili. The helper will guide you.',
          start: 'Start',
        },
        header: {
          title: 'Check my symptoms',
          subtitle: 'Tick what feels wrong. We will tell you what to do. This is not a doctor.',
        },
        field: {
          age: 'Your age (years)',
          sex: 'Sex at birth',
          sexUnknown: 'Prefer not to say',
          sexFemale: 'Female',
          sexMale: 'Male',
          pregnant: 'I am pregnant',
        },
        gps: {
          label: 'Find my clinic',
          use: 'Use my location',
          update: 'Update my location',
          findingClinic: 'Finding nearest clinic…',
          notSupported: 'Location not supported on this device.',
          failed: 'Could not get location. You can still continue.',
          listOffline: 'Clinic list unavailable offline. We will save your check.',
        },
        callback: {
          label: 'Phone for call-back (optional)',
          placeholder: '+254 7...',
          help: 'A health worker may call or send WhatsApp/SMS.',
        },
        consent: {
          prompt: 'Tell me about studies?',
          promptBody: 'If a research study at my clinic can help me, I want to know. I can say no later. (General consent only.)',
          signedBy: 'Signed by',
          change: 'change',
          open: 'Open consent (listen + sign)',
        },
        submit: {
          button: 'See my result',
          signInTip: 'Tip: sign in to save your checks to your Health Passport.',
          signInToSend: 'Your check is ready. Sign in to send it to your clinic.',
          pickClinic: 'Please pick your clinic so we can send your check.',
          savedOffline: 'Saved offline. We will send when online.',
          sent: 'Sent to your nearest clinic.',
          savedRetry: 'Saved on this phone. We will resend later.',
        },
        result: {
          notDiagnosis: "This is a check, not a doctor's diagnosis.",
          dangerTitle: 'Danger signs found',
          dangerBody: 'Please go to the nearest clinic right now. If you have a phone, call your community health worker.',
          callbackNote: 'A health worker will call <strong>{{phone}}</strong> if needed.',
          trialConsentNote: 'You said yes to learn about studies. The clinic will see if any study can help you.',
          checkAgain: 'Check again',
          downloadPdf: 'Download PDF',
          backToPassport: 'Back to my passport',
          pdfSuccess: 'PDF downloaded',
          pdfError: 'Could not generate PDF',
        },
      },
    }
  },
  id: {
    translation: {
      // Common UI elements - 20 keys
      common: {
        welcome: 'Selamat Datang',
        loading: 'Memuat...',
        save: 'Simpan',
        cancel: 'Batal',
        submit: 'Kirim',
        search: 'Cari',
        filter: 'Filter',
        success: 'Berhasil',
        error: 'Kesalahan',
        back: 'Kembali',
        next: 'Berikutnya',
        previous: 'Sebelumnya',
        close: 'Tutup',
        open: 'Buka',
        view: 'Lihat',
        edit: 'Ubah',
        delete: 'Hapus',
        confirm: 'Konfirmasi',
        yes: 'Ya',
        no: 'Tidak',
        actions: 'Tindakan',
        status: 'Status',
        date: 'Tanggal',
        time: 'Waktu',
        download: 'Unduh',
        upload: 'Unggah',
        refresh: 'Segarkan',
        retry: 'Coba Lagi',
        details: 'Detail',
        summary: 'Ringkasan',
      },
      // Navigation - 12 keys
      navigation: {
        home: 'Beranda',
        trials: 'Uji Klinis',
        about: 'Tentang Kami',
        regions: 'Wilayah',
        conditions: 'Kondisi',
        contact: 'Kontak',
        login: 'Masuk',
        register: 'Daftar',
        dashboard: 'Dasbor',
        profile: 'Profil',
        settings: 'Pengaturan',
        logout: 'Keluar',
      },
      // Clinical Trials - 15 keys
      trials: {
        clinicalTrial: 'Uji Klinis',
        enrollment: 'Pendaftaran',
        recruiting: 'Merekrut',
        phase1: 'Fase 1',
        phase2: 'Fase 2',
        phase3: 'Fase 3',
        phase4: 'Fase 4',
        eligibility: 'Kriteria Kelayakan',
        sponsor: 'Sponsor',
        location: 'Lokasi',
        studyType: 'Jenis Studi',
        status: 'Status',
        findTrials: 'Cari Uji Klinis',
        matchMe: 'Cocokkan Saya',
        learnMore: 'Pelajari Lebih Lanjut',
      },
      // Forms - 12 keys
      forms: {
        name: 'Nama',
        email: 'Email',
        phone: 'Telepon',
        dateOfBirth: 'Tanggal Lahir',
        gender: 'Jenis Kelamin',
        medicalCondition: 'Kondisi Medis',
        currentMedications: 'Obat Saat Ini',
        allergies: 'Alergi',
        required: 'Wajib',
        optional: 'Opsional',
        pleaseSelect: 'Silakan Pilih',
        enterValue: 'Masukkan Nilai',
      },
      // Onboarding - 10 keys
      onboarding: {
        welcomeTitle: 'Selamat Datang di Uji Klinis',
        getStarted: 'Mulai',
        step1Title: 'Informasi Pribadi',
        step2Title: 'Riwayat Medis',
        step3Title: 'Preferensi Anda',
        completeProfile: 'Lengkapi Profil',
        skipForNow: 'Lewati Sekarang',
        almostDone: 'Hampir Selesai',
        profileComplete: 'Profil Selesai',
        continueToTrials: 'Lanjut ke Uji Klinis',
      },
      // Matching - 10 keys
      matching: {
        foundTrials: 'Uji Klinis Ditemukan',
        matchScore: 'Skor Kecocokan',
        eligibilityMatch: 'Kecocokan Kelayakan',
        viewDetails: 'Lihat Detail',
        applyNow: 'Daftar Sekarang',
        noMatches: 'Tidak Ada Kecocokan Ditemukan',
        refineSearch: 'Perbaiki Pencarian',
        highMatch: 'Kecocokan Tinggi',
        mediumMatch: 'Kecocokan Sedang',
        lowMatch: 'Kecocokan Rendah',
      },
      // Site Staff - 12 keys
      siteStaff: {
        patients: 'Pasien',
        visits: 'Kunjungan',
        enrollment: 'Pendaftaran',
        activeTrials: 'Uji Klinis Aktif',
        scheduleVisit: 'Jadwalkan Kunjungan',
        viewPatient: 'Lihat Pasien',
        patientRecords: 'Rekam Medis Pasien',
        addPatient: 'Tambah Pasien',
        patientList: 'Daftar Pasien',
        upcomingVisits: 'Kunjungan Mendatang',
        completedVisits: 'Kunjungan Selesai',
        missedVisits: 'Kunjungan Terlewat',
      },
      // Consent - 10 keys
      consent: {
        title: 'Formulir Persetujuan',
        readCarefully: 'Baca dengan Teliti',
        signature: 'Tanda Tangan',
        agreeToTerms: 'Saya Setuju dengan Ketentuan',
        signHere: 'Tanda Tangan di Sini',
        dateOfConsent: 'Tanggal Persetujuan',
        witnessSignature: 'Tanda Tangan Saksi',
        consentGiven: 'Persetujuan Diberikan',
        consentPending: 'Persetujuan Tertunda',
        consentWithdrawn: 'Persetujuan Ditarik',
      },
      // Dashboard - 10 keys
      dashboard: {
        overview: 'Ikhtisar',
        myTrials: 'Uji Klinis Saya',
        upcomingVisits: 'Kunjungan Mendatang',
        messages: 'Pesan',
        documents: 'Dokumen',
        settings: 'Pengaturan',
        notifications: 'Notifikasi',
        quickActions: 'Tindakan Cepat',
        recentActivity: 'Aktivitas Terbaru',
        helpCenter: 'Pusat Bantuan',
      },
      // Errors - 15 keys
      errors: {
        genericError: 'Terjadi kesalahan',
        networkError: 'Kesalahan jaringan. Periksa koneksi internet Anda.',
        authError: 'Kesalahan autentikasi. Silakan masuk kembali.',
        validationError: 'Kesalahan validasi. Periksa input Anda.',
        notFound: 'Tidak ditemukan',
        forbidden: 'Akses ditolak',
        serverError: 'Kesalahan server. Coba lagi nanti.',
        sessionExpired: 'Sesi berakhir. Silakan masuk kembali.',
        invalidEmail: 'Format email tidak valid',
        invalidPhone: 'Format nomor telepon tidak valid',
        requiredField: 'Kolom ini wajib diisi',
        minLength: 'Minimum {min} karakter diperlukan',
        maxLength: 'Maksimum {max} karakter diizinkan',
        passwordMismatch: 'Kata sandi tidak cocok',
        invalidDate: 'Format tanggal tidak valid',
      },
      // Notifications - 12 keys
      notifications: {
        newMessage: 'Pesan Baru',
        visitReminder: 'Pengingat Kunjungan',
        matchFound: 'Kecocokan Ditemukan',
        enrollmentApproved: 'Pendaftaran Disetujui',
        documentReady: 'Dokumen Siap',
        consentRequired: 'Persetujuan Diperlukan',
        profileIncomplete: 'Profil Belum Lengkap',
        trialUpdate: 'Pembaruan Uji Klinis',
        medicationReminder: 'Pengingat Obat',
        appointmentConfirmed: 'Janji Temu Dikonfirmasi',
        appointmentCancelled: 'Janji Temu Dibatalkan',
        markAsRead: 'Tandai Dibaca',
      },
      // Medical - 10 keys
      medical: {
        diagnosis: 'Diagnosis',
        treatment: 'Perawatan',
        medication: 'Obat',
        dosage: 'Dosis',
        frequency: 'Frekuensi',
        sideEffects: 'Efek Samping',
        allergies: 'Alergi',
        medicalHistory: 'Riwayat Medis',
        currentConditions: 'Kondisi Saat Ini',
        familyHistory: 'Riwayat Keluarga',
      },
      // Admin - 10 keys
      admin: {
        userManagement: 'Manajemen Pengguna',
        siteManagement: 'Manajemen Situs',
        trialManagement: 'Manajemen Uji Klinis',
        reports: 'Laporan',
        analytics: 'Analitik',
        systemHealth: 'Kesehatan Sistem',
        auditLog: 'Log Audit',
        permissions: 'Izin',
        configuration: 'Konfigurasi',
        maintenance: 'Pemeliharaan',
      },
      // BPOM/Regulatory - 12 keys
      regulatory: {
        bpomApproval: 'Persetujuan BPOM',
        ethicsCommittee: 'Komite Etik',
        irbApproval: 'Persetujuan IRB',
        regulatorySubmission: 'Pengajuan Regulasi',
        complianceStatus: 'Status Kepatuhan',
        documentSubmission: 'Pengajuan Dokumen',
        approvalPending: 'Persetujuan Tertunda',
        approved: 'Disetujui',
        rejected: 'Ditolak',
        underReview: 'Dalam Peninjauan',
        expirationDate: 'Tanggal Kedaluwarsa',
        renewalRequired: 'Perbaruan Diperlukan',
      },
      // SATUSEHAT Integration - 8 keys
      satusehat: {
        connectionStatus: 'Status Koneksi',
        syncPatients: 'Sinkronkan Pasien',
        lastSync: 'Sinkronisasi Terakhir',
        ehrIntegration: 'Integrasi EHR',
        patientLinked: 'Pasien Terhubung',
        dataExchange: 'Pertukaran Data',
        fhirCompliant: 'Sesuai FHIR',
        healthRecords: 'Rekam Kesehatan',
      },
    }
  },
  th: {
    translation: {
      common: {
        welcome: 'ยินดีต้อนรับ',
        loading: 'กำลังโหลด...',
        save: 'บันทึก',
        cancel: 'ยกเลิก',
        submit: 'ส่ง',
        search: 'ค้นหา',
        filter: 'กรอง',
        success: 'สำเร็จ',
        error: 'ข้อผิดพลาด',
      },
      navigation: {
        home: 'หน้าแรก',
        trials: 'การทดลองทางคลินิก',
        about: 'เกี่ยวกับเรา',
        contact: 'ติดต่อ',
        login: 'เข้าสู่ระบบ',
        register: 'ลงทะเบียน',
      },
      trials: {
        clinicalTrial: 'การทดลองทางคลินิก',
        enrollment: 'การลงทะเบียน',
        recruiting: 'กำลังรับสมัคร',
        eligibility: 'เกณฑ์คุณสมบัติ',
        findTrials: 'ค้นหาการทดลอง',
      }
    }
  },
  vi: {
    translation: {
      common: {
        welcome: 'Chào mừng',
        loading: 'Đang tải...',
        save: 'Lưu',
        cancel: 'Hủy',
        submit: 'Gửi',
        search: 'Tìm kiếm',
        filter: 'Lọc',
        success: 'Thành công',
        error: 'Lỗi',
      },
      navigation: {
        home: 'Trang chủ',
        trials: 'Thử nghiệm lâm sàng',
        about: 'Về chúng tôi',
        contact: 'Liên hệ',
        login: 'Đăng nhập',
        register: 'Đăng ký',
      },
      trials: {
        clinicalTrial: 'Thử nghiệm lâm sàng',
        enrollment: 'Đăng ký tham gia',
        recruiting: 'Đang tuyển',
        eligibility: 'Tiêu chí đủ điều kiện',
        findTrials: 'Tìm thử nghiệm',
      }
    }
  },
  ko: {
    translation: {
      common: {
        welcome: '환영합니다',
        loading: '로딩 중...',
        save: '저장',
        cancel: '취소',
        submit: '제출',
        search: '검색',
        filter: '필터',
        success: '성공',
        error: '오류',
      },
      navigation: {
        home: '홈',
        trials: '임상시험',
        about: '회사 소개',
        contact: '연락처',
        login: '로그인',
        register: '등록',
      },
      trials: {
        clinicalTrial: '임상시험',
        enrollment: '등록',
        recruiting: '모집 중',
        eligibility: '자격 기준',
        findTrials: '시험 찾기',
      }
    }
  },
  ms: {
    translation: {
      common: {
        welcome: 'Selamat datang',
        loading: 'Memuatkan...',
        save: 'Simpan',
        cancel: 'Batal',
        submit: 'Hantar',
        search: 'Cari',
        filter: 'Tapis',
        success: 'Berjaya',
        error: 'Ralat',
      },
      navigation: {
        home: 'Laman Utama',
        trials: 'Percubaan Klinikal',
        about: 'Tentang Kami',
        contact: 'Hubungi',
        login: 'Log Masuk',
        register: 'Daftar',
      },
      trials: {
        clinicalTrial: 'Percubaan Klinikal',
        enrollment: 'Pendaftaran',
        recruiting: 'Merekrut',
        eligibility: 'Kriteria Kelayakan',
        findTrials: 'Cari Percubaan',
      }
    }
  },
  // ===== AFRICAN LANGUAGES =====
  // Chichewa/Nyanja (Malawi, Zambia, Mozambique) - ISO 639-1: ny
  ny: {
    translation: {
      common: {
        welcome: 'Takulandirani',
        loading: 'Ikukwera...',
        save: 'Sungani',
        cancel: 'Lekani',
        submit: 'Tumizani',
        search: 'Funani',
        filter: 'Sankhani',
        success: 'Zachita bwino',
        error: 'Cholakwika',
        back: 'Bwererani',
        next: 'Pitirizani',
        previous: 'Mbuyomu',
        close: 'Tsekani',
        open: 'Tsegulani',
        view: 'Onani',
        edit: 'Sinthani',
        delete: 'Chotsani',
        confirm: 'Tsimikizani',
        yes: 'Inde',
        no: 'Ayi',
        actions: 'Zochita',
        status: 'Momwe zilili',
        date: 'Tsiku',
        time: 'Nthawi',
        download: 'Tsitsani',
        upload: 'Kwezani',
        refresh: 'Konzaninso',
        retry: 'Yesaninso',
        details: 'Zambiri',
        summary: 'Mwachidule',
      },
      navigation: {
        home: 'Kunyumba',
        trials: 'Kuyesa Mankhwala',
        about: 'Za Ife',
        regions: 'Madera',
        conditions: 'Matenda',
        contact: 'Tilumikizeni',
        login: 'Lowani',
        register: 'Lembetsani',
        dashboard: 'Gawo Lalikulu',
        profile: 'Mbiri Yanu',
        settings: 'Zosintha',
        logout: 'Tulukani',
      },
      trials: {
        clinicalTrial: 'Kuyesa Mankhwala',
        enrollment: 'Kulembetsa',
        recruiting: 'Ikufuna Anthu',
        phase1: 'Gawo 1',
        phase2: 'Gawo 2',
        phase3: 'Gawo 3',
        phase4: 'Gawo 4',
        eligibility: 'Zofunikira Kulowa',
        sponsor: 'Wopereka Ndalama',
        location: 'Malo',
        studyType: 'Mtundu wa Kafukufuku',
        status: 'Momwe zilili',
        findTrials: 'Pezani Mayeso',
        matchMe: 'Ndifanitseni',
        learnMore: 'Phunzirani Zambiri',
      },
      forms: {
        name: 'Dzina',
        email: 'Imelo',
        phone: 'Foni',
        dateOfBirth: 'Tsiku Lobadwira',
        gender: 'Mwamuna kapena Mkazi',
        medicalCondition: 'Matenda',
        currentMedications: 'Mankhwala Amene Mukumwa',
        allergies: 'Zomwe Simumvera',
        required: 'Zofunikira',
        optional: 'Ngati Mufuna',
        pleaseSelect: 'Chonde Sankhani',
        enterValue: 'Lembani',
      },
      onboarding: {
        welcomeTitle: 'Takulandirani ku Kuyesa Mankhwala',
        getStarted: 'Yambani',
        step1Title: 'Mbiri Yanu',
        step2Title: 'Mbiri ya Matenda',
        step3Title: 'Zomwe Mukufuna',
        completeProfile: 'Tsatirani Zonse',
        skipForNow: 'Dumphani Pano',
        almostDone: 'Mwatsala Pang\'ono',
        profileComplete: 'Mbiri Yatheka',
        continueToTrials: 'Pitirizani ku Mayeso',
      },
      matching: {
        foundTrials: 'Mayeso Amene Apezeka',
        matchScore: 'Malonda Oyendera',
        eligibilityMatch: 'Kuyendera kwa Zofunikira',
        viewDetails: 'Onani Zambiri',
        applyNow: 'Pempherani Tsopano',
        noMatches: 'Palibe Zofanana',
        refineSearch: 'Konzaninso Kufuna',
        highMatch: 'Zofanana Kwambiri',
        mediumMatch: 'Zofanana Pang\'ono',
        lowMatch: 'Zofanana Pang\'onong\'ono',
      },
      siteStaff: {
        patients: 'Odwala',
        visits: 'Kudzaona',
        enrollment: 'Kulembetsa',
        activeTrials: 'Mayeso Amene Akuchitika',
        scheduleVisit: 'Konzekani Tsiku',
        viewPatient: 'Onani Wodwala',
        patientRecords: 'Zolemba za Odwala',
        addPatient: 'Onjezani Wodwala',
        patientList: 'Mndandanda wa Odwala',
        upcomingVisits: 'Kudzaona Kuli Patsogolo',
        completedVisits: 'Kudzaona Komwe Kwatheka',
        missedVisits: 'Kudzaona Komwe Kunalephera',
      },
      consent: {
        title: 'Fomu ya Chilolezo',
        readCarefully: 'Werengani Mosamala',
        signature: 'Kusayina',
        agreeToTerms: 'Ndikuvomera Malamulo',
        signHere: 'Sayinani Pano',
        dateOfConsent: 'Tsiku la Chilolezo',
        witnessSignature: 'Kusayina kwa Mboni',
        consentGiven: 'Chilolezo Chaperekedwa',
        consentPending: 'Chilolezo Chikuyembekezera',
        consentWithdrawn: 'Chilolezo Chachotsedwa',
      },
      dashboard: {
        overview: 'Kuyang\'ana Kwanu',
        myTrials: 'Mayeso Anga',
        upcomingVisits: 'Kudzaona Kuli Patsogolo',
        messages: 'Mauthenga',
        documents: 'Zolemba',
        settings: 'Zosintha',
        notifications: 'Zidziwitso',
        quickActions: 'Zochita Msanga',
        recentActivity: 'Zomwe Zachitika Posachedwa',
        helpCenter: 'Malo Othandizira',
      },
      errors: {
        genericError: 'Panachitika cholakwika',
        networkError: 'Cholakwika cha intaneti. Yang\'anani kugwirizana kwanu.',
        authError: 'Cholakwika cha kutsimikizira. Chonde lowani kachiwiri.',
        validationError: 'Cholakwika cha kutsimikizira. Yang\'anani zomwe mwalembetsa.',
        notFound: 'Sizinapezeke',
        forbidden: 'Kulowa kwakanyidwa',
        serverError: 'Cholakwika cha seva. Yesaninso pambuyo pake.',
        sessionExpired: 'Nthawi yathera. Chonde lowani kachiwiri.',
        invalidEmail: 'Imelo yolakwika',
        invalidPhone: 'Nambala ya foni yolakwika',
        requiredField: 'Malo amenewa afunika',
        minLength: 'Zikufunika zilembo {min} zochepa',
        maxLength: 'Zilembo {max} zokha zolola',
        passwordMismatch: 'Mawu achinsinsi sakugwirizana',
        invalidDate: 'Tsiku lolakwika',
      },
      notifications: {
        newMessage: 'Uthenga Watsopano',
        visitReminder: 'Kukumbutsa za Kudzaona',
        matchFound: 'Zofanana Zapezeka',
        enrollmentApproved: 'Kulembetsa Kwavomelezedwa',
        documentReady: 'Zolemba Zakonzeka',
        consentRequired: 'Chilolezo Chikufunika',
        profileIncomplete: 'Mbiri Sinatheke',
        trialUpdate: 'Uthenga wa Kuyeso',
        medicationReminder: 'Kukumbutsa za Mankhwala',
        appointmentConfirmed: 'Ndondomeko Yatsimikiziridwa',
        appointmentCancelled: 'Ndondomeko Yachotsedwa',
        markAsRead: 'Werenga',
      },
      medical: {
        diagnosis: 'Kuzindikira Matenda',
        treatment: 'Chisamaliro',
        medication: 'Mankhwala',
        dosage: 'Kuchuluka',
        frequency: 'Kangati',
        sideEffects: 'Zotsatira Zoyipa',
        allergies: 'Zomwe Simumvera',
        medicalHistory: 'Mbiri ya Matenda',
        currentConditions: 'Matenda a Panopa',
        familyHistory: 'Mbiri ya Banja',
      },
      admin: {
        userManagement: 'Kuyang\'anira Ogwiritsa Ntchito',
        siteManagement: 'Kuyang\'anira Malo',
        trialManagement: 'Kuyang\'anira Mayeso',
        reports: 'Malipoti',
        analytics: 'Kusanthula',
        systemHealth: 'Momwe Makina Alili',
        auditLog: 'Zolemba za Kuyang\'ana',
        permissions: 'Zilolezo',
        configuration: 'Kukonzekera',
        maintenance: 'Kusamalira',
      },
      regulatory: {
        pmraApproval: 'Chilolezo cha PMRA',
        ethicsCommittee: 'Komiti ya Zachikhalidwe',
        nhsrcApproval: 'Chilolezo cha NHSRC',
        comrecApproval: 'Chilolezo cha COMREC',
        ncstApproval: 'Chilolezo cha NCST',
        irbApproval: 'Chilolezo cha IRB',
        regulatorySubmission: 'Kutumiza kwa Boma',
        complianceStatus: 'Momwe Mukutsatira Malamulo',
        documentSubmission: 'Kutumiza Zolemba',
        approvalPending: 'Chilolezo Chikuyembekezera',
        approved: 'Chavomelezedwa',
        rejected: 'Chakanidwa',
        underReview: 'Chikuwunikiridwa',
        expirationDate: 'Tsiku Lomaliza',
        renewalRequired: 'Kuyambiranso Kofunikira',
      },
    }
  },
  // Hausa (Nigeria, Niger, Ghana) - ISO 639-1: ha - AERO-VASC Study
  ha: {
    translation: {
      common: {
        welcome: 'Barka da zuwa',
        loading: 'Ana lodawa...',
        save: 'Ajiye',
        cancel: 'Soke',
        submit: 'Aika',
        search: 'Nema',
        filter: 'Tace',
        success: 'An yi nasara',
        error: 'Kuskure',
        back: 'Komawa',
        next: 'Gaba',
        previous: 'Baya',
        close: 'Rufe',
        open: 'Buɗe',
        view: 'Duba',
        edit: 'Gyara',
        delete: 'Goge',
        confirm: 'Tabbatar',
        yes: 'Eh',
        no: "A'a",
        actions: 'Ayyuka',
        status: 'Matsayi',
        date: 'Kwanan wata',
        time: 'Lokaci',
        download: 'Sauke',
        upload: 'Ɗora',
        refresh: 'Sabunta',
        retry: 'Sake gwadawa',
        details: 'Cikakkun bayanai',
        summary: 'Taƙaitawa',
      },
      navigation: {
        home: 'Gida',
        trials: 'Gwajin Magani',
        about: 'Game da Mu',
        regions: 'Yankuna',
        conditions: 'Cututtuka',
        contact: 'Tuntuɓe mu',
        login: 'Shiga',
        register: 'Yi rajista',
        dashboard: 'Babban shafi',
        profile: 'Bayanan ku',
        settings: 'Saituna',
        logout: 'Fita',
      },
      trials: {
        clinicalTrial: 'Gwajin Magani',
        enrollment: 'Shigarwa',
        recruiting: 'Ana neman mutane',
        phase1: 'Mataki na 1',
        phase2: 'Mataki na 2',
        phase3: 'Mataki na 3',
        phase4: 'Mataki na 4',
        eligibility: 'Sharuɗɗan shiga',
        sponsor: 'Mai tallafawa',
        location: 'Wuri',
        studyType: 'Nau\'in bincike',
        status: 'Matsayi',
        findTrials: 'Nemo gwaje-gwaje',
        matchMe: 'Daidaita ni',
        learnMore: 'Ƙara koyo',
      },
      forms: {
        name: 'Suna',
        email: 'Imel',
        phone: 'Waya',
        dateOfBirth: 'Ranar haihuwa',
        gender: 'Jinsi',
        medicalCondition: 'Yanayin lafiya',
        currentMedications: 'Magungunan da ake amfani da su',
        allergies: 'Rashin jituwa da magani',
        required: 'Dole ne',
        optional: 'Na zaɓi',
        pleaseSelect: 'Da fatan za a zaɓa',
        enterValue: 'Shigar da darajar',
      },
      consent: {
        title: 'Takardar Izini',
        readCarefully: 'Karanta da kyau',
        signature: 'Sa hannu',
        agreeToTerms: 'Na yarda da sharuɗɗa',
        signHere: 'Sa hannu a nan',
        dateOfConsent: 'Ranar izini',
        witnessSignature: 'Sa hannun shaida',
        consentGiven: 'An ba da izini',
        consentPending: 'Ana jiran izini',
        consentWithdrawn: 'An janye izini',
      },
      dashboard: {
        overview: 'Duba gabaɗaya',
        myTrials: 'Gwaje-gwajena',
        upcomingVisits: 'Ziyarce-ziyarcen da ke zuwa',
        messages: 'Saƙonni',
        documents: 'Takardu',
        settings: 'Saituna',
        notifications: 'Sanarwa',
        quickActions: 'Ayyuka na sauri',
        recentActivity: 'Ayyukan baya-bayan nan',
        helpCenter: 'Cibiyar taimako',
      },
      medical: {
        diagnosis: 'Gano cuta',
        treatment: 'Magani',
        medication: 'Magani',
        dosage: 'Adadin magani',
        frequency: 'Lokutan shan magani',
        sideEffects: 'Illa',
        allergies: 'Rashin jituwa',
        medicalHistory: 'Tarihin lafiya',
        currentConditions: 'Yanayin lafiya na yanzu',
        familyHistory: 'Tarihin iyali',
      },
      errors: {
        genericError: 'An sami kuskure',
        networkError: 'Kuskuren hanyar sadarwa. Duba haɗin ku.',
        authError: 'Kuskuren tabbatarwa. Da fatan za a sake shiga.',
        notFound: 'Ba a samu ba',
        sessionExpired: 'Lokaci ya ƙare. Da fatan za a sake shiga.',
      },
      notifications: {
        newMessage: 'Sabon saƙo',
        visitReminder: 'Tunatarwar ziyara',
        matchFound: 'An sami daidaituwa',
        consentRequired: 'Ana buƙatar izini',
      },
    }
  },
  // Igbo (Nigeria) - ISO 639-1: ig - AERO-VASC Study
  ig: {
    translation: {
      common: {
        welcome: 'Nnọọ',
        loading: 'Na-ebugo...',
        save: 'Chekwaa',
        cancel: 'Kagbuo',
        submit: 'Nyefee',
        search: 'Chọọ',
        filter: 'Họrọ',
        success: 'Ọ gaara nke ọma',
        error: 'Njehie',
        back: 'Laghachi',
        next: 'Gaa n\'ihu',
        previous: 'Azụ',
        close: 'Mechie',
        open: 'Mepee',
        view: 'Lee',
        edit: 'Dezie',
        delete: 'Hichapụ',
        confirm: 'Kwado',
        yes: 'Ee',
        no: 'Mba',
        actions: 'Omume',
        status: 'Ọnọdụ',
        date: 'Ụbọchị',
        time: 'Oge',
        download: 'Budata',
        upload: 'Bulite',
        refresh: 'Mee ọhụrụ',
        retry: 'Nwaa ọzọ',
        details: 'Nkọwa',
        summary: 'Nchịkọta',
      },
      navigation: {
        home: 'Ụlọ',
        trials: 'Nnwale Ọgwụ',
        about: 'Maka Anyị',
        regions: 'Mpaghara',
        conditions: 'Ọrịa',
        contact: 'Kpọtụrụ anyị',
        login: 'Banye',
        register: 'Debanye aha',
        dashboard: 'Isi ibe',
        profile: 'Profaịlụ gị',
        settings: 'Ntọala',
        logout: 'Pụọ',
      },
      trials: {
        clinicalTrial: 'Nnwale Ọgwụ',
        enrollment: 'Ndebanye aha',
        recruiting: 'Na-achọ ndị mmadụ',
        phase1: 'Nzọụkwụ 1',
        phase2: 'Nzọụkwụ 2',
        phase3: 'Nzọụkwụ 3',
        phase4: 'Nzọụkwụ 4',
        eligibility: 'Ihe achọrọ',
        sponsor: 'Onye na-akwado',
        location: 'Ebe',
        studyType: 'Ụdị nnyocha',
        status: 'Ọnọdụ',
        findTrials: 'Chọta nnwale',
        matchMe: 'Dakọtara m',
        learnMore: 'Mụtakwuo',
      },
      forms: {
        name: 'Aha',
        email: 'Email',
        phone: 'Ekwentị',
        dateOfBirth: 'Ụbọchị ọmụmụ',
        gender: 'Okike',
        medicalCondition: 'Ọnọdụ ahụike',
        currentMedications: 'Ọgwụ a na-ewe ugbu a',
        allergies: 'Ihe na-emetụta gị',
        required: 'Achọrọ',
        optional: 'Nhọrọ',
        pleaseSelect: 'Biko họrọ',
        enterValue: 'Tinye uru',
      },
      consent: {
        title: 'Akwụkwọ Nkwenye',
        readCarefully: 'Gụọ nke ọma',
        signature: 'Mbinye aka',
        agreeToTerms: 'Ana m ekwenye na usoro',
        signHere: 'Binye aka ebe a',
        dateOfConsent: 'Ụbọchị nkwenye',
        witnessSignature: 'Mbinye aka onye akaebe',
        consentGiven: 'Enyere nkwenye',
        consentPending: 'Na-eche nkwenye',
        consentWithdrawn: 'Ewepụrụ nkwenye',
      },
      dashboard: {
        overview: 'Ntụle',
        myTrials: 'Nnwale m',
        upcomingVisits: 'Nleta na-abịa',
        messages: 'Ozi',
        documents: 'Akwụkwọ',
        settings: 'Ntọala',
        notifications: 'Ọkwa',
        quickActions: 'Omume ngwa ngwa',
        recentActivity: 'Omume nso nso a',
        helpCenter: 'Ebe enyemaka',
      },
      medical: {
        diagnosis: 'Nchọpụta ọrịa',
        treatment: 'Ọgwụgwọ',
        medication: 'Ọgwụ',
        dosage: 'Oke ọgwụ',
        frequency: 'Ugboro ole',
        sideEffects: 'Mmetụta ọjọọ',
        allergies: 'Ihe na-emetụta gị',
        medicalHistory: 'Akụkọ ahụike',
        currentConditions: 'Ọnọdụ ahụike ugbu a',
        familyHistory: 'Akụkọ ezinụlọ',
      },
      errors: {
        genericError: 'Njehie mere',
        networkError: 'Njehie netwọk. Lelee njikọ gị.',
        authError: 'Njehie nkwenye. Biko banye ọzọ.',
        notFound: 'Ahụghị ya',
        sessionExpired: 'Oge gị agwụla. Biko banye ọzọ.',
      },
      notifications: {
        newMessage: 'Ozi ọhụrụ',
        visitReminder: 'Ncheta nleta',
        matchFound: 'Achọpụtara ndakọrịta',
        consentRequired: 'Achọrọ nkwenye',
      },
    }
  },
  // Somali (Somalia, Djibouti, Ethiopia, Kenya) - ISO 639-1: so - AERO-VASC Study
  so: {
    translation: {
      common: {
        welcome: 'Soo dhawoow',
        loading: 'Waa la soo rarayaa...',
        save: 'Kaydi',
        cancel: 'Jooji',
        submit: 'Dir',
        search: 'Raadi',
        filter: 'Shaandhee',
        success: 'Guul',
        error: 'Khalad',
        back: 'Dib u noqo',
        next: 'Xiga',
        previous: 'Kan hore',
        close: 'Xir',
        open: 'Fur',
        view: 'Arag',
        edit: 'Wax ka beddel',
        delete: 'Tir',
        confirm: 'Xaqiiji',
        yes: 'Haa',
        no: 'Maya',
        actions: 'Ficillo',
        status: 'Xaalad',
        date: 'Taariikh',
        time: 'Waqti',
        download: 'Soo deji',
        upload: 'Soo geli',
        refresh: 'Cusboonaysii',
        retry: 'Mar kale isku day',
        details: 'Faahfaahin',
        summary: 'Soo koobid',
      },
      navigation: {
        home: 'Bogga hore',
        trials: 'Tijaabada Daawada',
        about: 'Naga',
        regions: 'Gobolada',
        conditions: 'Cudurrada',
        contact: 'Nala soo xiriir',
        login: 'Soo gal',
        register: 'Is diiwaan geli',
        dashboard: 'Bogga guud',
        profile: 'Xogtaada',
        settings: 'Dejinta',
        logout: 'Ka bax',
      },
      trials: {
        clinicalTrial: 'Tijaabada Daawada',
        enrollment: 'Isdiiwaangelinta',
        recruiting: 'Waa la raadinayaa',
        phase1: 'Wajiga 1aad',
        phase2: 'Wajiga 2aad',
        phase3: 'Wajiga 3aad',
        phase4: 'Wajiga 4aad',
        eligibility: 'Shuruudaha ku haboon',
        sponsor: 'Maalgeliye',
        location: 'Goob',
        studyType: 'Nooca daraasadda',
        status: 'Xaalad',
        findTrials: 'Raadi tijaabooyin',
        matchMe: 'I waafaji',
        learnMore: 'Wax badan baro',
      },
      forms: {
        name: 'Magaca',
        email: 'Iimeel',
        phone: 'Telefoon',
        dateOfBirth: 'Taariikhda dhalashada',
        gender: 'Jinsiga',
        medicalCondition: 'Xaaladda caafimaadka',
        currentMedications: 'Daawada hadda la qaadanayo',
        allergies: 'Xasaasiyadda',
        required: 'Waa lagama maarmaan',
        optional: 'Ikhtiyaari',
        pleaseSelect: 'Fadlan dooro',
        enterValue: 'Geli qiimaha',
      },
      consent: {
        title: 'Foomka Oggolaanshaha',
        readCarefully: 'Si taxadar leh u akhri',
        signature: 'Saxiixa',
        agreeToTerms: 'Waan aqbalay shuruudaha',
        signHere: 'Halkan ka saxiix',
        dateOfConsent: 'Taariikhda oggolaanshaha',
        witnessSignature: 'Saxiixa markhaatiga',
        consentGiven: 'Oggolaansho la siiyay',
        consentPending: 'Oggolaansho la sugayaa',
        consentWithdrawn: 'Oggolaansho la soo celiyay',
      },
      dashboard: {
        overview: 'Guud ahaan',
        myTrials: 'Tijaabadayda',
        upcomingVisits: 'Booqashooyinka soo socda',
        messages: 'Fariimaha',
        documents: 'Dukumiintiyada',
        settings: 'Dejinta',
        notifications: 'Ogeysiisyada',
        quickActions: 'Ficillo degdeg ah',
        recentActivity: 'Dhaqdhaqaaqii ugu dambeeyay',
        helpCenter: 'Xarunta caawinta',
      },
      medical: {
        diagnosis: 'Ogaanshaha cudurka',
        treatment: 'Daaweynta',
        medication: 'Daawada',
        dosage: 'Qiyaasta daawada',
        frequency: 'Inta jeer',
        sideEffects: 'Waxyeelada',
        allergies: 'Xasaasiyadda',
        medicalHistory: 'Taariikhda caafimaadka',
        currentConditions: 'Xaaladda caafimaadka hadda',
        familyHistory: 'Taariikhda qoyska',
      },
      errors: {
        genericError: 'Khalad ayaa dhacay',
        networkError: 'Khalad shabakad. Hubi isku xirka.',
        authError: 'Khalad xaqiijin. Fadlan mar kale soo gal.',
        notFound: 'Lama helin',
        sessionExpired: 'Waqtigaagu wuu dhammaaday. Fadlan mar kale soo gal.',
      },
      notifications: {
        newMessage: 'Fariin cusub',
        visitReminder: 'Xusuusin booqasho',
        matchFound: 'Waafajin la helay',
        consentRequired: 'Oggolaansho loo baahan yahay',
      },
      trialSite: {
        tabs: {
          dashboard: 'Guddiga',
          ecrf: 'eCRF',
          ecoa: 'eCOA',
          analytics: 'Falanqeyn',
          staff: 'Shaqaalaha',
          patients: 'Bukaan-socoodka',
          recruitment: 'CRM',
          econsent: 'Ogolaansho',
          safety: 'Badbaado/PV',
          ip: 'IRT / Maamulka IP',
          queries: 'Su\'aalaha',
          regulatory: 'Sharci',
          rfis: 'RFIs',
          inspections: 'Baaritaano',
          settings: 'Qaabeyn',
        },
        header: {
          backToSiteDashboard: 'Ku noqo Guddiga Goobta',
          siteManagement: 'Maamulka Goobta Tijaabada',
          activeContext: 'Macnaha Firfircoon',
          organization: 'Hay\'adda',
        },
        tooltips: {
          dashboard: 'Bogga aasaasiga. Arag tirada bukaanka, booqashada, iyo digniinooyinka.',
          ecrf: 'Foomamka elektaroonigga ah ee aad ku qortid xogta bukaanka booqasho kasta.',
          ecoa: 'Su\'aalaha caafimaadka ee bukaanku ay iyagu buuxiyaan.',
          analytics: 'Jaantuska iyo tirooyin muujinaya sida goobtu u shaqaynayso.',
          staff: 'Ku dar xubnaha kooxda, deji eraygooda sirta ah, hubi tababarkooda.',
          patients: 'Dhammaan bukaankaaga. Ku dar kuwo cusub, hubi xaaladooda.',
          recruitment: 'Hel oo diiwaan geli bukaanka tijaabada. Raac halka uu bukaan kasta joogto.',
          econsent: 'Hel ogolaanshaha bukaanka si elektaroonig ah. Waxay ku saxiixaan shaashadda.',
          safety: 'Warbixin dhibaatooyinka caafimaadka ee dhacda intii tijaabada socotay. Deg deg u samee.',
          ip: 'Maamul daawada cilmi baarista. Raac waxa la helay, la bixiyay, iyo la soo celiyay.',
          queries: 'Su\'aalaha maalgeliyaha ku saabsan xogtaada. Fur mid kasta oo hagaaji.',
          regulatory: 'Soo geli oo maamul dukumiintiyada loo baahan yahay.',
          rfis: 'Codsiyada macluumaadka ee maalgeliyaha. Halkan ugu jawaab.',
          inspections: 'Diyaari baaritaanno iyo kormeer. Hubi in dukumiintiyada dhan diyaar yihiin.',
          settings: 'Bedel magaca goobta, calaamadda, iyo goobaha kale.',
        },
        dashboard: {
          quickActions: 'Hawlaha Degdegga',
          quickActionsDescription: 'Hawlaha caadiga ah iyo gaababka maamulka goobta',
          triggerMatching: 'Bilow Waafajinta Goobta',
          triggering: 'Bilaabayaa...',
          viewPatientRoster: 'Arag Liiska Bukaanka',
          visitSchedule: 'Jadwalka Booqashada',
          myAssessments: 'Qiimaynteyda',
          trainingVideos: 'Fiidiyowyada Tababarka',
          viewTrainingManual: 'Arag Buugga Tababarka',
          dctInfrastructure: 'Kaabayaasha DCT',
          activeTrials: 'Tijaabada Firfircoon',
          paidAddons: 'Adeegyada Dheeraadka ah',
          paidAddonsDescription: 'Sifooyinka premium ee kor u qaadaya howlgallada caafimaadka goobta',
          enrollmentForecast: 'Saadaalinta Isdiiwaangelinta',
          enrollmentForecastDescription: 'Saadaal AI-ku wado ee dhamaystirka isdiiwaangelinta',
          patientManagementPro: 'Maamulka Bukaanka Pro',
          activeTrialsBilling: 'Tijaabada Firfircoon & Biilasha',
          dctModule: 'Qaybta DCT',
          inspectionReadiness: 'Diyaarinta Baaritaanka',
        },
        common: {
          pending: 'Sugitaan',
          inProgress: 'Socda',
          submitted: 'La gudbiyay',
          accepted: 'La aqbalay',
          overdue: 'Ka dib waqtiga',
          noSiteSelected: 'Goob lama dooran',
          questionnaires: 'Su\'aalaha',
          completed: 'Dhammaystiran',
          totalAssigned: 'Wadarta la xilsaaray',
          assignments: 'Xilsaarista',
          instrumentLibrary: 'Maktabadda Qalabka',
          templates: 'Qaababka',
          research: 'Cilmi-baadhis',
          questionnaireBuilder: 'Dhisaha Su\'aalaha',
          userManual: 'Buugga Isticmaalaha',
          ecoaManagement: 'Maamulka eCOA',
          ecoaDescription: 'Qiimaynta Natiijada Caafimaadka ee Elektaroonigga ah',
          inbox: 'Sanduuqa Warqadaha',
          requestsForInformation: 'Codsiyada Macluumaad',
          rfisDescription: 'Maamul oo uga jawaab codsiyada maalgeliyaha',
          logExternalRfi: 'Diiwaan Geli RFI dibedda',
          siteSettings: 'Qaabeynta Goobta',
          siteSettingsDescription: 'Cusbooneysii faahfaahin goobta oo habee moduleyaasha DCT',
          siteDetails: 'Faahfaahin Goobta',
          editSiteDetails: 'Wax ka beddel Faahfaahinta Goobta',
          configureDctModules: 'Habee Moduleyaasha DCT',
          saveChanges: 'Keydi Isbeddelada',
          saving: 'Keydiyaa...',
          siteName: 'Magaca Goobta',
          city: 'Magaalo',
          country: 'Waddan',
          notSet: 'Lama dejin',
        },
        investigator: {
          totalPatients: 'Wadarta Bukaanka',
          visitCompletion: 'Dhamaystirka Booqashada',
          protocolDeviations: 'Ka-leexashada Hab-maamuuska',
          adverseEvents: 'Dhacdooyinka Caadifadka',
          requiresReview: 'U baahan dib u eegis',
          enrollmentStatus: 'Xaaladda Isdiiwaangelinta',
          screening: 'Baaritaanka',
          active: 'Firfircoon',
          withdrawn: 'Laga baxay',
          aeSeverity: 'Qaybinta Darnaanta AE',
          mild: 'Fudud',
          moderate: 'Dhexdhexaad',
          severe: 'Daran',
          lifeThreatening: 'Halis Noolol',
          recentActivity: 'Dhaqdhaqaaqa Dhawaan',
          noRecentActivity: 'Dhaqdhaqaaq dhawaan ma jiro',
          patientEnrolled: 'Bukaan La Diiwaangeliyay',
          patientAssigned: 'Bukaan La Xilsaaray',
        },
        analytics: {
          siteAnalyticsDashboard: 'Guddiga Falanqeynta Goobta',
          performanceMetrics: 'Cabbirrada waxqabadka dhamaystiran ee dhammaan tijaabada',
          performanceMetricsFor: 'Cabbirrada waxqabadka ee',
          refresh: 'Cusbooneysii',
          exportReport: 'Soo Saar Warbixin',
          screeningConversion: 'Beddelka Baaritaanka',
          visitCompletion: 'Dhamaystirka Booqashada',
          protocolQuality: 'Tayada Hab-maamuuska',
          documentCompliance: 'Waafaqida Dukumiintiga',
          excellent: 'Aad u wanaagsan',
          needsImprovement: 'U baahan horumar',
          onTime: 'waqtiga ku habboon',
          openQueries: 'su\'aalaha furan',
          good: 'Wanaagsan',
          reviewRequired: 'Dib u Eegis Loo Baahan Yahay',
          monthlyRecruitment: 'Qorashada Bishii',
          patientsEnrolledLast30: 'Bukaan la diiwaangeliyay 30 maalmood ee la soo dhaafay',
          totalEnrolled: 'Wadarta La Diiwaangeliyay',
          activeAndCompleted: 'Bukaanka firfircoon iyo kuwa dhammaystiran',
          dropoutRate: 'Heerka Ka Bixitaanka',
          withinAcceptableRange: 'Xadka la aqbali karo',
          aboveIndustryAverage: 'Ka sareeya celceliska warshadaha',
          enrollmentTrends: 'Isbeddelada Isdiiwaangelinta',
          enrollmentTrendsDesc: 'Isdiiwaangelinta bukaanka 6-dii bilood ee la soo dhaafay',
          noEnrollmentData: 'Xog isdiiwaangelin ma jirto',
          visitStatusDistribution: 'Qaybinta Xaaladda Booqashada',
          visitStatusDesc: 'Xaaladda hadda ee dhammaan booqashooyinka bukaanka',
          noVisitData: 'Xog booqasho ma jirto',
          protocolDeviationsChart: 'Ka-leexashada Hab-maamuuska',
          deviationsBySeverity: 'Qaybinta heerka darnaanta',
          noProtocolDeviations: 'Ka-leexasho Hab-maamuus ma jirto',
          excellentCompliance: 'Waafaqid hab-maamuus aad u wanaagsan',
          siteQualityScore: 'Dhibcaha Tayada Goobta',
          overallAssessment: 'Qiimaynta guud ee waxqabadka',
          visitCompliance: 'Waafaqida Booqashada',
          queryResolution: 'Xallinta Su\'aalaha',
          documentCompletion: 'Dhamaystirka Dukumiintiga',
          onTimeVisits: 'Booqashada Waqtiga ah',
          overallPerformance: 'Waxqabadka Guud ee Goobta',
          exceptional: 'Gaara',
          fair: 'Dhexdhexaad',
          failedToLoad: 'Falanqeyntu ku guul darreysatay soo dejinta',
          retry: 'Isku day mar kale',
          analyticsRefreshed: 'Falanqeynta La Cusbooneysiiyay',
          dataRefreshed: 'Xogta si guul leh ayaa loo cusbooneysiiyay.',
          allTrialsRefreshed: 'Xogta dhammaan tijaabada ayaa la cusbooneysiiyay.',
          enrolled: 'La diiwaangeliyay',
          scheduled: 'La qorsheeyay',
          missed: 'La waayay',
          major: 'Weyn',
          minor: 'Yar',
          queryResolutionLabel: 'Xallinta su\'aalaha',
          screened: 'la baaray',
          visits: 'booqashooyin',
        },
        roster: {
          visits: 'Booqashooyin',
          addPatient: 'Ku dar Bukaan',
          bulkUpload: 'Soo Dejin Badan',
          sendInvite: 'Dir Casuumad',
          matchAllPatients: 'Waafaji Dhammaan Bukaanka',
          thisMonth: 'Bisha Tan',
          verified: 'La Xaqiijiyay',
          pendingLogin: 'Sugaya Gelitaanka',
          matched: 'La Waafajiyay',
          profileComplete: 'Xogta Shaqsiga Dhammaystiran',
          total: 'Wadarta',
          searchPlaceholder: 'Ku raadi magac ama iimeyl...',
          loadingMore: 'Wax badan soo dejinayaa...',
          loadingPatients: 'Bukaanka soo dejinayaa...',
          errorLoadingPatients: 'Qalad baa ku dhacay soo dejinta bukaanka',
          showing: 'Muujinaya',
          of: 'ka mid ah',
          patients: 'bukaan',
          patient: 'Bukaan',
          type: 'Nooc',
          contact: 'Xiriir',
          loginStatus: 'Xaaladda Gelitaanka',
          healthProfile: 'Xogta Caafimaadka',
          consentStatus: 'Xaaladda Ogolaanshaha',
          trialMatching: 'Waafajinta Tijaabada',
          workflow: 'Socodka Shaqada',
          actions: 'Tallaabooyin',
          notSent: 'Lama dirin',
          pending: 'Sugitaan',
          complete: 'Dhammaystiran',
          incomplete: 'Aan dhammaystirneyn',
          notMatched: 'Aan la waafajin',
          runMatch: 'Bilow Waafajinta',
          match: 'Waafaji',
          open: 'Fur',
          noContactInfo: 'Macluumaad xiriir ma jirto',
          noPatients: 'Bukaan weli ma jiro',
          noMatchingPatients: 'Bukaan la heli karo la aqoon waayay',
          tryAgain: 'Isku day mar kale',
          reloadPage: 'Dib u soo fur bogga',
        },
        staff: {
          siteStaff: 'Shaqaalaha Goobta',
          manageTeam: 'Maamul xubnaha kooxda ee loo xilsaaray goobta tijaabada',
          addStaffMember: 'Ku dar Xubin Shaqaale',
          staffEmail: 'Iimeylka Shaqaalaha',
          emailPlaceholder: 'shaqaale@tusaale.com',
          emailHint: 'Haddii isticmaaluhu uusan lahayn akoon, waxay helayaan iimeyl casuumad',
          firstName: 'Magaca Hore (Ikhtiyaari)',
          lastName: 'Magaca Dambe (Ikhtiyaari)',
          role: 'Door',
          selectRole: 'Dooro door',
          adding: 'Ku daraya...',
          addStaff: 'Ku dar Shaqaale',
          cancel: 'Kansal',
          loadingStaff: 'Shaqaalaha soo dejinayaa...',
          errorLoadingStaff: 'Qalad baa ku dhacay soo dejinta shaqaalaha',
          name: 'Magac',
          email: 'Iimeyl',
          compliance: 'Waafaqid',
          status: 'Xaalad',
          active: 'Firfircoon',
          inactive: 'Aan firfircooneyn',
          noStaff: 'Shaqaale weli lama xilsaarin',
          invitationSent: 'Casuumad la diray',
          staffAdded: 'Xubin shaqaale ah la kordhiyay',
          failedToAdd: 'Ku guul darreystay ku darista shaqaalaha',
          staffRemoved: 'Xubin shaqaale ah la saaray',
          staffRemovedDesc: 'Xubinta shaqaalaha waxaa loo calaamadeeyay mid aan firfircooneyn.',
          failedToRemove: 'Ku guul darreystay saarista shaqaalaha',
          missingInfo: 'Macluumaad maqan',
          provideEmailAndRole: 'Fadlan bixii iimeylka iyo doorka labadaba',
          attestationRequired: 'Caddayn loo baahan yahay',
          attestationHint: 'Fadlan xaqiiji in shaqaalaha lagu wargeliyay dhaqamada habka xogta',
        },
        addons: {
          clinical: 'Caafimaad',
          revenue: 'Dakhli',
          decentralized: 'Aan xarunta ahayn',
          complianceLabel: 'Waafaqid',
          patientManagementDesc: 'Khariidaynta OMOP dhamaystiran, kala duwanaanshaha, xaaladaha caafimaadka faahfaahsan',
          omopSdtm: 'Heerka xogta OMOP/SDTM',
          advancedDemographics: 'Raadraaca dadka sare',
          medicalHistory: 'Maamulka taariikhda caafimaadka',
          billingDesc: 'Maamul tijaabada firfircoon, jadwalka kharashka, dhacdooyinka la bixiyo, qaansheynta',
          trialActivation: 'Firfircoonida & maamulka tijaabada',
          feeSchedule: 'Dejinta jadwalka kharashka hab-maamuuska',
          automatedInvoicing: 'Qaansheynta tooska ah',
          dctDesc: 'Booqashada virtual-ka, kormeerka fog, ogolaanshaha digital, iyo isku xirka telehealth',
          telehealth: 'La-tashiga telehealth fiidiyow',
          remoteMonitoring: 'Kormeerka bukaanka ee fog',
          digitalConsent: 'Madadaalka ogolaanshaha digital-ka',
          inspectionDesc: 'Liisaska baaritaanka, falanqeynta farqiga dukumiintiga, iyo tababarka baaritaanka',
          ichGcpChecklist: '20+ shay oo liiska ICH-GCP ah',
          etmfGapAnalysis: 'Falanqeynta farqiga eTMF ee tooska ah',
          mockInspection: 'Tababarka su\'aal iyo jawaab baaritaanka',
          offlineTooltip: 'Soo deji xogta bukaanka, foomamka, iyo hab-maamuusyada hawlgalka goobta aan khadka ku jirin',
        },
        tabContent: {
          patients: {
            description: 'Watu wote waliosajiliwa kwenye kituo chako. Bonyeza jina ili kuona maelezo, ziara na mechi za majaribio.',
          },
          staff: {
            description: 'Watu wanaofanya kazi kwenye kituo chako. Ongeza wafanyakazi wapya, badilisha majukumu na simamia vyeti vya mafunzo.',
          },
          regulatory: {
            description: 'Hati na vibali vinavyohitajika kwa serikali. Weka usanidi wa utafiti, hati za TMF na usimamizi wa data ukiwa wa kisasa.',
            studySetup: 'Usanidi wa Utafiti',
            tmfDocuments: 'TMF na Hati',
            dataManagement: 'Usimamizi wa Data',
            oversightStaff: 'Usimamizi na Wafanyakazi',
          },
          safety: {
            description: 'Ripoti na fuatilia ikiwa mgonjwa anajisikia mgonjwa kutokana na dawa ya utafiti. Rekodi madhara, kagua kesi na wasilisha ripoti za usalama.',
            adverseEvents: 'Matukio Mabaya',
            adverseEventsShort: 'AE/SAE',
            reviewReporting: 'Ukaguzi na Ripoti',
            reviewShort: 'Ukaguzi',
            medications: 'Dawa',
            compliance: 'Utiifu',
          },
          ip: {
            title: 'Usimamizi wa IP na Ugawaji Bahati',
            help: 'Simamia hesabu ya Bidhaa ya Utafiti (IP), Teknolojia ya Majibu Mwingiliano (IRT/RTSM) kwa ugawaji bahati na usimamizi wa ugavi, na ufuatiliaji wa vifaa vya maabara kuu.',
            learnMore: 'Moduli hii inashughulikia maeneo makuu matatu: IRT/RTSM kwa ugawaji bahati wa vifurushi vya dawa, ugawaji na kufichua dharura; IP Inventory kwa uwajibikaji wa dawa na utiifu wa uhifadhi; na Maabara Kuu kwa ufuatiliaji wa vifurushi vya sampuli na usimamizi wa matokeo ya alama za kibaolojia. Shughuli zote zinarekodiwa kwa utiifu wa ICH-GCP §5.14.',
            irtRtsm: 'IRT / RTSM',
            ipInventory: 'Hesabu ya IP',
            centralLab: 'Maabara Kuu',
          },
          ecoa: {
            tipQuestionnaires: 'Jumla ya dodoso amilifu zinazopatikana kwa mgawo',
            tipPending: 'Mgawo unaosubiri kukamilishwa na wagonjwa au waangalizi',
            tipCompleted: 'Dodoso zilizowasilishwa kwa mafanikio na zenye matokeo',
            tipTotal: 'Jumla ya migawo ya dodoso kwa wagonjwa wote',
            builderHelp: 'Tengeneza dodoso za eCOA maalum kuanzia mwanzo au rekebisha vyombo vilivyoletwa kwa mahitaji mahususi ya jaribio lako.',
            builderDesc: 'Tengeneza dodoso za eCOA maalum au leta kutoka kwenye maktaba',
          },
          etmf: {
            siteEtmfTitle: 'Hati za eTMF za Kituo',
            headerNote: 'Tazama na fuatilia hati muhimu za kituo chako zilizowasilishwa kulingana na Mfano wa Marejeo wa DIA TMF (v3.3). Hati zilizopakiwa hapa au kupitia paneli ya AI Filing ya CRO huonekana zikiwa zimepangwa kwa eneo. Hati mahususi za kituo ni pamoja na vibali vya IRB, CV za wafanyakazi, kumbukumbu za uwakilishi na rekodi za mafunzo.',
            overallCompleteness: 'Ukamilifu wa Jumla',
            totalDocuments: 'Jumla ya Hati',
            zonesWithGaps: 'Maeneo Yenye Mapengo',
            acrossZones: 'Katika maeneo {{n}} kati ya 13',
            zonesBelowExpected: 'Maeneo chini ya idadi ya hati inayotarajiwa',
            zoneCompletenessTitle: 'Ukamilifu wa Eneo — DIA TMF v3.3',
            zoneLabel: 'Eneo',
            docsCount: 'hati {{actual}}/{{expected}}',
            docsFiled: '{{actual}} kati ya {{expected}} hati zilizotarajiwa zimewasilishwa',
            clickToFilter: 'Bonyeza ili kuchuja hati kwa eneo hili',
            siteDocuments: 'Hati za Kituo',
            searchPlaceholder: 'Tafuta hati...',
            loading: 'Inapakia hati...',
            noDocsFound: 'Hakuna hati zilizopatikana',
            emptyHintNone: 'Hati zilizowasilishwa kupitia paneli ya AI Filing ya CRO au kupakiwa moja kwa moja zitaonekana hapa.',
            emptyHintFiltered: 'Jaribu kurekebisha utafutaji wako au kichungi cha eneo.',
            colDocument: 'Hati',
            colType: 'Aina',
            colZone: 'Eneo',
            colStatus: 'Hali',
            colDate: 'Tarehe',
            unfiled: 'Haijawasilishwa',
          },
          dashboard: {
            fieldWorker: 'Mfanyakazi wa Uwanjani',
            fieldWorkerTooltip: 'Kiolesura cha simu kilichorahisishwa kwa ukusanyaji wa data uwanjani — vitals, eCOA, hati, utambuzi wa AI',
          },
          settings: {
            title: 'Mipangilio ya Kituo',
            selectCountry: 'Chagua nchi',
            statusActive: 'Amilifu',
            reasonPlaceholder: 'Eleza sababu ya mabadiliko haya...',
            brandingUpdatedTitle: 'Chapa imesasishwa',
            brandingUpdatedDesc: 'Chapa ya kituo chako imesasishwa kwa mafanikio',
            complianceInfra: 'Utiifu na Miundombinu',
            assessmentsLifecycle: 'Tathmini na Mzunguko wa Maisha',
            totRequiresOrg: 'Uhamishaji wa Wajibu unahitaji kiungo cha shirika.',
          },
        },
      },
    }
  },
  // Swahili (Kenya, Tanzania, Uganda, DRC) - Multi-country East African language
  sw: {
    translation: {
      common: {
        welcome: 'Karibu',
        loading: 'Inapakia...',
        save: 'Hifadhi',
        cancel: 'Ghairi',
        submit: 'Wasilisha',
        search: 'Tafuta',
        filter: 'Chuja',
        success: 'Imefanikiwa',
        error: 'Kosa',
        back: 'Rudi',
        next: 'Endelea',
        previous: 'Iliyotangulia',
        close: 'Funga',
        open: 'Fungua',
        view: 'Tazama',
        edit: 'Hariri',
        delete: 'Futa',
        confirm: 'Thibitisha',
        yes: 'Ndiyo',
        no: 'Hapana',
        actions: 'Vitendo',
        status: 'Hali',
        date: 'Tarehe',
        time: 'Muda',
        download: 'Pakua',
        upload: 'Pakia',
        refresh: 'Sasisha',
        retry: 'Jaribu Tena',
        details: 'Maelezo',
        summary: 'Muhtasari',
      },
      navigation: {
        home: 'Nyumbani',
        trials: 'Majaribio ya Kimatibabu',
        about: 'Kuhusu Sisi',
        regions: 'Mikoa',
        conditions: 'Hali za Kiafya',
        contact: 'Wasiliana',
        login: 'Ingia',
        register: 'Jisajili',
        dashboard: 'Dashibodi',
        profile: 'Wasifu',
        settings: 'Mipangilio',
        logout: 'Ondoka',
      },
      trials: {
        clinicalTrial: 'Jaribio la Kimatibabu',
        enrollment: 'Usajili',
        recruiting: 'Inaajiri',
        phase1: 'Awamu ya 1',
        phase2: 'Awamu ya 2',
        phase3: 'Awamu ya 3',
        phase4: 'Awamu ya 4',
        eligibility: 'Vigezo vya Kustahiki',
        sponsor: 'Mdhamini',
        location: 'Mahali',
        studyType: 'Aina ya Utafiti',
        status: 'Hali',
        findTrials: 'Tafuta Majaribio',
        matchMe: 'Nioanishe',
        learnMore: 'Jifunze Zaidi',
      },
      forms: {
        name: 'Jina',
        email: 'Barua pepe',
        phone: 'Simu',
        dateOfBirth: 'Tarehe ya Kuzaliwa',
        gender: 'Jinsia',
        medicalCondition: 'Hali ya Kiafya',
        currentMedications: 'Dawa za Sasa',
        allergies: 'Mzio',
        required: 'Inahitajika',
        optional: 'Si Lazima',
        pleaseSelect: 'Tafadhali Chagua',
        enterValue: 'Ingiza Thamani',
      },
      onboarding: {
        welcomeTitle: 'Karibu kwenye Majaribio ya Kimatibabu',
        getStarted: 'Anza',
        step1Title: 'Taarifa Binafsi',
        step2Title: 'Historia ya Kimatibabu',
        step3Title: 'Mapendeleo Yako',
        completeProfile: 'Kamilisha Wasifu',
        skipForNow: 'Ruka kwa Sasa',
        almostDone: 'Karibu Kumaliza',
        profileComplete: 'Wasifu Umekamilika',
        continueToTrials: 'Endelea na Majaribio',
        welcomePatient: 'Karibu PTC BRIDGE™',
        welcomePatientDesc: 'Jukwaa lako la kutafuta na kushiriki katika majaribio ya kimatibabu',
        welcomePatientContent: 'Tutakuongoza kupitia vipengele muhimu ili uanze safari yako ya majaribio ya kimatibabu.',
        completeProfileDesc: 'Jenga wasifu wako wa kiafya ili kupata mechi bora',
        findTrials: 'Tafuta Majaribio Yanayolingana',
        findTrialsDesc: 'Gundua majaribio ya kimatibabu kwa kutumia AI',
        applyToTrials: 'Omba Majaribio',
        applyToTrialsDesc: 'Eleza hamu na fuatilia maombi yako',
        stayConnected: 'Baki Umeunganishwa',
        stayConnectedDesc: 'Pokea masasisho na usimamie mawasiliano',
        welcomeSiteStaff: 'Karibu kwenye Usimamizi wa Tovuti ya Jaribio',
        welcomeSiteStaffDesc: 'Jukwaa lako la kusimamia shughuli za majaribio ya kimatibabu',
        skipTutorial: 'Ruka mafunzo',
        replayOnboarding: 'Rudia Mafunzo ya Kuanza',
        onboardingReset: 'Mafunzo yamewekwa upya',
        onboardingResetDesc: 'Sasisha ukurasa kuona mafunzo ya kuanza tena.',
      },
      matching: {
        foundTrials: 'Majaribio Yaliyopatikana',
        matchScore: 'Alama ya Ulinganifu',
        eligibilityMatch: 'Ulinganifu wa Ustahiki',
        viewDetails: 'Tazama Maelezo',
        applyNow: 'Omba Sasa',
        noMatches: 'Hakuna Ulinganifu Uliopatikana',
        refineSearch: 'Boresha Utafutaji',
        highMatch: 'Ulinganifu Mkubwa',
        mediumMatch: 'Ulinganifu wa Wastani',
        lowMatch: 'Ulinganifu Mdogo',
      },
      siteStaff: {
        patients: 'Wagonjwa',
        visits: 'Ziara',
        enrollment: 'Usajili',
        activeTrials: 'Majaribio Yanayoendelea',
        scheduleVisit: 'Panga Ziara',
        viewPatient: 'Tazama Mgonjwa',
        patientRecords: 'Rekodi za Mgonjwa',
        addPatient: 'Ongeza Mgonjwa',
        patientList: 'Orodha ya Wagonjwa',
        upcomingVisits: 'Ziara Zijazo',
        completedVisits: 'Ziara Zilizokamilika',
        missedVisits: 'Ziara Zilizokosekana',
      },
      consent: {
        title: 'Fomu ya Idhini',
        readCarefully: 'Soma kwa Makini',
        signature: 'Sahihi',
        agreeToTerms: 'Nakubaliana na Masharti',
        signHere: 'Saini Hapa',
        dateOfConsent: 'Tarehe ya Idhini',
        witnessSignature: 'Sahihi ya Shahidi',
        consentGiven: 'Idhini Imetolewa',
        consentPending: 'Idhini Inasubiri',
        consentWithdrawn: 'Idhini Imeondolewa',
      },
      dashboard: {
        overview: 'Muhtasari',
        myTrials: 'Majaribio Yangu',
        upcomingVisits: 'Ziara Zijazo',
        messages: 'Ujumbe',
        documents: 'Nyaraka',
        settings: 'Mipangilio',
        notifications: 'Arifa',
        quickActions: 'Vitendo vya Haraka',
        recentActivity: 'Shughuli za Hivi Karibuni',
        helpCenter: 'Kituo cha Msaada',
      },
      errors: {
        genericError: 'Kosa limetokea',
        networkError: 'Kosa la mtandao. Angalia muunganisho wako.',
        authError: 'Kosa la uthibitisho. Tafadhali ingia tena.',
        validationError: 'Kosa la uthibitisho. Angalia pembejeo zako.',
        notFound: 'Haijapatikana',
        forbidden: 'Ufikiaji umezuiwa',
        serverError: 'Kosa la seva. Jaribu tena baadaye.',
        sessionExpired: 'Kipindi kimeisha. Tafadhali ingia tena.',
        invalidEmail: 'Muundo wa barua pepe si sahihi',
        invalidPhone: 'Muundo wa nambari ya simu si sahihi',
        requiredField: 'Sehemu hii inahitajika',
        minLength: 'Herufi {min} ndogo zinahitajika',
        maxLength: 'Herufi {max} kubwa zinaruhusiwa',
        passwordMismatch: 'Nywila hazilingani',
        invalidDate: 'Muundo wa tarehe si sahihi',
      },
      notifications: {
        newMessage: 'Ujumbe Mpya',
        visitReminder: 'Ukumbusho wa Ziara',
        matchFound: 'Ulinganifu Umepatikana',
        enrollmentApproved: 'Usajili Umeidhinishwa',
        documentReady: 'Hati Iko Tayari',
        consentRequired: 'Idhini Inahitajika',
        profileIncomplete: 'Wasifu Haujakamilika',
        trialUpdate: 'Sasisha la Jaribio',
        medicationReminder: 'Ukumbusho wa Dawa',
        appointmentConfirmed: 'Miadi Imethibitishwa',
        appointmentCancelled: 'Miadi Imeghairiwa',
        markAsRead: 'Weka kama Imesomwa',
      },
      medical: {
        diagnosis: 'Uchunguzi',
        treatment: 'Matibabu',
        medication: 'Dawa',
        dosage: 'Kipimo',
        frequency: 'Marudio',
        sideEffects: 'Madhara',
        allergies: 'Mzio',
        medicalHistory: 'Historia ya Kimatibabu',
        currentConditions: 'Hali za Sasa',
        familyHistory: 'Historia ya Familia',
      },
      admin: {
        userManagement: 'Usimamizi wa Watumiaji',
        siteManagement: 'Usimamizi wa Tovuti',
        trialManagement: 'Usimamizi wa Majaribio',
        reports: 'Ripoti',
        analytics: 'Uchambuzi',
        systemHealth: 'Afya ya Mfumo',
        auditLog: 'Kumbukumbu ya Ukaguzi',
        permissions: 'Ruhusa',
        configuration: 'Usanidi',
        maintenance: 'Matengenezo',
      },
      regulatory: {
        ppbApproval: 'Idhini ya PPB',
        ethicsCommittee: 'Kamati ya Maadili',
        irbApproval: 'Idhini ya IRB',
        regulatorySubmission: 'Uwasilishaji wa Udhibiti',
        complianceStatus: 'Hali ya Uzingatiaji',
        documentSubmission: 'Uwasilishaji wa Hati',
        approvalPending: 'Idhini Inasubiri',
        approved: 'Imeidhinishwa',
        rejected: 'Imekataliwa',
        underReview: 'Inakaguliwa',
        expirationDate: 'Tarehe ya Kuisha',
        renewalRequired: 'Upya Unahitajika',
      },
      cdst: {
        title: 'Msaada wa Maamuzi ya Kimatibabu',
        patientIntake: 'Taarifa za Mgonjwa',
        chiefComplaint: 'Tatizo Kuu',
        dangerSigns: 'Dalili za Hatari',
        triageResult: 'Matokeo ya Uchunguzi',
        emergency: 'Dharura',
        urgent: 'Haraka',
        standard: 'Kawaida',
        preventive: 'Kinga',
        referTo: 'Peleka kwa',
        trialEligible: 'Anastahiki Jaribio la Kimatibabu',
        suspectedCondition: 'Hali Inayoshukiwa',
        recommendedAction: 'Hatua Inayopendekezwa',
        korogwe: 'Wilaya ya Korogwe',
        handeni: 'Wilaya ya Handeni',
        offlineAssessment: 'Tathmini ya Nje ya Mtandao',
        fullAiAssessment: 'Tathmini Kamili ya AI',
        connectivityRequired: 'Muunganisho wa mtandao unahitajika kwa uchunguzi kamili',
        fever: 'Homa',
        cough: 'Kikohozi',
        diarrhea: 'Kuhara',
        headache: 'Maumivu ya Kichwa',
        bodyPain: 'Maumivu ya Mwili',
        vomiting: 'Kutapika',
        breathingDifficulty: 'Ugumu wa Kupumua',
        rash: 'Upele',
        convulsions: 'Degedege',
        lethargy: 'Uchovu Mkubwa',
      },
      checklist: {
        gettingStarted: 'Kuanza',
        siteSetup: 'Kuanza — Usanidi wa Tovuti',
        phcFirstSteps: 'Kuanza — Hatua Zako za Kwanza',
        complete: 'kamili',
        allSet: 'Tayari! Uko tayari kuanza.',
        dismiss: 'Ondoa',
        addFirstPatient: 'Ongeza mgonjwa wako wa kwanza',
        addFirstPatientDesc: 'Sajili au mwalike mgonjwa kwenye orodha ya tovuti yako.',
        inviteStaff: 'Mwalike mfanyakazi',
        inviteStaffDesc: 'Ongeza waratibu, wachunguzi, au wauguzi kwenye timu yako.',
        scheduleVisit: 'Panga ziara ya mgonjwa',
        scheduleVisitDesc: 'Tengeneza ziara ya uchunguzi, ufuatiliaji, au kukamilisha utafiti.',
        setupAutoRules: 'Weka kanuni za ugawaji wa fomu kiotomatiki',
        setupAutoRulesDesc: 'Sanidi fomu zipi za eCRF/eCOA zinazopewa kiotomatiki wagonjwa wanapobadilisha hatua.',
        registerFirstPatient: 'Sajili mgonjwa wako wa kwanza',
        registerFirstPatientDesc: 'Ongeza mgonjwa mpya — chukua jina, tarehe ya kuzaliwa, na idhini.',
        completeConsultation: 'Kamilisha mashauriano',
        completeConsultationDesc: 'Rekodi dalili, uchunguzi, utambuzi, na mpango wa matibabu wa mgonjwa.',
        syncData: 'Sasisha data yako',
        syncDataDesc: 'Pakia data uliyokusanya nje ya mtandao kwenye seva kuu.',
        completeTraining: 'Kamilisha moduli ya mafunzo ya BlueCloudX',
        completeTrainingDesc: 'Jifunze ujuzi wa majaribio ya kimatibabu na kozi za bure za mtandaoni.',
      },
      emptyStates: {
        noPatients: 'Hakuna wagonjwa waliosajiliwa bado',
        noPatientsDesc: 'Ongeza mgonjwa wako wa kwanza kuanza kusimamia ushiriki wao katika jaribio.',
        noVisits: 'Hakuna ziara zilizopangwa',
        noVisitsDesc: 'Panga ziara ya uchunguzi, ufuatiliaji, au kukamilisha utafiti kwa mgonjwa.',
        noForms: 'Hakuna fomu zilizotolewa',
        noFormsDesc: 'Gawa fomu za eCRF au eCOA kwa wagonjwa kwa mkono au weka kanuni za ugawaji kiotomatiki.',
        noData: 'Hakuna data bado',
        noDataDesc: 'Data itaonekana hapa unapotumia jukwaa.',
      },
      chw: {
        aiDiagnosis: 'Tumia hii kusaidia kutambua ugonjwa mgonjwa anaweza kuwa nao. Jibu maswali kuhusu dalili na AI itapendekeza uchunguzi unaowezekana kwa kutumia miongozo ya WHO IMCI.',
        medicalInfo: 'Rekodi historia ya afya ya mgonjwa hapa — hali zilizopo (kama kisukari au VVU), dawa za sasa, na mzio wowote.',
        recordVitals: 'Pima na rekodi shinikizo la damu, joto, uzito, urefu, na mapigo ya moyo ya mgonjwa. Fanya hivi kila ziara.',
        syncData: 'Pakia data uliyokusanya nje ya mtandao kwenye seva kuu. Sasisha unapokuwa na mtandao.',
      },
      phc: {
        nav: {
          home: 'Nyumbani',
          register: 'Sajili',
          sync: 'Sawazisha',
          help: 'Msaada',
          referrals: 'Rufaa',
          aefi: 'Ripoti AEFI',
          stats: 'Takwimu',
          supervisor: 'Msimamizi',
          surveillance: 'Ufuatiliaji',
          dhis2: 'DHIS2',
          settings: 'Mipangilio',
          logout: 'Toka',
          helpTooltip: 'Pata makala ya msaada, miongozo ya mafunzo, na maelekezo. Inafanya kazi nje ya mtandao pia!',
          surveillanceTooltip: 'Fungua dashibodi ya ufuatiliaji wa serikali kwa ufuatiliaji wa magonjwa wa wakati halisi',
          dhis2Tooltip: 'Tazama dashibodi ya akili ya afya ya DHIS2',
        },
        toasts: {
          notAuthenticated: 'Hujathibitishwa',
          syncFailed: 'Usawazishaji umeshindikana',
          syncComplete: 'Usawazishaji umekamilika',
          syncCompleteDesc: 'Data yote imesasishwa.',
          syncError: 'Hitilafu ya usawazishaji',
          unknownError: 'Hitilafu isiyojulikana',
          signedOut: 'Umetoka kwa mafanikio',
          signOutError: 'Hitilafu kutoka',
          surveillanceUnavailable: 'Lango la ufuatiliaji halipatikani',
          surveillanceUnavailableDesc: 'Hakuna lango la ufuatiliaji wa serikali lililounganishwa na kituo chako.',
        },
        knowledgeBase: {
          title: 'Msaada na Maktaba ya Maarifa - PHC',
          description: 'Pata makala ya msaada, miongozo ya mafunzo, na maelekezo kwa wahudumu wa afya wa jamii.',
          headerTitle: 'Msaada na Mafunzo',
        },
      },
      patientSelfTriage: {
        seo: {
          title: 'Angalia dalili zangu | Uchunguzi wa Mgonjwa',
          description: 'Uchunguzi wa dalili bure na wa siri. Hufanya kazi nje ya mtandao. Hukuunganisha na kliniki ya karibu.',
          resultTitle: 'Matokeo ya uchunguzi wako | Pasipoti ya Afya',
        },
        back: 'Rudi',
        disclaimer: 'Huu ni uchunguzi wa kibinafsi, <strong>si utambuzi wa daktari</strong>. Ukijisikia mgonjwa sana, nenda kliniki sasa au mpigie mhudumu wa afya wa jamii.',
        voice: {
          cantRead: 'Huwezi kusoma? Sema badala yake.',
          guide: 'Sema kwa Kiingereza au Kiswahili. Msaidizi atakuongoza.',
          start: 'Anza',
        },
        header: {
          title: 'Angalia dalili zangu',
          subtitle: 'Weka alama kwenye kinachokusumbua. Tutakuambia la kufanya. Hii si daktari.',
        },
        field: {
          age: 'Umri wako (miaka)',
          sex: 'Jinsia ya kuzaliwa',
          sexUnknown: 'Sipendi kusema',
          sexFemale: 'Mke',
          sexMale: 'Mume',
          pregnant: 'Nina mimba',
        },
        gps: {
          label: 'Tafuta kliniki yangu',
          use: 'Tumia eneo langu',
          update: 'Sasisha eneo langu',
          findingClinic: 'Inatafuta kliniki ya karibu…',
          notSupported: 'Eneo halitumiki kwenye kifaa hiki.',
          failed: 'Hatukuweza kupata eneo. Bado unaweza kuendelea.',
          listOffline: 'Orodha ya kliniki haipatikani nje ya mtandao. Tutahifadhi uchunguzi wako.',
        },
        callback: {
          label: 'Simu ya kupiga (si lazima)',
          placeholder: '+254 7...',
          help: 'Mhudumu wa afya anaweza kupiga simu au kutuma WhatsApp/SMS.',
        },
        consent: {
          prompt: 'Niambie kuhusu tafiti?',
          promptBody: 'Ikiwa utafiti wa kitiba kwenye kliniki yangu unaweza kunisaidia, nataka kujua. Naweza kukataa baadaye. (Idhini ya jumla tu.)',
          signedBy: 'Imesainiwa na',
          change: 'badilisha',
          open: 'Fungua idhini (sikiliza + saini)',
        },
        submit: {
          button: 'Ona matokeo yangu',
          signInTip: 'Kidokezo: ingia ili kuhifadhi uchunguzi wako kwenye Pasipoti yako ya Afya.',
          signInToSend: 'Uchunguzi wako uko tayari. Ingia ili kuutuma kwenye kliniki yako.',
          pickClinic: 'Tafadhali chagua kliniki yako ili tuweze kutuma uchunguzi wako.',
          savedOffline: 'Imehifadhiwa nje ya mtandao. Tutatuma ukirudi mtandaoni.',
          sent: 'Imetumwa kwenye kliniki ya karibu.',
          savedRetry: 'Imehifadhiwa kwenye simu hii. Tutatuma tena baadaye.',
        },
        result: {
          notDiagnosis: 'Huu ni uchunguzi, si utambuzi wa daktari.',
          dangerTitle: 'Dalili za hatari zimepatikana',
          dangerBody: 'Tafadhali nenda kwenye kliniki ya karibu sasa hivi. Ukiwa na simu, mpigie mhudumu wa afya wa jamii.',
          callbackNote: 'Mhudumu wa afya atapiga simu kwa <strong>{{phone}}</strong> ikibidi.',
          trialConsentNote: 'Umekubali kujifunza kuhusu tafiti. Kliniki itaangalia kama utafiti wowote unaweza kukusaidia.',
          checkAgain: 'Angalia tena',
          downloadPdf: 'Pakua PDF',
          backToPassport: 'Rudi kwenye pasipoti yangu',
          pdfSuccess: 'PDF imepakuliwa',
          pdfError: 'Hatukuweza kutengeneza PDF',
        },
      },
    }
  },
  af: {
    translation: {
      common: {
        welcome: 'Welkom',
        loading: 'Laai...',
        save: 'Stoor',
        cancel: 'Kanselleer',
        submit: 'Dien in',
        search: 'Soek',
        filter: 'Filter',
        success: 'Sukses',
        error: 'Fout',
        back: 'Terug',
        next: 'Volgende',
        previous: 'Vorige',
        close: 'Sluit',
        open: 'Open',
        view: 'Bekyk',
        edit: 'Wysig',
        delete: 'Verwyder',
        confirm: 'Bevestig',
        yes: 'Ja',
        no: 'Nee',
        actions: 'Aksies',
        status: 'Status',
        date: 'Datum',
        time: 'Tyd',
        download: 'Laai af',
        upload: 'Laai op',
        refresh: 'Herlaai',
        retry: 'Probeer weer',
        details: 'Besonderhede',
        summary: 'Opsomming',
      },
      navigation: {
        home: 'Tuis',
        trials: 'Kliniese Proewe',
        about: 'Oor Ons',
        regions: 'Streke',
        conditions: 'Toestande',
        contact: 'Kontak',
        login: 'Teken in',
        register: 'Registreer',
        dashboard: 'Kontroleskerm',
        profile: 'Profiel',
        settings: 'Instellings',
        logout: 'Teken uit',
      },
      trials: {
        clinicalTrial: 'Kliniese Proef',
        enrollment: 'Inskrywing',
        recruiting: 'Werf',
        phase1: 'Fase 1',
        phase2: 'Fase 2',
        phase3: 'Fase 3',
        phase4: 'Fase 4',
        eligibility: 'Geskiktheidskriteria',
        sponsor: 'Borg',
        location: 'Ligging',
        studyType: 'Studie Tipe',
        status: 'Status',
        findTrials: 'Vind Proewe',
        matchMe: 'Pas My',
        learnMore: 'Leer Meer',
      },
      forms: {
        name: 'Naam',
        email: 'E-pos',
        phone: 'Telefoon',
        dateOfBirth: 'Geboortedatum',
        gender: 'Geslag',
        medicalCondition: 'Mediese Toestand',
        currentMedications: 'Huidige Medikasie',
        allergies: 'Allergieë',
        required: 'Verpligtend',
        optional: 'Opsioneel',
        pleaseSelect: 'Kies asseblief',
        enterValue: 'Voer waarde in',
      },
      onboarding: {
        welcomeTitle: 'Welkom by Kliniese Proewe',
        getStarted: 'Begin',
        step1Title: 'Persoonlike Inligting',
        step2Title: 'Mediese Geskiedenis',
        step3Title: 'Jou Voorkeure',
        completeProfile: 'Voltooi Profiel',
        skipForNow: 'Slaan vir nou oor',
        almostDone: 'Amper klaar',
        profileComplete: 'Profiel Voltooi',
        continueToTrials: 'Gaan na Proewe',
      },
      matching: {
        foundTrials: 'Proewe Gevind',
        matchScore: 'Passing Telling',
        eligibilityMatch: 'Geskiktheid Passing',
        viewDetails: 'Bekyk Besonderhede',
        applyNow: 'Dien Nou Aan',
        noMatches: 'Geen Passings Gevind',
        refineSearch: 'Verfyn Soektog',
        highMatch: 'Hoë Passing',
        mediumMatch: 'Medium Passing',
        lowMatch: 'Lae Passing',
      },
      siteStaff: {
        patients: 'Pasiënte',
        visits: 'Besoeke',
        enrollment: 'Inskrywing',
        activeTrials: 'Aktiewe Proewe',
        scheduleVisit: 'Skeduleer Besoek',
        viewPatient: 'Bekyk Pasiënt',
        patientRecords: 'Pasiënt Rekords',
        addPatient: 'Voeg Pasiënt By',
        patientList: 'Pasiënt Lys',
        upcomingVisits: 'Komende Besoeke',
        completedVisits: 'Voltooide Besoeke',
        missedVisits: 'Gemiste Besoeke',
      },
      consent: {
        title: 'Toestemmingsvorm',
        readCarefully: 'Lees Versigtig',
        signature: 'Handtekening',
        agreeToTerms: 'Ek stem saam met die Voorwaardes',
        signHere: 'Teken Hier',
        dateOfConsent: 'Datum van Toestemming',
        witnessSignature: 'Getuie Handtekening',
        consentGiven: 'Toestemming Gegee',
        consentPending: 'Toestemming Hangend',
        consentWithdrawn: 'Toestemming Onttrek',
      },
      dashboard: {
        overview: 'Oorsig',
        myTrials: 'My Proewe',
        upcomingVisits: 'Komende Besoeke',
        messages: 'Boodskappe',
        documents: 'Dokumente',
        settings: 'Instellings',
        notifications: 'Kennisgewings',
        quickActions: 'Vinnige Aksies',
        recentActivity: 'Onlangse Aktiwiteit',
        helpCenter: 'Hulpsentrum',
      },
      errors: {
        genericError: '\'n Fout het voorgekom',
        networkError: 'Netwerk fout. Kontroleer jou verbinding.',
        authError: 'Verifikasie fout. Teken asseblief weer in.',
        validationError: 'Validasie fout. Kontroleer jou invoer.',
        notFound: 'Nie gevind nie',
        forbidden: 'Toegang geweier',
        serverError: 'Bediener fout. Probeer later weer.',
        sessionExpired: 'Sessie verval. Teken asseblief weer in.',
        invalidEmail: 'Ongeldige e-pos formaat',
        invalidPhone: 'Ongeldige telefoonnommer formaat',
        requiredField: 'Hierdie veld is verpligtend',
        minLength: 'Minimum {min} karakters benodig',
        maxLength: 'Maksimum {max} karakters toegelaat',
        passwordMismatch: 'Wagwoorde stem nie ooreen nie',
        invalidDate: 'Ongeldige datum formaat',
      },
      notifications: {
        newMessage: 'Nuwe Boodskap',
        visitReminder: 'Besoek Herinnering',
        matchFound: 'Passing Gevind',
        enrollmentApproved: 'Inskrywing Goedgekeur',
        documentReady: 'Dokument Gereed',
        consentRequired: 'Toestemming Benodig',
        profileIncomplete: 'Profiel Onvolledig',
        trialUpdate: 'Proef Opdatering',
        medicationReminder: 'Medikasie Herinnering',
        appointmentConfirmed: 'Afspraak Bevestig',
        appointmentCancelled: 'Afspraak Gekanselleer',
        markAsRead: 'Merk as Gelees',
      },
      medical: {
        diagnosis: 'Diagnose',
        treatment: 'Behandeling',
        medication: 'Medikasie',
        dosage: 'Dosis',
        frequency: 'Frekwensie',
        sideEffects: 'Newe-effekte',
        allergies: 'Allergieë',
        medicalHistory: 'Mediese Geskiedenis',
        currentConditions: 'Huidige Toestande',
        familyHistory: 'Familie Geskiedenis',
      },
      admin: {
        userManagement: 'Gebruikersbestuur',
        siteManagement: 'Webwerfbestuur',
        trialManagement: 'Proefbestuur',
        reports: 'Verslae',
        analytics: 'Analitiek',
        systemHealth: 'Stelsel Gesondheid',
        auditLog: 'Oudit Log',
        permissions: 'Toestemmings',
        configuration: 'Konfigurasie',
        maintenance: 'Onderhoud',
      },
      regulatory: {
        sahpraApproval: 'SAHPRA Goedkeuring',
        ethicsCommittee: 'Etiek Komitee',
        irbApproval: 'IRB Goedkeuring',
        regulatorySubmission: 'Regulatoriese Indiening',
        complianceStatus: 'Nakomingstatus',
        documentSubmission: 'Dokument Indiening',
        approvalPending: 'Goedkeuring Hangend',
        approved: 'Goedgekeur',
        rejected: 'Verwerp',
        underReview: 'Onder Hersiening',
        expirationDate: 'Vervaldatum',
        renewalRequired: 'Hernuwing Benodig',
      },
    }
  },
  // Zulu (South Africa)
  zu: {
    translation: {
      common: {
        welcome: 'Siyakwamukela',
        loading: 'Iyalayisha...',
        save: 'Gcina',
        cancel: 'Khansela',
        submit: 'Thumela',
        search: 'Sesha',
        filter: 'Hlunga',
        success: 'Impumelelo',
        error: 'Iphutha',
        back: 'Emuva',
        next: 'Okulandelayo',
        previous: 'Okwangaphambili',
        close: 'Vala',
        open: 'Vula',
        view: 'Buka',
        edit: 'Hlela',
        delete: 'Susa',
        confirm: 'Qinisekisa',
        yes: 'Yebo',
        no: 'Cha',
        actions: 'Izenzo',
        status: 'Isimo',
        date: 'Usuku',
        time: 'Isikhathi',
        download: 'Landa',
        upload: 'Layisha',
        refresh: 'Vuselela',
        retry: 'Zama Futhi',
        details: 'Imininingwane',
        summary: 'Isifinyezo',
      },
      navigation: {
        home: 'Ikhaya',
        trials: 'Ukuhlolwa Kwezokwelapha',
        about: 'Mayelana Nathi',
        regions: 'Izifunda',
        conditions: 'Izimo',
        contact: 'Xhumana Nathi',
        login: 'Ngena',
        register: 'Bhalisa',
        dashboard: 'Ideshibhodi',
        profile: 'Iphrofayela',
        settings: 'Izilungiselelo',
        logout: 'Phuma',
      },
      trials: {
        clinicalTrial: 'Ukuhlolwa Kwezokwelapha',
        enrollment: 'Ukubhalisa',
        recruiting: 'Iyaqasha',
        phase1: 'Isigaba 1',
        phase2: 'Isigaba 2',
        phase3: 'Isigaba 3',
        phase4: 'Isigaba 4',
        eligibility: 'Imibandela Yokufaneleka',
        sponsor: 'Umxhasi',
        location: 'Indawo',
        studyType: 'Uhlobo Lwesifundo',
        status: 'Isimo',
        findTrials: 'Thola Ukuhlolwa',
        matchMe: 'Ngifanisele',
        learnMore: 'Funda Kabanzi',
      },
      forms: {
        name: 'Igama',
        email: 'I-imeyili',
        phone: 'Ucingo',
        dateOfBirth: 'Usuku Lokuzalwa',
        gender: 'Ubulili',
        medicalCondition: 'Isimo Sezempilo',
        currentMedications: 'Imithi Yamanje',
        allergies: 'Ama-alaji',
        required: 'Kuyadingeka',
        optional: 'Ukukhetha',
        pleaseSelect: 'Sicela Ukhethe',
        enterValue: 'Faka Inani',
      },
      onboarding: {
        welcomeTitle: 'Siyakwamukela Ekuhlolweni Kwezokwelapha',
        getStarted: 'Qala',
        step1Title: 'Imininingwane Yakho',
        step2Title: 'Umlando Wezokwelapha',
        step3Title: 'Izinqumo Zakho',
        completeProfile: 'Qedela Iphrofayela',
        skipForNow: 'Yeqa Okwamanje',
        almostDone: 'Sekuseduze Ukuqeda',
        profileComplete: 'Iphrofayela Iphelele',
        continueToTrials: 'Qhubeka Nokuhlolwa',
      },
      matching: {
        foundTrials: 'Ukuhlolwa Okutholakele',
        matchScore: 'Amaphuzu Okufana',
        eligibilityMatch: 'Ukufana Kokufaneleka',
        viewDetails: 'Buka Imininingwane',
        applyNow: 'Faka Isicelo Manje',
        noMatches: 'Akukho Okufanayo Okutholakele',
        refineSearch: 'Lungisa Ukusesha',
        highMatch: 'Ukufana Okuphezulu',
        mediumMatch: 'Ukufana Okuphakathi',
        lowMatch: 'Ukufana Okuncane',
      },
      siteStaff: {
        patients: 'Iziguli',
        visits: 'Izivakasho',
        enrollment: 'Ukubhalisa',
        activeTrials: 'Ukuhlolwa Okusebenzayo',
        scheduleVisit: 'Hlela Isivakasho',
        viewPatient: 'Buka Isiguli',
        patientRecords: 'Amarekhodi Eziguli',
        addPatient: 'Engeza Isiguli',
        patientList: 'Uhlu Lweziguli',
        upcomingVisits: 'Izivakasho Ezizayo',
        completedVisits: 'Izivakasho Eziqediwe',
        missedVisits: 'Izivakasho Eziphuthiwe',
      },
      consent: {
        title: 'Ifomu Yemvume',
        readCarefully: 'Funda Ngokucophelela',
        signature: 'Isiginesha',
        agreeToTerms: 'Ngiyavumelana Nemibandela',
        signHere: 'Sayina Lapha',
        dateOfConsent: 'Usuku Lwemvume',
        witnessSignature: 'Isiginesha Kafakazi',
        consentGiven: 'Imvume Inikwe',
        consentPending: 'Imvume Iyalindwa',
        consentWithdrawn: 'Imvume Ihoximiswe',
      },
      dashboard: {
        overview: 'Umbono Wonke',
        myTrials: 'Ukuhlolwa Kwami',
        upcomingVisits: 'Izivakasho Ezizayo',
        messages: 'Imiyalezo',
        documents: 'Amadokhumenti',
        settings: 'Izilungiselelo',
        notifications: 'Izaziso',
        quickActions: 'Izenzo Ezisheshayo',
        recentActivity: 'Umsebenzi Wakamuva',
        helpCenter: 'Isikhungo Sosizo',
      },
      errors: {
        genericError: 'Kwenzeke iphutha',
        networkError: 'Iphutha lenethiwekhi. Hlola ukuxhumana kwakho.',
        authError: 'Iphutha lokuqinisekisa. Sicela ungene futhi.',
        validationError: 'Iphutha lokuqinisekisa. Hlola okufakile.',
        notFound: 'Akutholakali',
        forbidden: 'Ukufinyelela kunqatshiwe',
        serverError: 'Iphutha leseva. Zama futhi kamuva.',
        sessionExpired: 'Isikhathi siphelelwe. Sicela ungene futhi.',
        invalidEmail: 'Ifomethi ye-imeyili engekho emthethweni',
        invalidPhone: 'Ifomethi yenombolo yocingo engekho emthethweni',
        requiredField: 'Lesi sigaba siyadingeka',
        minLength: 'Kudingeka izinhlamvu ezingu-{min} okungenani',
        maxLength: 'Izinhlamvu ezingu-{max} zivumelekile',
        passwordMismatch: 'Amaphasiwedi awahambisani',
        invalidDate: 'Ifomethi yosuku engekho emthethweni',
      },
      notifications: {
        newMessage: 'Umlayezo Omusha',
        visitReminder: 'Isikhumbuzo Sesivakasho',
        matchFound: 'Ukufana Kutholakele',
        enrollmentApproved: 'Ukubhalisa Kwamukelwe',
        documentReady: 'Idokhumenti Ilungile',
        consentRequired: 'Imvume Iyadingeka',
        profileIncomplete: 'Iphrofayela Ayiphelele',
        trialUpdate: 'Isibuyekezo Sokuhlolwa',
        medicationReminder: 'Isikhumbuzo Semithi',
        appointmentConfirmed: 'Isithembiso Siqinisekisiwe',
        appointmentCancelled: 'Isithembiso Sikhanseliwe',
        markAsRead: 'Maka Njengokufundiwe',
      },
      medical: {
        diagnosis: 'Ukuhlonzwa',
        treatment: 'Ukwelashwa',
        medication: 'Umuthi',
        dosage: 'Isikali',
        frequency: 'Ukuvama',
        sideEffects: 'Imiphumela Emibi',
        allergies: 'Ama-alaji',
        medicalHistory: 'Umlando Wezokwelapha',
        currentConditions: 'Izimo Zamanje',
        familyHistory: 'Umlando Womndeni',
      },
      admin: {
        userManagement: 'Ukuphathwa Kwabasebenzisi',
        siteManagement: 'Ukuphathwa Kwesayithi',
        trialManagement: 'Ukuphathwa Kokuhlolwa',
        reports: 'Imibiko',
        analytics: 'Ukuhlaziywa',
        systemHealth: 'Impilo Yohlelo',
        auditLog: 'Ilog Yokuhlola',
        permissions: 'Izimvume',
        configuration: 'Ukumiswa',
        maintenance: 'Ukulondolozwa',
      },
      regulatory: {
        sahpraApproval: 'Imvume ye-SAHPRA',
        ethicsCommittee: 'Ikomiti Yokuziphatha',
        irbApproval: 'Imvume ye-IRB',
        regulatorySubmission: 'Ukuthunyelwa Komthetho',
        complianceStatus: 'Isimo Sokuthobela',
        documentSubmission: 'Ukuthunyelwa Kwedokhumenti',
        approvalPending: 'Imvume Iyalindwa',
        approved: 'Kwamukelwe',
        rejected: 'Kwenqatshelwe',
        underReview: 'Ngaphansi Kokubuyekezwa',
        expirationDate: 'Usuku Lokuphelelwa',
        renewalRequired: 'Ukuvuselelwa Kuyadingeka',
      },
    }
  },
  // Xhosa (South Africa)
  xh: {
    translation: {
      common: {
        welcome: 'Wamkelekile',
        loading: 'Iyalayisha...',
        save: 'Gcina',
        cancel: 'Rhoxisa',
        submit: 'Ngenisa',
        search: 'Khangela',
        filter: 'Hluza',
        success: 'Impumelelo',
        error: 'Impazamo',
        back: 'Emva',
        next: 'Okulandelayo',
        previous: 'Okwangaphambili',
        close: 'Vala',
        open: 'Vula',
        view: 'Jonga',
        edit: 'Hlela',
        delete: 'Cima',
        confirm: 'Qinisekisa',
        yes: 'Ewe',
        no: 'Hayi',
        actions: 'Izenzo',
        status: 'Isimo',
        date: 'Umhla',
        time: 'Ixesha',
        download: 'Khuphela',
        upload: 'Layisha',
        refresh: 'Hlaziya',
        retry: 'Zama Kwakhona',
        details: 'Iinkcukacha',
        summary: 'Isishwankathelo',
      },
      navigation: {
        home: 'Ekhaya',
        trials: 'Uvavanyo Lwezonyango',
        about: 'Malunga Nathi',
        regions: 'Imimandla',
        conditions: 'Iimeko',
        contact: 'Qhagamshelana Nathi',
        login: 'Ngena',
        register: 'Bhalisa',
        dashboard: 'Ideshibhodi',
        profile: 'Iprofayile',
        settings: 'Izicwangciso',
        logout: 'Phuma',
      },
      trials: {
        clinicalTrial: 'Uvavanyo Lwezonyango',
        enrollment: 'Ukubhalisa',
        recruiting: 'Iyaqesha',
        phase1: 'Isigaba 1',
        phase2: 'Isigaba 2',
        phase3: 'Isigaba 3',
        phase4: 'Isigaba 4',
        eligibility: 'Imiqathango Yokufaneleka',
        sponsor: 'Umxhasi',
        location: 'Indawo',
        studyType: 'Uhlobo Lwesifundo',
        status: 'Isimo',
        findTrials: 'Fumana Uvavanyo',
        matchMe: 'Ndifanise',
        learnMore: 'Funda Ngakumbi',
      },
      forms: {
        name: 'Igama',
        email: 'I-imeyile',
        phone: 'Umnxeba',
        dateOfBirth: 'Umhla Wokuzalwa',
        gender: 'Isini',
        medicalCondition: 'Imeko Yezempilo',
        currentMedications: 'Amayeza Angoku',
        allergies: 'I-alaji',
        required: 'Iyafuneka',
        optional: 'Ukukhetha',
        pleaseSelect: 'Nceda Ukhethe',
        enterValue: 'Faka Ixabiso',
      },
      onboarding: {
        welcomeTitle: 'Wamkelekile Kuvavanyo Lwezonyango',
        getStarted: 'Qala',
        step1Title: 'Iinkcukacha Zakho',
        step2Title: 'Imbali Yezempilo',
        step3Title: 'Izikhethwa Zakho',
        completeProfile: 'Gqiba Iprofayile',
        skipForNow: 'Tsiba Okwangoku',
        almostDone: 'Phantse Ugqibile',
        profileComplete: 'Iprofayile Igqityiwe',
        continueToTrials: 'Qhubeka Novavanyo',
      },
      matching: {
        foundTrials: 'Uvavanyo Olufumanekileyo',
        matchScore: 'Amanqaku Okufana',
        eligibilityMatch: 'Ukufana Kokufaneleka',
        viewDetails: 'Jonga Iinkcukacha',
        applyNow: 'Faka Isicelo Ngoku',
        noMatches: 'Akukho Kufanayo Okufumanekileyo',
        refineSearch: 'Lungisa Ukukhangela',
        highMatch: 'Ukufana Okuphezulu',
        mediumMatch: 'Ukufana Okuphakathi',
        lowMatch: 'Ukufana Okuncinci',
      },
      siteStaff: {
        patients: 'Izigulana',
        visits: 'Iintyelelo',
        enrollment: 'Ukubhalisa',
        activeTrials: 'Uvavanyo Olusebenzayo',
        scheduleVisit: 'Cwangcisa Intyelelo',
        viewPatient: 'Jonga Isigulana',
        patientRecords: 'Irekhodi Zezigulana',
        addPatient: 'Yongeza Isigulana',
        patientList: 'Uluhlu Lwezigulana',
        upcomingVisits: 'Iintyelelo Ezizayo',
        completedVisits: 'Iintyelelo Ezigqityiweyo',
        missedVisits: 'Iintyelelo Eziphosisiweyo',
      },
      consent: {
        title: 'Ifomu Yemvume',
        readCarefully: 'Funda Ngenyameko',
        signature: 'Utyikityo',
        agreeToTerms: 'Ndiyavumelana Nemiqathango',
        signHere: 'Tyikitya Apha',
        dateOfConsent: 'Umhla Wemvume',
        witnessSignature: 'Utyikityo Lwengqina',
        consentGiven: 'Imvume Inikwe',
        consentPending: 'Imvume Iyalindwa',
        consentWithdrawn: 'Imvume Irhoxisiwe',
      },
      dashboard: {
        overview: 'Isishwankathelo',
        myTrials: 'Uvavanyo Lwam',
        upcomingVisits: 'Iintyelelo Ezizayo',
        messages: 'Imiyalezo',
        documents: 'Amaxwebhu',
        settings: 'Izicwangciso',
        notifications: 'Izaziso',
        quickActions: 'Izenzo Ezikhawulezileyo',
        recentActivity: 'Umsebenzi Wakutshanje',
        helpCenter: 'Iziko Loncedo',
      },
      errors: {
        genericError: 'Kwenzeke impazamo',
        networkError: 'Impazamo yenethiwekhi. Jonga unxibelelwano lwakho.',
        authError: 'Impazamo yokuqinisekisa. Nceda ungene kwakhona.',
        validationError: 'Impazamo yokuqinisekisa. Jonga okufakileyo.',
        notFound: 'Ayifumaneki',
        forbidden: 'Ukufikelela kwalwayo',
        serverError: 'Impazamo yeseva. Zama kwakhona kamva.',
        sessionExpired: 'Iseshini iphelelwe. Nceda ungene kwakhona.',
        invalidEmail: 'Ifomathi ye-imeyile engekho semthethweni',
        invalidPhone: 'Ifomathi yenombolo yomnxeba engekho semthethweni',
        requiredField: 'Esi sihloko siyafuneka',
        minLength: 'Kufuneka ubuncinci oonobumba abangu-{min}',
        maxLength: 'Oonobumba abangu-{max} bavumelekile',
        passwordMismatch: 'Iipasiwedi azifani',
        invalidDate: 'Ifomathi yomhla engekho semthethweni',
      },
      notifications: {
        newMessage: 'Umyalezo Omtsha',
        visitReminder: 'Isikhumbuzo Sentyelelo',
        matchFound: 'Ukufana Kufunyenwe',
        enrollmentApproved: 'Ukubhalisa Kwamkelwe',
        documentReady: 'Ixwebhu Lilungile',
        consentRequired: 'Imvume Iyafuneka',
        profileIncomplete: 'Iprofayile Ayigqitywanga',
        trialUpdate: 'Uhlaziyo Lovavanyo',
        medicationReminder: 'Isikhumbuzo Samayeza',
        appointmentConfirmed: 'Isithembiso Siqinisekisiwe',
        appointmentCancelled: 'Isithembiso Sirhoxisiwe',
        markAsRead: 'Phawula Njengokufundiweyo',
      },
      medical: {
        diagnosis: 'Ukuhlaziywa',
        treatment: 'Unyango',
        medication: 'Amayeza',
        dosage: 'Umlinganiselo',
        frequency: 'Ukufuthelwa',
        sideEffects: 'Imiphumo Emibi',
        allergies: 'I-alaji',
        medicalHistory: 'Imbali Yezempilo',
        currentConditions: 'Iimeko Zangoku',
        familyHistory: 'Imbali Yosapho',
      },
      admin: {
        userManagement: 'Ukulawulwa Kwabasebenzisi',
        siteManagement: 'Ukulawulwa Kwesayithi',
        trialManagement: 'Ukulawulwa Kovavanyo',
        reports: 'Iingxelo',
        analytics: 'Uhlalutyo',
        systemHealth: 'Impilo Yenkqubo',
        auditLog: 'Ilog Yophicotho',
        permissions: 'Iimvume',
        configuration: 'Ukumiselwa',
        maintenance: 'Ulondolozo',
      },
      regulatory: {
        sahpraApproval: 'Imvume ye-SAHPRA',
        ethicsCommittee: 'IKomiti Yemimiselo',
        irbApproval: 'Imvume ye-IRB',
        regulatorySubmission: 'Ukunikelwa Komthetho',
        complianceStatus: 'Isimo Sokuthobela',
        documentSubmission: 'Ukunikelwa Kwexwebhu',
        approvalPending: 'Imvume Iyalindwa',
        approved: 'Yamkelwe',
        rejected: 'Yaliwe',
        underReview: 'Phantsi Kophononongo',
        expirationDate: 'Umhla Wokuphelelwa',
        renewalRequired: 'Ukuhlaziywa Kuyafuneka',
      },
    }
  },
  // Kinyarwanda (Rwanda)
  rw: {
    translation: {
      common: {
        welcome: 'Murakaza neza',
        loading: 'Birimo...',
        save: 'Bika',
        cancel: 'Hagarika',
        submit: 'Ohereza',
        search: 'Shakisha',
        filter: 'Shungura',
        success: 'Byagenze neza',
        error: 'Ikosa',
        back: 'Subira inyuma',
        next: 'Komeza',
        previous: 'Ibanje',
        close: 'Funga',
        open: 'Fungura',
        view: 'Reba',
        edit: 'Hindura',
        delete: 'Siba',
        confirm: 'Emeza',
        yes: 'Yego',
        no: 'Oya',
        actions: 'Ibikorwa',
        status: 'Imimerere',
        date: 'Itariki',
        time: 'Igihe',
        download: 'Kuramo',
        upload: 'Kohereza',
        refresh: 'Kuvugurura',
        retry: 'Ongera ugerageze',
        details: 'Ibisobanuro',
        summary: 'Incamake',
      },
      navigation: {
        home: 'Ahabanza',
        trials: 'Ibizamini by\'ubuvuzi',
        about: 'Abo turi',
        regions: 'Intara',
        conditions: 'Indwara',
        contact: 'Twandikire',
        login: 'Injira',
        register: 'Iyandikishe',
        dashboard: 'Imbonerahamwe',
        profile: 'Umwirondoro',
        settings: 'Igenamiterere',
        logout: 'Sohoka',
      },
      trials: {
        clinicalTrial: 'Ikizamini cy\'ubuvuzi',
        enrollment: 'Kwiyandikisha',
        recruiting: 'Barashaka abakandida',
        phase1: 'Icyiciro cya 1',
        phase2: 'Icyiciro cya 2',
        phase3: 'Icyiciro cya 3',
        phase4: 'Icyiciro cya 4',
        eligibility: 'Ibisabwa',
        sponsor: 'Umuterankunga',
        location: 'Ahantu',
        studyType: 'Ubwoko bw\'ubushakashatsi',
        status: 'Imimerere',
        findTrials: 'Shakisha ibizamini',
        matchMe: 'Mfashe kubona',
        learnMore: 'Menya byinshi',
      },
      forms: {
        name: 'Izina',
        email: 'Imeri',
        phone: 'Telefoni',
        dateOfBirth: 'Itariki y\'amavuko',
        gender: 'Igitsina',
        medicalCondition: 'Imimerere y\'ubuzima',
        currentMedications: 'Imiti urimo gufata',
        allergies: 'Alereji',
        required: 'Birakenewe',
        optional: 'Si ngombwa',
        pleaseSelect: 'Hitamo',
        enterValue: 'Andika agaciro',
      },
      onboarding: {
        welcomeTitle: 'Murakaza neza mu bizamini by\'ubuvuzi',
        getStarted: 'Tangira',
        step1Title: 'Amakuru yawe',
        step2Title: 'Amateka y\'ubuzima',
        step3Title: 'Ibyo ushaka',
        completeProfile: 'Uzuza umwirondoro',
        skipForNow: 'Simbuka ubu',
        almostDone: 'Ugiye kurangiza',
        profileComplete: 'Umwirondoro wuzuye',
        continueToTrials: 'Komeza ku bizamini',
      },
      matching: {
        foundTrials: 'Ibizamini byabonetse',
        matchScore: 'Amanota yo guhuza',
        eligibilityMatch: 'Guhuza n\'ibisabwa',
        viewDetails: 'Reba ibisobanuro',
        applyNow: 'Saba ubu',
        noMatches: 'Nta bihuye byabonetse',
        refineSearch: 'Vugurura ishakiro',
        highMatch: 'Guhuza cyane',
        mediumMatch: 'Guhuza hagati',
        lowMatch: 'Guhuza gake',
      },
      siteStaff: {
        patients: 'Abarwayi',
        visits: 'Gusura',
        enrollment: 'Kwiyandikisha',
        activeTrials: 'Ibizamini bikorwa',
        scheduleVisit: 'Shiraho igihe cyo gusura',
        viewPatient: 'Reba umurwayi',
        patientRecords: 'Amadosiye y\'abarwayi',
        addPatient: 'Ongeraho umurwayi',
        patientList: 'Urutonde rw\'abarwayi',
        upcomingVisits: 'Gusura bizaza',
        completedVisits: 'Gusura byarangiye',
        missedVisits: 'Gusura byananiwe',
      },
      consent: {
        title: 'Ifishi yo kwemera',
        readCarefully: 'Soma neza',
        signature: 'Umukono',
        agreeToTerms: 'Nemera amategeko',
        signHere: 'Shyira umukono hano',
        dateOfConsent: 'Itariki yo kwemera',
        witnessSignature: 'Umukono w\'umuhamya',
        consentGiven: 'Kwemera byatanzwe',
        consentPending: 'Kwemera birindirwa',
        consentWithdrawn: 'Kwemera byakuweho',
      },
      dashboard: {
        overview: 'Incamake',
        myTrials: 'Ibizamini byanjye',
        upcomingVisits: 'Gusura bizaza',
        messages: 'Ubutumwa',
        documents: 'Inyandiko',
        settings: 'Igenamiterere',
        notifications: 'Imenyesha',
        quickActions: 'Ibikorwa byihuse',
        recentActivity: 'Ibikorwa bya vuba',
        helpCenter: 'Ikigo cy\'ubufasha',
      },
      errors: {
        genericError: 'Habaye ikosa',
        networkError: 'Ikosa rya murandasi. Reba ukuntu uhuzwa.',
        authError: 'Ikosa ryo kwemeza. Ongera winjire.',
        validationError: 'Ikosa ryo kugenzura. Reba ibyo wanditse.',
        notFound: 'Ntibyabonetse',
        forbidden: 'Kwinjira birabujijwe',
        serverError: 'Ikosa rya seriveri. Ongera ugerageze.',
        sessionExpired: 'Igihe cyarangiye. Ongera winjire.',
        invalidEmail: 'Imeri idakora',
        invalidPhone: 'Nimero ya telefoni idakora',
        requiredField: 'Iki gice girakenewe',
        minLength: 'Bisaba inyuguti {min} gusa',
        maxLength: 'Inyuguti {max} ni zo zerekeye',
        passwordMismatch: 'Ijambo ry\'ibanga ntirihuye',
        invalidDate: 'Itariki idakora',
      },
      notifications: {
        newMessage: 'Ubutumwa bushya',
        visitReminder: 'Urwibutso rwo gusura',
        matchFound: 'Guhuza byabonetse',
        enrollmentApproved: 'Kwiyandikisha byemejwe',
        documentReady: 'Inyandiko yateguwe',
        consentRequired: 'Kwemera birakenewe',
        profileIncomplete: 'Umwirondoro ntuzuye',
        trialUpdate: 'Amakuru mashya y\'ikizamini',
        medicationReminder: 'Urwibutso rw\'imiti',
        appointmentConfirmed: 'Igihe cyemejwe',
        appointmentCancelled: 'Igihe cyahagaritswe',
        markAsRead: 'Shyira nk\'ikisomwe',
      },
      medical: {
        diagnosis: 'Isuzuma',
        treatment: 'Ubuvuzi',
        medication: 'Imiti',
        dosage: 'Ingano',
        frequency: 'Inshuro',
        sideEffects: 'Ingaruka mbi',
        allergies: 'Alereji',
        medicalHistory: 'Amateka y\'ubuzima',
        currentConditions: 'Imimerere y\'ubu',
        familyHistory: 'Amateka y\'umuryango',
      },
      admin: {
        userManagement: 'Gucunga abakoresha',
        siteManagement: 'Gucunga urubuga',
        trialManagement: 'Gucunga ibizamini',
        reports: 'Raporo',
        analytics: 'Isesengura',
        systemHealth: 'Ubuzima bwa sisitemu',
        auditLog: 'Raporo yo kugenzura',
        permissions: 'Uburenganzira',
        configuration: 'Imiterere',
        maintenance: 'Kubungabunga',
      },
      regulatory: {
        rfdaApproval: 'Kwemezwa na RFDA',
        ethicsCommittee: 'Komite y\'imyitwarire',
        irbApproval: 'Kwemezwa na IRB',
        regulatorySubmission: 'Kohereza ku mategeko',
        complianceStatus: 'Imimerere yo kubahiriza',
        documentSubmission: 'Kohereza inyandiko',
        approvalPending: 'Kwemezwa birindirwa',
        approved: 'Byemejwe',
        rejected: 'Byangiwe',
        underReview: 'Birimo gusuzumwa',
        expirationDate: 'Itariki yo kurangira',
        renewalRequired: 'Kongera bisabwa',
      },
    }
  },
  // French (Rwanda, DRC, Francophone Africa)
  fr: {
    translation: {
      common: {
        welcome: 'Bienvenue',
        loading: 'Chargement...',
        save: 'Enregistrer',
        cancel: 'Annuler',
        submit: 'Soumettre',
        search: 'Rechercher',
        filter: 'Filtrer',
        success: 'Succès',
        error: 'Erreur',
        back: 'Retour',
        next: 'Suivant',
        previous: 'Précédent',
        close: 'Fermer',
        open: 'Ouvrir',
        view: 'Voir',
        edit: 'Modifier',
        delete: 'Supprimer',
        confirm: 'Confirmer',
        yes: 'Oui',
        no: 'Non',
        actions: 'Actions',
        status: 'Statut',
        date: 'Date',
        time: 'Heure',
        download: 'Télécharger',
        upload: 'Téléverser',
        refresh: 'Actualiser',
        retry: 'Réessayer',
        details: 'Détails',
        summary: 'Résumé',
      },
      navigation: {
        home: 'Accueil',
        trials: 'Essais Cliniques',
        about: 'À Propos',
        regions: 'Régions',
        conditions: 'Conditions',
        contact: 'Contact',
        login: 'Connexion',
        register: 'Inscription',
        dashboard: 'Tableau de Bord',
        profile: 'Profil',
        settings: 'Paramètres',
        logout: 'Déconnexion',
      },
      trials: {
        clinicalTrial: 'Essai Clinique',
        enrollment: 'Inscription',
        recruiting: 'En Recrutement',
        phase1: 'Phase 1',
        phase2: 'Phase 2',
        phase3: 'Phase 3',
        phase4: 'Phase 4',
        eligibility: 'Critères d\'Éligibilité',
        sponsor: 'Promoteur',
        location: 'Lieu',
        studyType: 'Type d\'Étude',
        status: 'Statut',
        findTrials: 'Trouver des Essais',
        matchMe: 'Me Correspondre',
        learnMore: 'En Savoir Plus',
      },
      forms: {
        name: 'Nom',
        email: 'E-mail',
        phone: 'Téléphone',
        dateOfBirth: 'Date de Naissance',
        gender: 'Genre',
        medicalCondition: 'Condition Médicale',
        currentMedications: 'Médicaments Actuels',
        allergies: 'Allergies',
        required: 'Obligatoire',
        optional: 'Optionnel',
        pleaseSelect: 'Veuillez Sélectionner',
        enterValue: 'Entrer une Valeur',
      },
      onboarding: {
        welcomeTitle: 'Bienvenue aux Essais Cliniques',
        getStarted: 'Commencer',
        step1Title: 'Informations Personnelles',
        step2Title: 'Antécédents Médicaux',
        step3Title: 'Vos Préférences',
        completeProfile: 'Compléter le Profil',
        skipForNow: 'Passer pour l\'instant',
        almostDone: 'Presque Terminé',
        profileComplete: 'Profil Complété',
        continueToTrials: 'Continuer vers les Essais',
      },
      matching: {
        foundTrials: 'Essais Trouvés',
        matchScore: 'Score de Correspondance',
        eligibilityMatch: 'Correspondance d\'Éligibilité',
        viewDetails: 'Voir les Détails',
        applyNow: 'Postuler Maintenant',
        noMatches: 'Aucune Correspondance Trouvée',
        refineSearch: 'Affiner la Recherche',
        highMatch: 'Haute Correspondance',
        mediumMatch: 'Correspondance Moyenne',
        lowMatch: 'Faible Correspondance',
      },
      siteStaff: {
        patients: 'Patients',
        visits: 'Visites',
        enrollment: 'Inscription',
        activeTrials: 'Essais Actifs',
        scheduleVisit: 'Planifier une Visite',
        viewPatient: 'Voir le Patient',
        patientRecords: 'Dossiers Patients',
        addPatient: 'Ajouter un Patient',
        patientList: 'Liste des Patients',
        upcomingVisits: 'Visites à Venir',
        completedVisits: 'Visites Terminées',
        missedVisits: 'Visites Manquées',
      },
      consent: {
        title: 'Formulaire de Consentement',
        readCarefully: 'Lire Attentivement',
        signature: 'Signature',
        agreeToTerms: 'J\'accepte les Conditions',
        signHere: 'Signer Ici',
        dateOfConsent: 'Date de Consentement',
        witnessSignature: 'Signature du Témoin',
        consentGiven: 'Consentement Donné',
        consentPending: 'Consentement en Attente',
        consentWithdrawn: 'Consentement Retiré',
      },
      dashboard: {
        overview: 'Vue d\'Ensemble',
        myTrials: 'Mes Essais',
        upcomingVisits: 'Visites à Venir',
        messages: 'Messages',
        documents: 'Documents',
        settings: 'Paramètres',
        notifications: 'Notifications',
        quickActions: 'Actions Rapides',
        recentActivity: 'Activité Récente',
        helpCenter: 'Centre d\'Aide',
      },
      errors: {
        genericError: 'Une erreur s\'est produite',
        networkError: 'Erreur réseau. Vérifiez votre connexion.',
        authError: 'Erreur d\'authentification. Veuillez vous reconnecter.',
        validationError: 'Erreur de validation. Vérifiez vos entrées.',
        notFound: 'Non trouvé',
        forbidden: 'Accès refusé',
        serverError: 'Erreur serveur. Réessayez plus tard.',
        sessionExpired: 'Session expirée. Veuillez vous reconnecter.',
        invalidEmail: 'Format d\'e-mail invalide',
        invalidPhone: 'Format de numéro de téléphone invalide',
        requiredField: 'Ce champ est obligatoire',
        minLength: 'Minimum {min} caractères requis',
        maxLength: 'Maximum {max} caractères autorisés',
        passwordMismatch: 'Les mots de passe ne correspondent pas',
        invalidDate: 'Format de date invalide',
      },
      notifications: {
        newMessage: 'Nouveau Message',
        visitReminder: 'Rappel de Visite',
        matchFound: 'Correspondance Trouvée',
        enrollmentApproved: 'Inscription Approuvée',
        documentReady: 'Document Prêt',
        consentRequired: 'Consentement Requis',
        profileIncomplete: 'Profil Incomplet',
        trialUpdate: 'Mise à Jour de l\'Essai',
        medicationReminder: 'Rappel de Médicament',
        appointmentConfirmed: 'Rendez-vous Confirmé',
        appointmentCancelled: 'Rendez-vous Annulé',
        markAsRead: 'Marquer comme Lu',
      },
      medical: {
        diagnosis: 'Diagnostic',
        treatment: 'Traitement',
        medication: 'Médicament',
        dosage: 'Dosage',
        frequency: 'Fréquence',
        sideEffects: 'Effets Secondaires',
        allergies: 'Allergies',
        medicalHistory: 'Antécédents Médicaux',
        currentConditions: 'Conditions Actuelles',
        familyHistory: 'Antécédents Familiaux',
      },
      admin: {
        userManagement: 'Gestion des Utilisateurs',
        siteManagement: 'Gestion du Site',
        trialManagement: 'Gestion des Essais',
        reports: 'Rapports',
        analytics: 'Analyses',
        systemHealth: 'Santé du Système',
        auditLog: 'Journal d\'Audit',
        permissions: 'Permissions',
        configuration: 'Configuration',
        maintenance: 'Maintenance',
      },
      regulatory: {
        ansApproval: 'Approbation ANS',
        ethicsCommittee: 'Comité d\'Éthique',
        irbApproval: 'Approbation IRB',
        regulatorySubmission: 'Soumission Réglementaire',
        complianceStatus: 'Statut de Conformité',
        documentSubmission: 'Soumission de Document',
        approvalPending: 'Approbation en Attente',
        approved: 'Approuvé',
        rejected: 'Rejeté',
        underReview: 'En Cours d\'Examen',
        expirationDate: 'Date d\'Expiration',
        renewalRequired: 'Renouvellement Requis',
        // France-specific regulatory terms
        ansmApproval: 'Approbation ANSM',
        cppApproval: 'Approbation CPP',
        cnilCompliance: 'Conformité CNIL',
        rgpdCompliance: 'Conformité RGPD',
        nationalDatabaseIntegration: 'Intégration Base de Données Nationale',
        professionalChannelsOnly: 'Canaux Professionnels Uniquement',
        noWhatsApp: 'WhatsApp Non Autorisé',
        dataResidencyFrance: 'Résidence des Données en France',
        healthDataHosting: 'Hébergement de Données de Santé (HDS)',
        jardéLaw: 'Loi Jardé',
        bioethicsLaw: 'Loi de Bioéthique',
      },
      // France communication channels
      channels: {
        emailOnly: 'E-mail Uniquement',
        smsNotifications: 'Notifications SMS',
        securePortal: 'Portail Sécurisé',
        professionalMessaging: 'Messagerie Professionnelle',
        noConsumerApps: 'Applications Grand Public Non Autorisées',
      },
      // Trial Site portal translations
      trialSite: {
        tabs: {
          dashboard: 'Tableau de Bord',
          ecrf: 'eCRF',
          ecoa: 'eCOA',
          analytics: 'Analyses',
          staff: 'Personnel',
          patients: 'Patients',
          recruitment: 'Recrutement',
          econsent: 'eConsentement',
          safety: 'Sécurité/PV',
          ip: 'Produit Expérimental',
          queries: 'Requêtes',
          regulatory: 'Réglementaire',
          rfis: 'Demandes d\'Information',
          inspections: 'Inspections',
          settings: 'Paramètres',
        },
        header: {
          backToSiteDashboard: 'Retour au Tableau de Bord',
          siteManagement: 'Gestion du Site d\'Essai',
          activeContext: 'Contexte Actif',
          organization: 'Organisation',
        },
        tooltips: {
          dashboard: 'Actions rapides, métriques du site et indicateurs de performance clés',
          ecrf: 'Cahiers d\'Observation Électroniques - saisie et validation des données patients',
          ecoa: 'Évaluations Cliniques Électroniques - questionnaires et résultats rapportés par les patients',
          analytics: 'Métriques de performance du site, tendances d\'inscription et qualité des données',
          staff: 'Gestion des membres du personnel, rôles et certifications',
          patients: 'Liste des patients, statut d\'inscription et suivi de l\'activation',
          recruitment: 'Pipeline de recrutement et gestion de la relation patient',
          econsent: 'Documents de consentement éclairé électronique et signatures',
          safety: 'Déclaration d\'événements indésirables, codage MedDRA, export CIOMS & E2B',
          ip: 'Inventaire et suivi de la dispensation des produits expérimentaux',
          queries: 'Requêtes de données des moniteurs et promoteurs nécessitant une résolution',
          regulatory: 'Documents réglementaires du site, approbations IRB et statut de conformité',
          rfis: 'Demandes d\'Information du promoteur nécessitant une réponse du site',
          inspections: 'Listes de vérification de préparation aux inspections et préparation d\'audit',
          settings: 'Configuration du site, image de marque et préférences de communication',
        },
        dashboard: {
          quickActions: 'Actions Rapides',
          quickActionsDescription: 'Tâches courantes et raccourcis pour la gestion du site',
          triggerMatching: 'Lancer l\'Appariement de Mon Site',
          triggering: 'Lancement...',
          viewPatientRoster: 'Voir la Liste des Patients',
          visitSchedule: 'Calendrier des Visites',
          myAssessments: 'Mes Évaluations',
          trainingVideos: 'Vidéos de Formation',
          viewTrainingManual: 'Voir le Manuel de Formation',
          dctInfrastructure: 'Infrastructure DCT',
          activeTrials: 'Essais Actifs',
          paidAddons: 'Modules Complémentaires Payants',
          paidAddonsDescription: 'Fonctionnalités premium pour améliorer les opérations cliniques et de revenus de votre site',
          enrollmentForecast: 'Prévision d\'Inscription',
          enrollmentForecastDescription: 'Prédictions alimentées par l\'IA pour l\'achèvement de l\'inscription aux essais',
          patientManagementPro: 'Gestion Patient Pro',
          activeTrialsBilling: 'Essais Actifs et Facturation',
          dctModule: 'Module DCT',
          inspectionReadiness: 'Préparation aux Inspections',
        },
        common: {
          pending: 'En Attente',
          inProgress: 'En Cours',
          submitted: 'Soumis',
          accepted: 'Accepté',
          overdue: 'En Retard',
          noSiteSelected: 'Aucun site sélectionné',
          questionnaires: 'Questionnaires',
          completed: 'Terminé',
          totalAssigned: 'Total Assigné',
          assignments: 'Affectations',
          instrumentLibrary: 'Bibliothèque d\'Instruments',
          templates: 'Modèles',
          research: 'Recherche',
          questionnaireBuilder: 'Constructeur de Questionnaires',
          userManual: 'Manuel Utilisateur',
          ecoaManagement: 'Gestion eCOA',
          ecoaDescription: 'Évaluations Cliniques Électroniques pour les patients et les observateurs',
          inbox: 'Boîte de Réception',
          requestsForInformation: 'Demandes d\'Information',
          rfisDescription: 'Gérer et répondre aux DI du promoteur et aux questionnaires de faisabilité',
          logExternalRfi: 'Enregistrer une DI Externe',
          siteSettings: 'Paramètres du Site',
          siteSettingsDescription: 'Mettre à jour les détails du site et configurer les modules DCT',
          siteDetails: 'Détails du Site',
          editSiteDetails: 'Modifier les Détails du Site',
          configureDctModules: 'Configurer les Modules DCT',
          saveChanges: 'Enregistrer les Modifications',
          saving: 'Enregistrement...',
          siteName: 'Nom du Site',
          city: 'Ville',
          country: 'Pays',
          notSet: 'Non défini',
        },
        investigator: {
          totalPatients: 'Total Patients',
          visitCompletion: 'Achèvement des Visites',
          protocolDeviations: 'Déviations du Protocole',
          adverseEvents: 'Événements Indésirables',
          requiresReview: 'Nécessite une revue',
          enrollmentStatus: 'Statut d\'Inscription',
          screening: 'Présélection',
          active: 'Actif',
          withdrawn: 'Retiré',
          aeSeverity: 'Distribution de la Sévérité des EI',
          mild: 'Léger',
          moderate: 'Modéré',
          severe: 'Sévère',
          lifeThreatening: 'Mettant en jeu le pronostic vital',
          recentActivity: 'Activité Récente du Site',
          noRecentActivity: 'Aucune activité récente',
          patientEnrolled: 'Patient Inscrit',
          patientAssigned: 'Patient Assigné',
        },
        analytics: {
          siteAnalyticsDashboard: 'Tableau de Bord Analytique du Site',
          performanceMetrics: 'Métriques de performance complètes pour tous les essais',
          performanceMetricsFor: 'Métriques de performance pour',
          refresh: 'Actualiser',
          exportReport: 'Exporter le Rapport',
          screeningConversion: 'Conversion de Présélection',
          visitCompletion: 'Achèvement des Visites',
          protocolQuality: 'Qualité du Protocole',
          documentCompliance: 'Conformité Documentaire',
          excellent: 'Excellent',
          needsImprovement: 'À améliorer',
          onTime: 'à l\'heure',
          openQueries: 'requêtes ouvertes',
          good: 'Bon',
          reviewRequired: 'Revue Requise',
          monthlyRecruitment: 'Recrutement Mensuel',
          patientsEnrolledLast30: 'Patients inscrits ces 30 derniers jours',
          totalEnrolled: 'Total Inscrits',
          activeAndCompleted: 'Patients actifs et terminés',
          dropoutRate: 'Taux d\'Abandon',
          withinAcceptableRange: 'Dans la plage acceptable',
          aboveIndustryAverage: 'Au-dessus de la moyenne du secteur',
          enrollmentTrends: 'Tendances d\'Inscription',
          enrollmentTrendsDesc: 'Inscription des patients sur les 6 derniers mois',
          noEnrollmentData: 'Aucune donnée d\'inscription disponible',
          visitStatusDistribution: 'Répartition du Statut des Visites',
          visitStatusDesc: 'Statut actuel de toutes les visites patients',
          noVisitData: 'Aucune donnée de visite disponible',
          protocolDeviationsChart: 'Déviations du Protocole',
          deviationsBySeverity: 'Répartition par sévérité',
          noProtocolDeviations: 'Aucune Déviation du Protocole',
          excellentCompliance: 'Excellente conformité au protocole',
          siteQualityScore: 'Score Qualité du Site',
          overallAssessment: 'Évaluation globale des performances',
          visitCompliance: 'Conformité des Visites',
          queryResolution: 'Résolution des Requêtes',
          documentCompletion: 'Complétion Documentaire',
          onTimeVisits: 'Visites à l\'Heure',
          overallPerformance: 'Performance Globale du Site',
          exceptional: 'Exceptionnel',
          fair: 'Correct',
          failedToLoad: 'Échec du Chargement des Analyses',
          retry: 'Réessayer',
          analyticsRefreshed: 'Analyses Actualisées',
          dataRefreshed: 'Données actualisées avec succès.',
          allTrialsRefreshed: 'Les données de tous les essais ont été actualisées.',
          enrolled: 'Inscrits',
          scheduled: 'Planifiées',
          missed: 'Manquées',
          major: 'Majeur',
          minor: 'Mineur',
          queryResolutionLabel: 'Résolution des requêtes',
          screened: 'présélectionnés',
          visits: 'visites',
        },
        roster: {
          visits: 'Visites',
          addPatient: 'Ajouter un Patient',
          bulkUpload: 'Import Groupé',
          sendInvite: 'Envoyer une Invitation',
          matchAllPatients: 'Apparier Tous les Patients',
          thisMonth: 'Ce Mois',
          verified: 'Vérifié',
          pendingLogin: 'Connexion en Attente',
          matched: 'Appariés',
          profileComplete: 'Profil Complet',
          total: 'Total',
          searchPlaceholder: 'Rechercher par nom ou e-mail...',
          loadingMore: 'Chargement...',
          loadingPatients: 'Chargement des patients...',
          errorLoadingPatients: 'Erreur de chargement des patients',
          showing: 'Affichage',
          of: 'sur',
          patients: 'patients',
          patient: 'Patient',
          type: 'Type',
          contact: 'Contact',
          loginStatus: 'Statut de Connexion',
          healthProfile: 'Profil de Santé',
          consentStatus: 'Statut du Consentement',
          trialMatching: 'Appariement aux Essais',
          workflow: 'Flux de Travail',
          actions: 'Actions',
          notSent: 'Non Envoyé',
          pending: 'En Attente',
          complete: 'Complet',
          incomplete: 'Incomplet',
          notMatched: 'Non Apparié',
          runMatch: 'Lancer l\'Appariement',
          match: 'Apparier',
          open: 'Ouvrir',
          noContactInfo: 'Aucune info de contact',
          noPatients: 'Aucun patient pour le moment',
          noMatchingPatients: 'Aucun patient ne correspond à votre recherche',
          tryAgain: 'Réessayer',
          reloadPage: 'Recharger la Page',
        },
        staff: {
          siteStaff: 'Personnel du Site',
          manageTeam: 'Gérer les membres de l\'équipe assignés à ce site d\'essai',
          addStaffMember: 'Ajouter un Membre',
          staffEmail: 'E-mail du Personnel',
          emailPlaceholder: 'personnel@exemple.com',
          emailHint: 'Si l\'utilisateur n\'a pas de compte, il recevra un e-mail d\'invitation',
          firstName: 'Prénom (Optionnel)',
          lastName: 'Nom (Optionnel)',
          role: 'Rôle',
          selectRole: 'Sélectionner un rôle',
          adding: 'Ajout...',
          addStaff: 'Ajouter',
          cancel: 'Annuler',
          loadingStaff: 'Chargement du personnel...',
          errorLoadingStaff: 'Erreur de chargement du personnel',
          name: 'Nom',
          email: 'E-mail',
          compliance: 'Conformité',
          status: 'Statut',
          active: 'Actif',
          inactive: 'Inactif',
          noStaff: 'Aucun membre du personnel assigné',
          invitationSent: 'Invitation envoyée',
          staffAdded: 'Membre ajouté',
          failedToAdd: 'Échec de l\'ajout',
          staffRemoved: 'Membre retiré',
          staffRemovedDesc: 'Le membre du personnel a été marqué comme inactif.',
          failedToRemove: 'Échec du retrait',
          missingInfo: 'Informations manquantes',
          provideEmailAndRole: 'Veuillez fournir l\'e-mail et le rôle',
          attestationRequired: 'Attestation requise',
          attestationHint: 'Veuillez confirmer que le membre du personnel a été informé des pratiques de traitement des données',
        },
        addons: {
          clinical: 'Clinique',
          revenue: 'Revenus',
          decentralized: 'Décentralisé',
          complianceLabel: 'Conformité',
          patientManagementDesc: 'Cartographie OMOP complète, données démographiques de diversité, conditions médicales détaillées',
          omopSdtm: 'Standardisation des données OMOP/SDTM',
          advancedDemographics: 'Suivi démographique avancé',
          medicalHistory: 'Gestion des antécédents médicaux',
          billingDesc: 'Gérer les essais actifs, les grilles tarifaires, les événements facturables, la facturation',
          trialActivation: 'Activation et gestion des essais',
          feeSchedule: 'Configuration des grilles tarifaires',
          automatedInvoicing: 'Facturation automatisée',
          dctDesc: 'Visites virtuelles, surveillance à distance, consentement numérique et intégration de télésanté',
          telehealth: 'Consultations vidéo de télésanté',
          remoteMonitoring: 'Surveillance à distance des patients',
          digitalConsent: 'Plateforme de consentement numérique',
          inspectionDesc: 'Listes de vérification pré-inspection, analyse des lacunes documentaires et formation aux inspections simulées',
          ichGcpChecklist: '20+ éléments de la checklist ICH-GCP',
          etmfGapAnalysis: 'Analyse automatisée des lacunes eTMF',
          mockInspection: 'Formation aux inspections simulées',
          offlineTooltip: 'Télécharger les données patients, formulaires et protocoles pour le travail de terrain hors ligne dans les zones sans connectivité',
        },
        tabContent: {
          patients: {
            description: 'Toutes les personnes inscrites sur votre site. Cliquez sur un nom pour voir leurs détails, visites et correspondances d\'essais.',
          },
          staff: {
            description: 'Personnes travaillant sur votre site. Ajoutez du nouveau personnel, modifiez les rôles et gérez les certificats de formation.',
          },
          regulatory: {
            description: 'Documents et approbations requis par les autorités. Tenez à jour la mise en place de l\'étude, les documents TMF et la gestion des données.',
            studySetup: 'Mise en Place de l\'Étude',
            tmfDocuments: 'TMF & Documents',
            dataManagement: 'Gestion des Données',
            oversightStaff: 'Supervision & Personnel',
          },
          safety: {
            description: 'Signalez et suivez si un patient se sent mal à cause du médicament d\'étude. Enregistrez les effets secondaires, examinez les cas et soumettez les rapports de sécurité.',
            adverseEvents: 'Événements Indésirables',
            adverseEventsShort: 'EI/EIG',
            reviewReporting: 'Examen & Rapports',
            reviewShort: 'Examen',
            medications: 'Médicaments',
            compliance: 'Conformité',
          },
          ip: {
            title: 'Gestion du PI & Randomisation',
            help: 'Gérez l\'inventaire du Produit Investigationnel (PI), la Technologie de Réponse Interactive (IRT/RTSM) pour la randomisation et la gestion des approvisionnements, et le suivi des kits de laboratoire central.',
            learnMore: 'Ce module couvre trois domaines clés : IRT/RTSM pour la randomisation des kits de médicament, la dispensation et le déblindage d\'urgence ; Inventaire PI pour la responsabilité du médicament et la conformité de stockage ; et Laboratoire Central pour le suivi des kits d\'échantillons et la gestion des résultats de biomarqueurs. Toutes les opérations sont enregistrées pour la conformité ICH-GCP §5.14.',
            irtRtsm: 'IRT / RTSM',
            ipInventory: 'Inventaire PI',
            centralLab: 'Laboratoire Central',
          },
          ecoa: {
            tipQuestionnaires: 'Total de questionnaires actifs disponibles pour attribution',
            tipPending: 'Attributions en attente de complétion par les patients ou observateurs',
            tipCompleted: 'Questionnaires soumis avec succès et résultats notés',
            tipTotal: 'Total des attributions de questionnaires sur tous les patients',
            builderHelp: 'Créez des questionnaires eCOA personnalisés à partir de zéro ou personnalisez des instruments importés pour les besoins spécifiques de votre essai.',
            builderDesc: 'Créez des questionnaires eCOA personnalisés ou importez depuis la bibliothèque',
          },
          etmf: {
            siteEtmfTitle: 'Documents eTMF du Site',
            headerNote: 'Visualisez et suivez les documents essentiels de votre site classés selon le Modèle de Référence DIA TMF (v3.3). Les documents téléchargés ici ou via le panneau AI Filing du CRO apparaissent organisés par zone. Les documents spécifiques au site incluent les approbations IRB, les CV du personnel, les journaux de délégation et les registres de formation.',
            overallCompleteness: 'Complétude Globale',
            totalDocuments: 'Total Documents',
            zonesWithGaps: 'Zones avec Lacunes',
            acrossZones: 'Sur {{n}} des 13 zones',
            zonesBelowExpected: 'Zones sous le nombre de documents attendu',
            zoneCompletenessTitle: 'Complétude par Zone — DIA TMF v3.3',
            zoneLabel: 'Zone',
            docsCount: '{{actual}}/{{expected}} docs',
            docsFiled: '{{actual}} sur {{expected}} documents attendus déposés',
            clickToFilter: 'Cliquez pour filtrer les documents par cette zone',
            siteDocuments: 'Documents du Site',
            searchPlaceholder: 'Rechercher des documents...',
            loading: 'Chargement des documents...',
            noDocsFound: 'Aucun document trouvé',
            emptyHintNone: 'Les documents déposés via le panneau AI Filing du CRO ou téléchargés directement apparaîtront ici.',
            emptyHintFiltered: 'Essayez d\'ajuster votre recherche ou filtre de zone.',
            colDocument: 'Document',
            colType: 'Type',
            colZone: 'Zone',
            colStatus: 'Statut',
            colDate: 'Date',
            unfiled: 'Non classé',
          },
          dashboard: {
            fieldWorker: 'Agent de Terrain',
            fieldWorkerTooltip: 'Interface mobile simplifiée pour la collecte de données sur le terrain — signes vitaux, eCOA, documents, diagnostic IA',
          },
          settings: {
            title: 'Paramètres du Site',
            selectCountry: 'Sélectionnez un pays',
            statusActive: 'Actif',
            reasonPlaceholder: 'Décrivez la raison de ce changement...',
            brandingUpdatedTitle: 'Image de marque mise à jour',
            brandingUpdatedDesc: 'L\'image de marque de votre site a été mise à jour avec succès',
            complianceInfra: 'Conformité & Infrastructure',
            assessmentsLifecycle: 'Évaluations & Cycle de Vie',
            totRequiresOrg: 'Le Transfert d\'Obligations nécessite un lien d\'organisation.',
          },
        },
      },
      phc: {
        nav: {
          home: 'Accueil',
          register: 'Enregistrer',
          sync: 'Synchroniser',
          help: 'Aide',
          referrals: 'Références',
          aefi: 'Signaler AEFI',
          stats: 'Statistiques',
          supervisor: 'Superviseur',
          surveillance: 'Surveillance',
          dhis2: 'DHIS2',
          settings: 'Paramètres',
          logout: 'Déconnexion',
          helpTooltip: 'Trouvez des articles d\'aide, des guides de formation et des instructions étape par étape. Fonctionne aussi hors ligne !',
          surveillanceTooltip: 'Ouvrir le tableau de bord de surveillance gouvernemental pour la surveillance des maladies en temps réel',
          dhis2Tooltip: 'Voir le tableau de bord d\'intelligence sanitaire DHIS2',
        },
        toasts: {
          notAuthenticated: 'Non authentifié',
          syncFailed: 'Échec de la synchronisation',
          syncComplete: 'Synchronisation terminée',
          syncCompleteDesc: 'Toutes les données sont à jour.',
          syncError: 'Erreur de synchronisation',
          unknownError: 'Erreur inconnue',
          signedOut: 'Déconnexion réussie',
          signOutError: 'Erreur lors de la déconnexion',
          surveillanceUnavailable: 'Portail de surveillance non disponible',
          surveillanceUnavailableDesc: 'Aucun portail de surveillance gouvernemental n\'est lié à votre établissement.',
        },
        knowledgeBase: {
          title: 'Aide et Base de Connaissances - PHC',
          description: 'Trouvez des articles d\'aide, des guides de formation et des instructions pour les agents de santé communautaires.',
          headerTitle: 'Aide et Formation',
        },
      },
      patientSelfTriage: {
        seo: {
          title: 'Vérifier mes symptômes | Auto-Triage Patient',
          description: 'Vérification gratuite et privée des symptômes. Fonctionne hors ligne. Vous connecte à la clinique la plus proche.',
          resultTitle: 'Votre résultat de contrôle | Passeport Santé',
        },
        back: 'Retour',
        disclaimer: "Ceci est une auto-vérification, <strong>pas un diagnostic</strong>. Si vous vous sentez très malade, allez à la clinique maintenant ou appelez votre agent de santé communautaire.",
        voice: {
          cantRead: 'Vous ne savez pas lire ? Parlez à la place.',
          guide: "Parlez en anglais ou en swahili. L'assistant vous guidera.",
          start: 'Commencer',
        },
        header: {
          title: 'Vérifier mes symptômes',
          subtitle: "Cochez ce qui ne va pas. Nous vous dirons quoi faire. Ce n'est pas un médecin.",
        },
        field: {
          age: 'Votre âge (années)',
          sex: 'Sexe à la naissance',
          sexUnknown: 'Préfère ne pas dire',
          sexFemale: 'Femme',
          sexMale: 'Homme',
          pregnant: 'Je suis enceinte',
        },
        gps: {
          label: 'Trouver ma clinique',
          use: 'Utiliser ma position',
          update: 'Mettre à jour ma position',
          findingClinic: 'Recherche de la clinique la plus proche…',
          notSupported: 'Localisation non prise en charge sur cet appareil.',
          failed: 'Impossible d’obtenir la position. Vous pouvez continuer.',
          listOffline: 'Liste des cliniques indisponible hors ligne. Nous enregistrerons votre vérification.',
        },
        callback: {
          label: 'Téléphone pour rappel (facultatif)',
          placeholder: '+254 7...',
          help: 'Un agent de santé peut appeler ou envoyer WhatsApp/SMS.',
        },
        consent: {
          prompt: 'Parlez-moi des études ?',
          promptBody: 'Si une étude clinique dans ma clinique peut m’aider, je veux le savoir. Je peux dire non plus tard. (Consentement général uniquement.)',
          signedBy: 'Signé par',
          change: 'modifier',
          open: 'Ouvrir le consentement (écouter + signer)',
        },
        submit: {
          button: 'Voir mon résultat',
          signInTip: 'Astuce : connectez-vous pour enregistrer vos vérifications dans votre Passeport Santé.',
          signInToSend: 'Votre vérification est prête. Connectez-vous pour l’envoyer à votre clinique.',
          pickClinic: 'Veuillez choisir votre clinique pour que nous puissions envoyer votre vérification.',
          savedOffline: 'Enregistré hors ligne. Nous enverrons une fois en ligne.',
          sent: 'Envoyé à votre clinique la plus proche.',
          savedRetry: 'Enregistré sur ce téléphone. Nous renverrons plus tard.',
        },
        result: {
          notDiagnosis: "Ceci est une vérification, pas un diagnostic médical.",
          dangerTitle: 'Signes de danger détectés',
          dangerBody: 'Veuillez vous rendre à la clinique la plus proche immédiatement. Si vous avez un téléphone, appelez votre agent de santé communautaire.',
          callbackNote: 'Un agent de santé appellera <strong>{{phone}}</strong> si nécessaire.',
          trialConsentNote: 'Vous avez accepté d’en savoir plus sur les études. La clinique verra si une étude peut vous aider.',
          checkAgain: 'Vérifier à nouveau',
          downloadPdf: 'Télécharger PDF',
          backToPassport: 'Retour à mon passeport',
          pdfSuccess: 'PDF téléchargé',
          pdfError: 'Impossible de générer le PDF',
        },
      },
    }
  },
  mg: {
    translation: {
      common: {
        welcome: 'Tongasoa',
        loading: 'Miandry...',
        save: 'Tehirizo',
        cancel: 'Ajanony',
        submit: 'Alefa',
        search: 'Tadiavo',
        filter: 'Sivano',
        success: 'Fahombiazana',
        error: 'Hadisoana',
        back: 'Miverina',
        next: 'Manaraka',
        previous: 'Teo aloha',
        close: 'Akatona',
        open: 'Sokafy',
        view: 'Jereo',
        edit: 'Ovao',
        delete: 'Fafao',
        confirm: 'Hamafiso',
        yes: 'Eny',
        no: 'Tsia',
        actions: 'Hetsika',
        status: 'Sata',
        date: 'Daty',
        time: 'Ora',
        download: 'Alao',
        upload: 'Ampidiro',
        refresh: 'Vaozina',
        retry: 'Avereno',
        details: 'Antsipiriany',
        summary: 'Famintinana',
      },
      navigation: {
        home: 'Fandraisana',
        trials: 'Fitsapana Ara-pitsaboana',
        about: 'Momba anay',
        regions: 'Faritra',
        conditions: 'Aretina',
        contact: 'Fifandraisana',
        login: 'Hiditra',
        register: 'Hisoratra anarana',
        dashboard: 'Tabilao',
        profile: 'Mombamomba anao',
        settings: 'Fanamboarana',
        logout: 'Hivoaka',
      },
      trials: {
        clinicalTrial: 'Fitsapana Ara-pitsaboana',
        enrollment: 'Fisoratana anarana',
        recruiting: 'Mitady olona',
        phase1: 'Dingana 1',
        phase2: 'Dingana 2',
        phase3: 'Dingana 3',
        phase4: 'Dingana 4',
        eligibility: 'Fepetra takiana',
        sponsor: 'Mpanohana',
        location: 'Toerana',
        studyType: 'Karazana fikarohana',
        status: 'Sata',
        findTrials: 'Tadiavo fitsapana',
        matchMe: 'Ampifanaraho amiko',
        learnMore: 'Fantaro bebe kokoa',
      },
      forms: {
        name: 'Anarana',
        email: 'Mailaka',
        phone: 'Finday',
        dateOfBirth: 'Daty nahaterahana',
        gender: 'Lahy na vavy',
        medicalCondition: 'Aretina',
        currentMedications: 'Fanafody ankehitriny',
        allergies: 'Arerijia',
        required: 'Ilaina',
        optional: 'Tsy voatery',
        pleaseSelect: 'Misafidiana',
        enterValue: 'Ampidiro sanda',
      },
      onboarding: {
        welcomeTitle: 'Tongasoa amin\'ny Fitsapana Ara-pitsaboana',
        getStarted: 'Manomboha',
        step1Title: 'Mombamomba anao',
        step2Title: 'Tantaran\'ny fahasalamanao',
        step3Title: 'Ny safidinao',
        completeProfile: 'Fenoy ny mombamomba anao',
        skipForNow: 'Avela aloha',
        almostDone: 'Efa akaiky vita',
        profileComplete: 'Vita ny mombamomba anao',
        continueToTrials: 'Handroso any amin\'ny fitsapana',
      },
      matching: {
        foundTrials: 'Fitsapana hita',
        matchScore: 'Isa fampifanarahana',
        eligibilityMatch: 'Fampifanarahana fepetra',
        viewDetails: 'Jereo antsipiriany',
        applyNow: 'Misoratra anarana izao',
        noMatches: 'Tsy misy mifanaraka',
        refineSearch: 'Hamaroina ny fikarohana',
        highMatch: 'Mifanaraka tsara',
        mediumMatch: 'Mifanaraka antonony',
        lowMatch: 'Tsy dia mifanaraka',
      },
      siteStaff: {
        patients: 'Marary',
        visits: 'Famangiana',
        enrollment: 'Fisoratana anarana',
        activeTrials: 'Fitsapana mandeha',
        scheduleVisit: 'Manao fandaharana famangiana',
        viewPatient: 'Jereo ny marary',
        patientRecords: 'Rakitra marary',
        addPatient: 'Hanampy marary',
        patientList: 'Lisitry ny marary',
        upcomingVisits: 'Famangiana ho avy',
        completedVisits: 'Famangiana vita',
        missedVisits: 'Famangiana diso',
      },
      consent: {
        title: 'Taratasy fanekena',
        readCarefully: 'Vakio tsara',
        signature: 'Sonia',
        agreeToTerms: 'Manaiky ny fepetra',
        signHere: 'Soniavy eto',
        dateOfConsent: 'Daty nanekena',
        witnessSignature: 'Sonia vavolombelona',
        consentGiven: 'Fanekena nomena',
        consentPending: 'Fanekena mbola andrasana',
        consentWithdrawn: 'Fanekena nesorina',
      },
      dashboard: {
        overview: 'Fijery ankapobeny',
        myTrials: 'Ny fitsapako',
        upcomingVisits: 'Famangiana ho avy',
        messages: 'Hafatra',
        documents: 'Antontan-taratasy',
        settings: 'Fanamboarana',
        notifications: 'Fampandrenesana',
        quickActions: 'Hetsika haingana',
        recentActivity: 'Hetsika vao haingana',
        helpCenter: 'Foibe fanampiana',
      },
      errors: {
        genericError: 'Nisy hadisoana nitranga',
        networkError: 'Olana amin\'ny fifandraisana. Hamarino ny internet.',
        authError: 'Olana fanamarinana. Mba midira indray.',
        validationError: 'Olana fanamarinana. Hamarino ny nampidirinao.',
        notFound: 'Tsy hita',
        forbidden: 'Tsy azo idirana',
        serverError: 'Olana amin\'ny mpizara. Andramo aoriana.',
        sessionExpired: 'Tapitra ny fotoana. Mba midira indray.',
        invalidEmail: 'Tsy mety ny endrika mailaka',
        invalidPhone: 'Tsy mety ny laharan-finday',
        requiredField: 'Ilaina ity saha ity',
        minLength: 'Farafahakeliny {min} litera ilaina',
        maxLength: 'Farafahabetsany {max} litera azo atao',
        passwordMismatch: 'Tsy mitovy ny teny miafina',
        invalidDate: 'Tsy mety ny endrika daty',
      },
      notifications: {
        newMessage: 'Hafatra vaovao',
        visitReminder: 'Fampahatsiahivana famangiana',
        matchFound: 'Misy mifanaraka',
        enrollmentApproved: 'Fisoratana anarana nekena',
        documentReady: 'Antontan-taratasy vonona',
        consentRequired: 'Fanekena ilaina',
        profileIncomplete: 'Tsy feno ny mombamomba anao',
        trialUpdate: 'Vaovao momba ny fitsapana',
        medicationReminder: 'Fampahatsiahivana fanafody',
        appointmentConfirmed: 'Fotoana voamarina',
        appointmentCancelled: 'Fotoana nofoanana',
        markAsRead: 'Mariho ho voavaky',
      },
      medical: {
        diagnosis: 'Fanamarinana aretina',
        treatment: 'Fitsaboana',
        medication: 'Fanafody',
        dosage: 'Habetsaky ny fanafody',
        frequency: 'Matetika',
        sideEffects: 'Voka-dratsy',
        allergies: 'Arerijia',
        medicalHistory: 'Tantaran\'ny fahasalamana',
        currentConditions: 'Aretina ankehitriny',
        familyHistory: 'Tantaran\'ny fianakaviana',
      },
      admin: {
        userManagement: 'Fitantanana mpampiasa',
        siteManagement: 'Fitantanana tranokala',
        trialManagement: 'Fitantanana fitsapana',
        reports: 'Tatitra',
        analytics: 'Famakafakana',
        systemHealth: 'Fahasalaman\'ny rafitra',
        auditLog: 'Rakitra fanamarinana',
        permissions: 'Fahazoan-dalana',
        configuration: 'Fanamboarana',
        maintenance: 'Fikojakojana',
      },
      regulatory: {
        apmmApproval: 'Fankatoavan\'ny APMM',
        ethicsCommittee: 'Komitin\'ny etika',
        irbApproval: 'Fankatoavan\'ny IRB',
        regulatorySubmission: 'Fandefasana ara-pitsipika',
        complianceStatus: 'Sata fanarahana',
        documentSubmission: 'Fandefasana antontan-taratasy',
        approvalPending: 'Fankatoavana andrasana',
        approved: 'Voankato',
        rejected: 'Nolavina',
        underReview: 'Eo am-pisavana',
        expirationDate: 'Daty fahataperana',
        renewalRequired: 'Ilaina fanavaozana',
        // Madagascar-specific terms
        cnamApproval: 'Fankatoavan\'ny CNAM',
        ministryOfHealth: 'Minisiteran\'ny Fahasalamana',
        dataProtectionLaw: 'Lalàna fiarovana angona',
        localEthicsBoard: 'Komitin\'ny etika eo an-toerana',
      },
      // Madagascar data protection
      dataProtection: {
        law2014038: 'Lalàna 2014-038',
        cmil: 'CMIL (Commission Malgache)',
        personalDataRights: 'Zon\'ny angona manokana',
        dataProcessing: 'Fanodinana angona',
        consentRequired: 'Fanekena ilaina',
        crossBorderTransfer: 'Famindrana mihoatra ny sisintany',
      },
    }
  },
  // ===== PORTUGUESE (Lusophone Africa — Mozambique, Angola, Guinea-Bissau) =====
  pt: {
    translation: {
      common: {
        welcome: 'Bem-vindo',
        loading: 'Carregando...',
        save: 'Salvar',
        cancel: 'Cancelar',
        submit: 'Enviar',
        search: 'Pesquisar',
        filter: 'Filtrar',
        success: 'Sucesso',
        error: 'Erro',
        back: 'Voltar',
        next: 'Próximo',
        previous: 'Anterior',
        close: 'Fechar',
        open: 'Abrir',
        view: 'Ver',
        edit: 'Editar',
        delete: 'Eliminar',
        confirm: 'Confirmar',
        yes: 'Sim',
        no: 'Não',
      },
      navigation: {
        home: 'Início',
        trials: 'Ensaios Clínicos',
        about: 'Sobre Nós',
        regions: 'Regiões',
        conditions: 'Condições',
        contact: 'Contacto',
        login: 'Entrar',
        register: 'Registar',
        dashboard: 'Painel',
        profile: 'Perfil',
      },
      trials: {
        clinicalTrial: 'Ensaio Clínico',
        enrollment: 'Inscrição',
        recruiting: 'Recrutando',
        phase1: 'Fase 1',
        phase2: 'Fase 2',
        phase3: 'Fase 3',
        phase4: 'Fase 4',
        eligibility: 'Critérios de Elegibilidade',
        sponsor: 'Patrocinador',
        location: 'Localização',
        studyType: 'Tipo de Estudo',
        status: 'Estado',
        findTrials: 'Encontrar Ensaios',
        matchMe: 'Encontrar para Mim',
        learnMore: 'Saber Mais',
      },
      forms: {
        name: 'Nome',
        email: 'Email',
        phone: 'Telefone',
        dateOfBirth: 'Data de Nascimento',
        gender: 'Género',
        medicalCondition: 'Condição Médica',
        currentMedications: 'Medicamentos Atuais',
        allergies: 'Alergias',
        required: 'Obrigatório',
      },
      // Trial Site portal translations (mirrors en.translation.trialSite)
      trialSite: {
        tabs: {
          dashboard: 'Painel',
          ecrf: 'eCRF',
          ecoa: 'eCOA',
          analytics: 'Análises',
          staff: 'Equipa',
          patients: 'Pacientes',
          recruitment: 'CRM',
          econsent: 'eConsentimento',
          safety: 'Segurança/PV',
          ip: 'Gestão IRT / IP',
          queries: 'Consultas',
          regulatory: 'Regulamentar',
          rfis: 'Pedidos de Informação',
          inspections: 'Inspecções',
          settings: 'Definições',
        },
        header: {
          backToSiteDashboard: 'Voltar ao Painel do Centro',
          siteManagement: 'Gestão do Centro de Ensaios',
          activeContext: 'Contexto Activo',
          organization: 'Organização',
        },
        tooltips: {
          dashboard: 'A sua página principal. Veja números de pacientes, progresso de visitas e alertas importantes.',
          ecrf: 'Formulários electrónicos onde regista os dados do ensaio em cada visita do paciente.',
          ecoa: 'Questionários de saúde que os pacientes preenchem sobre como se sentem.',
          analytics: 'Gráficos e números que mostram como o seu centro está a desempenhar.',
          staff: 'Adicione membros da equipa, defina palavras-passe e verifique a formação.',
          patients: 'Todos os seus pacientes. Adicione novos, verifique o estado e gira registos.',
          recruitment: 'Encontre e inscreva pacientes para ensaios. Acompanhe cada paciente no processo.',
          econsent: 'Obtenha consentimento do paciente electronicamente. Assinam no ecrã ou via WhatsApp.',
          safety: 'Reporte problemas de saúde durante o ensaio. Muito importante — faça-o rapidamente.',
          ip: 'Gira o medicamento do estudo. Acompanhe o que foi recebido, distribuído e devolvido.',
          queries: 'Perguntas do patrocinador sobre os seus dados. Abra cada uma e corrija o problema.',
          regulatory: 'Carregue e gira documentos exigidos como aprovações éticas e CVs da equipa.',
          rfis: 'Pedidos de informação do patrocinador. Responda aqui.',
          inspections: 'Prepare auditorias e inspecções. Verifique se todos os documentos estão prontos.',
          settings: 'Altere o nome do centro, logótipo e outras definições.',
        },
        dashboard: {
          quickActions: 'Acções Rápidas',
          quickActionsDescription: 'Tarefas comuns e atalhos para a gestão do centro',
          triggerMatching: 'Iniciar Correspondência do Centro',
          triggering: 'A iniciar...',
          viewPatientRoster: 'Ver Lista de Pacientes',
          visitSchedule: 'Calendário de Visitas',
          myAssessments: 'As Minhas Avaliações',
          trainingVideos: 'Vídeos de Formação',
          viewTrainingManual: 'Ver Manual de Formação',
          dctInfrastructure: 'Infra-estrutura DCT',
          activeTrials: 'Ensaios Activos',
          paidAddons: 'Módulos Pagos',
          paidAddonsDescription: 'Funcionalidades premium para reforçar a operação clínica e financeira do centro',
          enrollmentForecast: 'Previsão de Inscrições',
          enrollmentForecastDescription: 'Previsões com IA para a conclusão das inscrições do ensaio',
          patientManagementPro: 'Gestão de Pacientes Pro',
          activeTrialsBilling: 'Ensaios Activos e Facturação',
          dctModule: 'Módulo DCT',
          inspectionReadiness: 'Prontidão para Inspecção',
        },
        common: {
          pending: 'Pendente',
          inProgress: 'Em Curso',
          submitted: 'Submetido',
          accepted: 'Aceite',
          overdue: 'Em Atraso',
          noSiteSelected: 'Nenhum centro seleccionado',
          questionnaires: 'Questionários',
          completed: 'Concluído',
          totalAssigned: 'Total Atribuído',
          assignments: 'Atribuições',
          instrumentLibrary: 'Biblioteca de Instrumentos',
          templates: 'Modelos',
          research: 'Investigação',
          questionnaireBuilder: 'Construtor de Questionários',
          userManual: 'Manual do Utilizador',
          ecoaManagement: 'Gestão eCOA',
          ecoaDescription: 'Avaliações Clínicas Electrónicas para pacientes e observadores',
          inbox: 'Caixa de Entrada',
          requestsForInformation: 'Pedidos de Informação',
          rfisDescription: 'Gira e responda a pedidos de informação e questionários de viabilidade do patrocinador',
          logExternalRfi: 'Registar Pedido Externo',
          siteSettings: 'Definições do Centro',
          siteSettingsDescription: 'Actualize detalhes do centro e configure módulos DCT',
          siteDetails: 'Detalhes do Centro',
          editSiteDetails: 'Editar Detalhes do Centro',
          configureDctModules: 'Configurar Módulos DCT',
          saveChanges: 'Guardar Alterações',
          saving: 'A guardar...',
          siteName: 'Nome do Centro',
          city: 'Cidade',
          country: 'País',
          notSet: 'Não definido',
        },
        investigator: {
          totalPatients: 'Total de Pacientes',
          visitCompletion: 'Conclusão de Visitas',
          protocolDeviations: 'Desvios ao Protocolo',
          adverseEvents: 'Eventos Adversos',
          requiresReview: 'Requer revisão',
          enrollmentStatus: 'Estado da Inscrição',
          screening: 'Triagem',
          active: 'Activo',
          withdrawn: 'Retirado',
          aeSeverity: 'Distribuição da Gravidade dos EAs',
          mild: 'Ligeiro',
          moderate: 'Moderado',
          severe: 'Grave',
          lifeThreatening: 'Risco de vida',
          recentActivity: 'Actividade Recente do Centro',
          noRecentActivity: 'Sem actividade recente',
          patientEnrolled: 'Paciente Inscrito',
          patientAssigned: 'Paciente Atribuído',
        },
        analytics: {
          siteAnalyticsDashboard: 'Painel de Análises do Centro',
          performanceMetrics: 'Métricas de desempenho abrangentes para todos os ensaios',
          performanceMetricsFor: 'Métricas de desempenho para',
          refresh: 'Actualizar',
          exportReport: 'Exportar Relatório',
          screeningConversion: 'Conversão de Triagem',
          visitCompletion: 'Conclusão de Visitas',
          protocolQuality: 'Qualidade do Protocolo',
          documentCompliance: 'Conformidade Documental',
          excellent: 'Excelente',
          needsImprovement: 'Necessita melhoria',
          onTime: 'no prazo',
          openQueries: 'consultas abertas',
          good: 'Bom',
          reviewRequired: 'Revisão Necessária',
          monthlyRecruitment: 'Recrutamento Mensal',
          patientsEnrolledLast30: 'Pacientes inscritos nos últimos 30 dias',
          totalEnrolled: 'Total Inscrito',
          activeAndCompleted: 'Pacientes activos e concluídos',
          dropoutRate: 'Taxa de Abandono',
          withinAcceptableRange: 'Dentro do intervalo aceitável',
          aboveIndustryAverage: 'Acima da média do sector',
          enrollmentTrends: 'Tendências de Inscrição',
          enrollmentTrendsDesc: 'Inscrição de pacientes nos últimos 6 meses',
          noEnrollmentData: 'Sem dados de inscrição disponíveis',
          visitStatusDistribution: 'Distribuição do Estado das Visitas',
          visitStatusDesc: 'Estado actual de todas as visitas dos pacientes',
          noVisitData: 'Sem dados de visitas disponíveis',
          protocolDeviationsChart: 'Desvios ao Protocolo',
          deviationsBySeverity: 'Discriminação por gravidade',
          noProtocolDeviations: 'Sem Desvios ao Protocolo',
          excellentCompliance: 'Excelente conformidade com o protocolo',
          siteQualityScore: 'Pontuação de Qualidade do Centro',
          overallAssessment: 'Avaliação geral do desempenho',
          visitCompliance: 'Conformidade de Visitas',
          queryResolution: 'Resolução de Consultas',
          documentCompletion: 'Conclusão Documental',
          onTimeVisits: 'Visitas no Prazo',
          overallPerformance: 'Desempenho Global do Centro',
          exceptional: 'Excepcional',
          fair: 'Razoável',
          failedToLoad: 'Falha ao Carregar Análises',
          retry: 'Tentar Novamente',
          analyticsRefreshed: 'Análises Actualizadas',
          dataRefreshed: 'Dados actualizados com sucesso.',
          allTrialsRefreshed: 'Os dados de todos os ensaios foram actualizados.',
          enrolled: 'Inscritos',
          scheduled: 'Agendadas',
          missed: 'Perdidas',
          major: 'Major',
          minor: 'Minor',
          queryResolutionLabel: 'Resolução de consultas',
          screened: 'triados',
          visits: 'visitas',
        },
        roster: {
          visits: 'Visitas',
          addPatient: 'Adicionar Paciente',
          bulkUpload: 'Carregamento em Lote',
          sendInvite: 'Enviar Convite',
          matchAllPatients: 'Corresponder Todos os Pacientes',
          thisMonth: 'Este Mês',
          verified: 'Verificado',
          pendingLogin: 'Início de Sessão Pendente',
          matched: 'Correspondido',
          profileComplete: 'Perfil Completo',
          total: 'Total',
          searchPlaceholder: 'Pesquisar por nome ou email...',
          loadingMore: 'A carregar mais...',
          loadingPatients: 'A carregar pacientes...',
          errorLoadingPatients: 'Erro ao carregar pacientes',
          showing: 'A mostrar',
          of: 'de',
          patients: 'pacientes',
          patient: 'Paciente',
          type: 'Tipo',
          contact: 'Contacto',
          loginStatus: 'Estado de Sessão',
          healthProfile: 'Perfil de Saúde',
          consentStatus: 'Estado do Consentimento',
          trialMatching: 'Correspondência de Ensaios',
          workflow: 'Fluxo de Trabalho',
          actions: 'Acções',
          notSent: 'Não Enviado',
          pending: 'Pendente',
          complete: 'Completo',
          incomplete: 'Incompleto',
          notMatched: 'Sem Correspondência',
          runMatch: 'Executar Correspondência',
          match: 'Corresponder',
          open: 'Abrir',
          noContactInfo: 'Sem informação de contacto',
          noPatients: 'Ainda sem pacientes',
          noMatchingPatients: 'Nenhum paciente corresponde à pesquisa',
          tryAgain: 'Tentar Novamente',
          reloadPage: 'Recarregar Página',
        },
        staff: {
          siteStaff: 'Equipa do Centro',
          manageTeam: 'Gira os membros da equipa atribuídos a este centro de ensaios',
          addStaffMember: 'Adicionar Membro da Equipa',
          staffEmail: 'Email do Membro',
          emailPlaceholder: 'equipa@exemplo.com',
          emailHint: 'Se o utilizador não tiver conta, receberá um convite por email',
          firstName: 'Nome (Opcional)',
          lastName: 'Apelido (Opcional)',
          role: 'Função',
          selectRole: 'Seleccione uma função',
          adding: 'A adicionar...',
          addStaff: 'Adicionar Membro',
          cancel: 'Cancelar',
          loadingStaff: 'A carregar equipa...',
          errorLoadingStaff: 'Erro ao carregar membros da equipa',
          name: 'Nome',
          email: 'Email',
          compliance: 'Conformidade',
          status: 'Estado',
          active: 'Activo',
          inactive: 'Inactivo',
          noStaff: 'Ainda sem membros atribuídos',
          invitationSent: 'Convite enviado',
          staffAdded: 'Membro adicionado',
          failedToAdd: 'Falha ao adicionar membro',
          staffRemoved: 'Membro removido',
          staffRemovedDesc: 'O membro foi marcado como inactivo.',
          failedToRemove: 'Falha ao remover membro',
          missingInfo: 'Informação em falta',
          provideEmailAndRole: 'Por favor indique email e função',
          attestationRequired: 'Atestação necessária',
          attestationHint: 'Por favor confirme que o membro foi informado sobre as práticas de tratamento de dados',
        },
        addons: {
          clinical: 'Clínico',
          revenue: 'Receita',
          decentralized: 'Descentralizado',
          complianceLabel: 'Conformidade',
          patientManagementDesc: 'Mapeamento OMOP completo, demografia de diversidade, condições médicas detalhadas',
          omopSdtm: 'Padronização de dados OMOP/SDTM',
          advancedDemographics: 'Acompanhamento demográfico avançado',
          medicalHistory: 'Gestão do histórico médico',
          billingDesc: 'Gira ensaios activos, tabelas de honorários, eventos facturáveis e facturação',
          trialActivation: 'Activação e gestão de ensaios',
          feeSchedule: 'Configuração da tabela de honorários do protocolo',
          automatedInvoicing: 'Facturação automatizada',
          dctDesc: 'Visitas virtuais, monitorização remota, consentimento digital e integração de telessaúde',
          telehealth: 'Consultas de telessaúde por vídeo',
          remoteMonitoring: 'Monitorização remota de pacientes',
          digitalConsent: 'Plataforma de consentimento digital',
          inspectionDesc: 'Listas de verificação pré-inspecção, análise de lacunas documentais e formação simulada',
          ichGcpChecklist: 'Mais de 20 itens da lista ICH-GCP',
          etmfGapAnalysis: 'Análise automatizada de lacunas eTMF',
          mockInspection: 'Formação Q&A de inspecção simulada',
          offlineTooltip: 'Descarregue dados de pacientes, formulários e protocolos para trabalho de campo offline em zonas sem conectividade',
        },
        tabContent: {
          patients: {
            description: 'Todas as pessoas registadas no seu centro. Clique num nome para ver detalhes, visitas e correspondências de ensaios.',
          },
          staff: {
            description: 'Pessoas que trabalham no seu centro. Adicione novos colaboradores, altere funções e gira certificados de formação.',
          },
          regulatory: {
            description: 'Documentos e aprovações exigidos pelas autoridades. Mantenha actualizada a configuração do estudo, documentos TMF e gestão de dados.',
            studySetup: 'Configuração do Estudo',
            tmfDocuments: 'TMF e Documentos',
            dataManagement: 'Gestão de Dados',
            oversightStaff: 'Supervisão e Equipa',
          },
          safety: {
            description: 'Reporte e acompanhe se um paciente se sente mal devido ao medicamento do estudo. Registe efeitos secundários, reveja casos e submeta relatórios de segurança.',
            adverseEvents: 'Eventos Adversos',
            adverseEventsShort: 'EA/EAG',
            reviewReporting: 'Revisão e Reporte',
            reviewShort: 'Revisão',
            medications: 'Medicamentos',
            compliance: 'Conformidade',
          },
          ip: {
            title: 'Gestão de PI e Aleatorização',
            help: 'Gira o inventário de Produto Investigacional (PI), Tecnologia de Resposta Interactiva (IRT/RTSM) para aleatorização e gestão de fornecimentos, e o seguimento de kits do laboratório central.',
            learnMore: 'Este módulo cobre três áreas principais: IRT/RTSM para aleatorização de kits de medicamento, dispensa e desocultação de emergência; Inventário de PI para responsabilização do medicamento e conformidade de armazenamento; e Laboratório Central para seguimento de kits de amostras e gestão de resultados de biomarcadores. Todas as operações são registadas para conformidade ICH-GCP §5.14.',
            irtRtsm: 'IRT / RTSM',
            ipInventory: 'Inventário de PI',
            centralLab: 'Laboratório Central',
          },
          ecoa: {
            tipQuestionnaires: 'Total de questionários activos disponíveis para atribuição',
            tipPending: 'Atribuições à espera de conclusão pelos pacientes ou observadores',
            tipCompleted: 'Questionários submetidos com sucesso e com resultados pontuados',
            tipTotal: 'Total de atribuições de questionários para todos os pacientes',
            builderHelp: 'Crie questionários eCOA personalizados de raiz ou personalize instrumentos importados para as necessidades específicas do seu ensaio.',
            builderDesc: 'Crie questionários eCOA personalizados ou importe da biblioteca',
          },
          etmf: {
            siteEtmfTitle: 'Documentos eTMF do Centro',
            headerNote: 'Visualize e acompanhe os documentos essenciais do seu centro arquivados segundo o Modelo de Referência DIA TMF (v3.3). Os documentos carregados aqui ou através do painel AI Filing do CRO aparecem organizados por zona. Documentos específicos do centro incluem aprovações IRB, CVs da equipa, registos de delegação e registos de formação.',
            overallCompleteness: 'Completude Global',
            totalDocuments: 'Total de Documentos',
            zonesWithGaps: 'Zonas com Lacunas',
            acrossZones: 'Em {{n}} de 13 zonas',
            zonesBelowExpected: 'Zonas abaixo da contagem de documentos esperada',
            zoneCompletenessTitle: 'Completude por Zona — DIA TMF v3.3',
            zoneLabel: 'Zona',
            docsCount: '{{actual}}/{{expected}} docs',
            docsFiled: '{{actual}} de {{expected}} documentos esperados arquivados',
            clickToFilter: 'Clique para filtrar documentos por esta zona',
            siteDocuments: 'Documentos do Centro',
            searchPlaceholder: 'Pesquisar documentos...',
            loading: 'A carregar documentos...',
            noDocsFound: 'Nenhum documento encontrado',
            emptyHintNone: 'Documentos arquivados através do painel AI Filing do CRO ou carregados directamente aparecerão aqui.',
            emptyHintFiltered: 'Tente ajustar a sua pesquisa ou filtro de zona.',
            colDocument: 'Documento',
            colType: 'Tipo',
            colZone: 'Zona',
            colStatus: 'Estado',
            colDate: 'Data',
            unfiled: 'Não arquivado',
          },
          dashboard: {
            fieldWorker: 'Trabalhador de Campo',
            fieldWorkerTooltip: 'Interface móvel simplificada para recolha de dados no terreno — sinais vitais, eCOA, documentos, diagnóstico por IA',
          },
          settings: {
            title: 'Definições do Centro',
            selectCountry: 'Seleccionar país',
            statusActive: 'Activo',
            reasonPlaceholder: 'Descreva a razão desta alteração...',
            brandingUpdatedTitle: 'Marca actualizada',
            brandingUpdatedDesc: 'A marca do seu centro foi actualizada com sucesso',
            complianceInfra: 'Conformidade e Infra-estrutura',
            assessmentsLifecycle: 'Avaliações e Ciclo de Vida',
            totRequiresOrg: 'A Transferência de Obrigações requer uma ligação a uma organização.',
          },
        },
        components: {
          rosterStats: {
            activationRate: 'Taxa de Activação',
            profileCompletionRate: 'Taxa de Conclusão do Perfil',
            verifiedSummary: '{{verified}} de {{total}} pacientes verificados',
            profileSummary: '{{complete}} de {{total}} pacientes têm perfis completos',
          },
          rosterActions: {
            consentForm: 'Formulário de Consentimento',
            scanPatientForm: 'Digitalizar Formulário do Paciente',
          },
          portalStatus: {
            active: 'Portal Activo',
            invited: 'Convite Enviado',
            pending: 'Pendente',
            noAccess: 'Sem Portal',
          },
          trialSelector: {
            loading: 'A carregar ensaios...',
            filterLabel: 'Filtro de Ensaio:',
            placeholder: 'Seleccionar um ensaio',
            allTrials: 'Todos os Ensaios',
            aggregate: 'Agregado',
          },
          readinessBadge: {
            trialReady: 'Pronto para Ensaio',
            scoreTitle: 'Pontuação de Prontidão para Ensaios',
            scoreLevel: '{{level}} ({{score}}/100)',
            assessmentValue: 'Valor da Avaliação',
            assessmentValueDesc: 'Esta avaliação vale $5K-$30K em honorários de consultoria CRO',
            readinessLevel: 'Prontidão para Ensaio: {{level}}',
            scoredAs: 'Avaliado como: {{type}}',
            breakdownTitle: 'Detalhe da Pontuação:',
            infrastructure: 'Infra-estrutura:',
            regulatory: 'Regulatório:',
            staffing: 'Pessoal:',
            facility: 'Instalação:',
            experience: 'Experiência:',
            siteType: {
              hospital: 'Hospital / Centro Médico',
              dayClinic: 'Clínica de Dia / Ambulatório',
              researchCenter: 'Centro de Investigação',
              communityHealth: 'Centro de Saúde Comunitário',
            },
          },
          flowPipeline: {
            title: 'Pipeline de Fluxo do Ensaio',
            total: '{{n}} Total',
            screenRate: 'Taxa de Triagem',
            enrollRate: 'Taxa de Inscrição',
            randomizeRate: 'Taxa de Aleatorização',
            completionRate: 'Taxa de Conclusão',
            stages: {
              matched: 'Combinado',
              screening: 'Triagem',
              consent: 'Consentido',
              enrolled: 'Inscrito',
              randomized: 'Aleatorizado',
              active: 'Activo',
              completed: 'Concluído',
              billed: 'Facturado',
            },
          },
          featureGate: {
            checkingAccess: 'A verificar acesso...',
            upgradeRequired: 'Actualização Necessária',
            featureRequiresTier: 'Esta funcionalidade requer o nível',
            subscriptionTier: 'de subscrição.',
            perMonth: '/mês',
            currentlyOn: 'Está actualmente no nível',
            tierSuffix: '.',
            upgradeSubscription: 'Actualizar Subscrição',
          },
          offlineCache: {
            title: 'Estado da Cache Offline',
            online: 'Online',
            offline: 'Offline',
            description: 'Monitorize dados em cache e sincronizações pendentes',
            pendingSyncOne: '{{n}} item à espera de sincronização',
            pendingSyncOther: '{{n}} itens à espera de sincronização',
            syncNow: 'Sincronizar Agora',
            conflictsOne: '{{n}} conflito de sincronização detectado',
            conflictsOther: '{{n}} conflitos de sincronização detectados',
            cachedItems: 'Itens em Cache',
            offlinePatients: 'Pacientes Offline',
            queuedFiles: 'Ficheiros em Fila',
            queuedActions: 'Acções em Fila',
            lastCached: 'Última cache: {{time}}',
            expiredOne: '{{n}} item expirado na cache',
            expiredOther: '{{n}} itens expirados na cache',
            refresh: 'Actualizar',
            clearCache: 'Limpar Cache',
            clearTitle: 'Limpar Todos os Dados em Cache?',
            clearDesc: 'Isto removerá todos os dados em cache deste dispositivo. As sincronizações pendentes NÃO serão afectadas. Terá de preparar para offline novamente antes de ir para o terreno.',
            cancel: 'Cancelar',
            syncing: 'A sincronizar...',
            syncAll: 'Sincronizar Tudo ({{n}} itens)',
            allSynced: 'Todos os dados sincronizados',
            cannotSyncOffline: 'Não é possível sincronizar offline',
            syncCompleted: 'Sincronização concluída',
            syncFailed: 'Falha na sincronização',
            cacheCleared: 'Cache limpa',
            clearFailed: 'Falha ao limpar a cache',
          },
          prepareOffline: {
            ready: 'Pronto',
            stale: 'Desactualizado',
            offlineReady: 'Pronto para Offline',
            prepareOffline: 'Preparar Offline',
            title: 'Preparação Offline',
            description: 'Descarregue dados de pacientes, formulários e protocolos para trabalho de campo offline',
            patients: '{{n}} Pacientes',
            consentForms: '{{n}} Formulários de Consentimento',
            ecrfTemplates: '{{n}} Modelos eCRF',
            protocols: '{{n}} Protocolos',
            lastPrepared: 'Última preparação: {{time}}',
            preparing: 'A preparar...',
            refreshData: 'Actualizar Dados',
            prepareForOffline: 'Preparar para Offline',
            clearTitle: 'Limpar Dados Offline?',
            clearDesc: 'Isto removerá todos os dados em cache, incluindo pacientes, formulários e protocolos. Terá de preparar novamente antes de ir para offline.',
            cancel: 'Cancelar',
            clearData: 'Limpar Dados',
            offlineWarning: 'Está offline. Ligue-se para preparar dados.',
          },
          croSync: {
            loading: 'A carregar...',
            notManaged: 'Não gerido por CRO',
            neverSynced: 'Nunca sincronizado',
            managedBy: 'Gerido por {{name}}',
            lastSync: 'Última sincronização: {{time}}',
            activeTrials: 'Ensaios activos: {{n}}',
            triggerSync: 'Accionar sincronização manual',
            croManaged: 'Gerido por CRO',
          },
          inviteLink: {
            title: 'Link de Convite do Centro',
            description: 'Crie um link de registo reutilizável para este centro de ensaio que pode ser partilhado por e-mails em massa ou materiais de marketing.',
            customSlug: 'Slug de URL Personalizado',
            slugPlaceholder: 'p. ex., cape-town-research',
            slugHelp: 'Use apenas letras minúsculas, números e hífenes',
            autoGenerate: 'Gerar automaticamente a partir do nome do centro',
            updating: 'A actualizar...',
            updateLink: 'Actualizar Link de Convite',
            createLink: 'Criar Link de Convite',
            registrationLink: 'Link de Registo',
            copyToClipboard: 'Copiar para a área de transferência',
            previewPage: 'Pré-visualizar página de registo',
            sendInvitation: 'Enviar Convite',
            sendInvitationDesc: 'Envie um convite a partir de modelo directamente do sistema via E-mail, SMS ou WhatsApp.',
            email: 'E-mail',
            sms: 'SMS',
            whatsapp: 'WhatsApp',
            recipientNameLabel: 'Nome do Destinatário (opcional)',
            recipientNamePlaceholder: 'p. ex., João Silva',
            emailAddress: 'Endereço de E-mail',
            phoneNumber: 'Número de Telefone',
            sending: 'A enviar...',
            sendVia: 'Enviar via {{channel}}',
            qrCode: 'Código QR',
            downloadPng: 'Descarregar PNG',
            qrHelp: 'Imprima este código QR para cartazes, panfletos ou ecrãs da clínica',
            howToUse: 'Como utilizar:',
            howSendDirect: 'Envie convites directamente via E-mail, SMS ou WhatsApp acima',
            howCopyLink: 'Copie o link para e-mails em massa ou materiais de marketing',
            howPrintQr: 'Imprima o código QR para cartazes da clínica ou materiais de marketing',
            howAutoAssign: 'Os pacientes registar-se-ão directamente e serão atribuídos automaticamente a {{name}}',
            notConfigured: 'Ainda não configurado',
            downloaded: 'Descarregado!',
            qrSaved: 'Código QR guardado como PNG',
            noLink: 'Nenhum Link Disponível',
            configureSlugFirst: 'Configure primeiro o slug do convite.',
            copied: 'Copiado!',
            copiedDesc: 'Link de convite copiado para a área de transferência',
            invalidSlug: 'Slug Inválido',
            invalidSlugDesc: 'Introduza um slug válido.',
            slugExists: 'O Slug Já Existe',
            slugExistsDesc: 'Este slug já está em uso. Escolha outro diferente.',
            success: 'Sucesso',
            updateSuccess: 'Link de convite actualizado com sucesso',
            error: 'Erro',
            updateFailed: 'Falha ao actualizar o link de convite',
            noInviteLink: 'Nenhum Link de Convite',
            configureFirst: 'Configure primeiro o slug do convite.',
            validationError: 'Erro de Validação',
            invitationSent: 'Convite Enviado!',
            inviteSentDesc: 'Convite enviado via {{channel}} para {{contact}}',
            failedToSend: 'Falha ao Enviar',
            failedToSendDesc: 'Não foi possível enviar o convite. Tente novamente.',
          },
        },
      },
      cdst: {
        title: 'Apoio à Decisão Clínica com IA',
        patientIntake: 'Informação do Paciente',
        chiefComplaint: 'Queixa Principal',
        dangerSigns: 'Sinais de Perigo',
        triageResult: 'Resultado da Triagem',
        emergency: 'Emergência',
        urgent: 'Urgente',
        standard: 'Padrão',
        preventive: 'Preventivo',
        referTo: 'Encaminhar para',
        trialEligible: 'Elegível para Ensaio Clínico',
        suspectedCondition: 'Condição Suspeita',
        recommendedAction: 'Acção Recomendada',
        korogwe: 'Distrito de Korogwe',
        handeni: 'Distrito de Handeni',
        offlineAssessment: 'Avaliação Offline',
        fullAiAssessment: 'Avaliação Completa com IA',
        connectivityRequired: 'Conectividade necessária para diagnóstico completo',
        fever: 'Febre',
        cough: 'Tosse',
        diarrhea: 'Diarreia',
        headache: 'Dor de Cabeça',
        bodyPain: 'Dor no Corpo',
        vomiting: 'Vómitos',
        breathingDifficulty: 'Dificuldade Respiratória',
        rash: 'Erupção Cutânea',
        convulsions: 'Convulsões',
        lethargy: 'Letargia',
      },
      consent: {
        informedConsent: 'Consentimento Informado',
        dataPrivacy: 'Privacidade de Dados',
        personalDataRights: 'Direitos de Dados Pessoais',
        dataProcessing: 'Processamento de Dados',
        consentRequired: 'Consentimento Necessário',
        crossBorderTransfer: 'Transferência Transfronteiriça',
        title: 'Formulário de Consentimento',
        readCarefully: 'Leia com Atenção',
        signature: 'Assinatura',
        agreeToTerms: 'Concordo com os Termos',
        signHere: 'Assine Aqui',
        dateOfConsent: 'Data do Consentimento',
        witnessSignature: 'Assinatura da Testemunha',
        consentGiven: 'Consentimento Dado',
        consentPending: 'Consentimento Pendente',
        consentWithdrawn: 'Consentimento Retirado',
      },
      onboarding: {
        welcomeTitle: 'Bem-vindo aos Ensaios Clínicos',
        getStarted: 'Começar',
        step1Title: 'Informação Pessoal',
        step2Title: 'Histórico Médico',
        step3Title: 'Suas Preferências',
        completeProfile: 'Completar Perfil',
        skipForNow: 'Saltar por Agora',
        almostDone: 'Quase Pronto',
        profileComplete: 'Perfil Completo',
        continueToTrials: 'Continuar para Ensaios',
        // Site staff & patient onboarding (mirrors EN)
        welcomePatient: 'Bem-vindo ao PTC BRIDGE™',
        welcomePatientDesc: 'A sua plataforma para encontrar e participar em ensaios clínicos',
        welcomePatientContent: 'Vamos guiá-lo pelas funcionalidades principais para começar o seu percurso em ensaios clínicos.',
        completeProfileDesc: 'Construa o seu perfil de saúde para uma melhor correspondência de ensaios',
        completeProfileContent: 'Adicione condições médicas, medicação e dados demográficos. Um perfil completo ajuda a IA a encontrar os ensaios mais relevantes.',
        findTrials: 'Encontrar Ensaios Compatíveis',
        findTrialsDesc: 'Descubra ensaios clínicos com correspondência por IA',
        applyToTrials: 'Candidatar-se a Ensaios',
        applyToTrialsDesc: 'Manifeste interesse e acompanhe as suas candidaturas',
        stayConnected: 'Mantenha-se Ligado',
        stayConnectedDesc: 'Receba actualizações e gira comunicações',
        welcomeSiteStaff: 'Bem-vindo à Gestão do Centro de Ensaios',
        welcomeSiteStaffDesc: 'A sua plataforma para gerir as operações de ensaios clínicos',
        welcomeSiteStaffContent: 'Vamos guiá-lo pelas ferramentas essenciais para gerir pacientes, visitas, documentos e facturação.',
        dashboardOverview: 'Visão Geral do Painel',
        dashboardOverviewDesc: 'Monitorize métricas chave e acções rápidas',
        patientManagement: 'Gestão de Pacientes',
        patientManagementDesc: 'Gira a sua lista de pacientes e inscrições',
        visitScheduling: 'Agendamento de Visitas',
        visitSchedulingDesc: 'Agende e acompanhe visitas de pacientes',
        docsRegulatory: 'Documentos e Regulamentar',
        docsRegulatoryDesc: 'Gira formulários de consentimento e resultados laboratoriais',
        activeBilling: 'Ensaios Activos e Facturação',
        activeBillingDesc: 'Tabelas de honorários do protocolo e facturação',
        skipTutorial: 'Saltar tutorial',
        replayOnboarding: 'Repetir Tutorial de Integração',
        onboardingReset: 'Tutorial reposto',
        onboardingResetDesc: 'Actualize a página para voltar a ver o tutorial de integração.',
      },
      matching: {
        foundTrials: 'Ensaios Encontrados',
        matchScore: 'Pontuação de Correspondência',
        eligibilityMatch: 'Correspondência de Elegibilidade',
        viewDetails: 'Ver Detalhes',
        applyNow: 'Candidatar-se Agora',
        noMatches: 'Nenhuma Correspondência Encontrada',
        refineSearch: 'Refinar Pesquisa',
        highMatch: 'Correspondência Alta',
        mediumMatch: 'Correspondência Média',
        lowMatch: 'Correspondência Baixa',
      },
      siteStaff: {
        patients: 'Pacientes',
        visits: 'Visitas',
        enrollment: 'Inscrição',
        activeTrials: 'Ensaios Activos',
        scheduleVisit: 'Agendar Visita',
        viewPatient: 'Ver Paciente',
        patientRecords: 'Registos de Pacientes',
        addPatient: 'Adicionar Paciente',
        patientList: 'Lista de Pacientes',
        upcomingVisits: 'Visitas Futuras',
        completedVisits: 'Visitas Concluídas',
        missedVisits: 'Visitas Perdidas',
      },
      dashboard: {
        overview: 'Visão Geral',
        myTrials: 'Meus Ensaios',
        upcomingVisits: 'Visitas Futuras',
        messages: 'Mensagens',
        documents: 'Documentos',
        settings: 'Configurações',
        notifications: 'Notificações',
        quickActions: 'Ações Rápidas',
        recentActivity: 'Atividade Recente',
        helpCenter: 'Centro de Ajuda',
      },
      errors: {
        genericError: 'Ocorreu um erro',
        networkError: 'Erro de rede. Verifique a sua ligação.',
        authError: 'Erro de autenticação. Inicie sessão novamente.',
        validationError: 'Erro de validação. Verifique os seus dados.',
        notFound: 'Não encontrado',
        forbidden: 'Acesso negado',
        serverError: 'Erro do servidor. Tente novamente mais tarde.',
        sessionExpired: 'Sessão expirada. Inicie sessão novamente.',
        invalidEmail: 'Formato de email inválido',
        invalidPhone: 'Formato de telefone inválido',
        requiredField: 'Este campo é obrigatório',
      },
      notifications: {
        newMessage: 'Nova Mensagem',
        visitReminder: 'Lembrete de Visita',
        matchFound: 'Correspondência Encontrada',
        enrollmentApproved: 'Inscrição Aprovada',
        documentReady: 'Documento Pronto',
        consentRequired: 'Consentimento Necessário',
        profileIncomplete: 'Perfil Incompleto',
        trialUpdate: 'Atualização do Ensaio',
        medicationReminder: 'Lembrete de Medicação',
        appointmentConfirmed: 'Consulta Confirmada',
        appointmentCancelled: 'Consulta Cancelada',
        markAsRead: 'Marcar como Lido',
      },
      medical: {
        diagnosis: 'Diagnóstico',
        treatment: 'Tratamento',
        medication: 'Medicação',
        dosage: 'Dosagem',
        frequency: 'Frequência',
        sideEffects: 'Efeitos Secundários',
        allergies: 'Alergias',
        medicalHistory: 'Histórico Médico',
        currentConditions: 'Condições Atuais',
        familyHistory: 'Histórico Familiar',
      },
      admin: {
        userManagement: 'Gestão de Utilizadores',
        siteManagement: 'Gestão de Sites',
        trialManagement: 'Gestão de Ensaios',
        reports: 'Relatórios',
        analytics: 'Análises',
        systemHealth: 'Estado do Sistema',
        auditLog: 'Registo de Auditoria',
        permissions: 'Permissões',
        configuration: 'Configuração',
        maintenance: 'Manutenção',
      },
      regulatory: {
        ethicsCommittee: 'Comité de Ética',
        regulatorySubmission: 'Submissão Regulatória',
        complianceStatus: 'Estado de Conformidade',
        documentSubmission: 'Submissão de Documentos',
        approvalPending: 'Aprovação Pendente',
        approved: 'Aprovado',
        rejected: 'Rejeitado',
        underReview: 'Em Revisão',
        expirationDate: 'Data de Expiração',
        renewalRequired: 'Renovação Necessária',
      },
      checklist: {
        gettingStarted: 'Começar',
        siteSetup: 'Começar — Configuração do Centro',
        phcFirstSteps: 'Começar — Os Seus Primeiros Passos',
        complete: 'completo',
        allSet: 'Tudo pronto! Está pronto para começar.',
        dismiss: 'Dispensar',
        addFirstPatient: 'Adicione o seu primeiro paciente',
        addFirstPatientDesc: 'Registe ou convide um paciente para a lista do centro de ensaios.',
        inviteStaff: 'Convide um membro da equipa',
        inviteStaffDesc: 'Adicione coordenadores, investigadores ou enfermeiros à sua equipa.',
        scheduleVisit: 'Agende uma visita do paciente',
        scheduleVisitDesc: 'Crie uma triagem, seguimento ou visita de conclusão do estudo.',
        setupAutoRules: 'Configure regras de atribuição automática de formulários',
        setupAutoRulesDesc: 'Configure que formulários eCRF/eCOA são atribuídos automaticamente quando os pacientes mudam de fase.',
        registerFirstPatient: 'Registe o seu primeiro paciente',
        registerFirstPatientDesc: 'Adicione um novo paciente — registe nome, data de nascimento e consentimento.',
        completeConsultation: 'Complete uma consulta',
        completeConsultationDesc: 'Registe sintomas, exame, diagnóstico e plano de tratamento do paciente.',
        syncData: 'Sincronize os seus dados',
        syncDataDesc: 'Carregue dados recolhidos offline para o servidor central.',
        completeTraining: 'Complete um módulo de formação BlueCloudX',
        completeTrainingDesc: 'Aprenda competências de ensaios clínicos com cursos online gratuitos.',
      },
      emptyStates: {
        noPatients: 'Ainda sem pacientes inscritos',
        noPatientsDesc: 'Adicione o seu primeiro paciente para começar a gerir a sua participação no ensaio.',
        noVisits: 'Sem visitas agendadas',
        noVisitsDesc: 'Agende uma triagem, seguimento ou visita de conclusão do estudo para um paciente.',
        noForms: 'Sem formulários atribuídos',
        noFormsDesc: 'Atribua formulários eCRF ou eCOA aos pacientes manualmente ou configure regras automáticas.',
        noTrials: 'Sem ensaios activos',
        noTrialsDesc: 'Adicione um ensaio activo dos contratos do patrocinador para começar a inscrição.',
        noData: 'Ainda sem dados',
        noDataDesc: 'Os dados aparecerão aqui à medida que utiliza a plataforma.',
      },
      tours: {
        takeTour: 'Fazer a Visita Guiada',
        replayTour: 'Repetir Visita Guiada',
        siteTour: 'Visita ao Centro',
        phcTour: 'Visita PHC',
        croTour: 'Visita CRO',
      },
      chw: {
        aiDiagnosis: 'Use isto para ajudar a diagnosticar a condição que o paciente pode ter. Responda a perguntas sobre sintomas e a IA sugerirá diagnósticos possíveis usando as directrizes IMCI da OMS. Confirme sempre com o seu supervisor para casos graves.',
        medicalInfo: 'Registe o histórico de saúde do paciente aqui — condições existentes (como diabetes ou VIH), medicamentos actuais e quaisquer alergias.',
        recordVitals: 'Meça e registe a pressão arterial, temperatura, peso, altura e frequência cardíaca do paciente. Faça isto em cada visita.',
        labResults: 'Insira aqui os resultados dos exames laboratoriais, ou utilize a câmara para digitalizar um relatório impresso.',
        screening: 'Aplique questionários clínicos de triagem validados. Podem ser dados em línguas locais. Siga as instruções com atenção.',
        prescriptions: 'Dispense medicação a partir do formulário da clínica. Seleccione o medicamento, dose e duração. Verifique sempre as alergias antes de dispensar.',
        growth: 'Acompanhe o crescimento da criança usando os padrões da OMS. Insira peso, altura e perímetro cefálico.',
        immunizations: 'Veja e actualize o calendário de vacinação da criança. Registe vacinas administradas e agende a próxima visita.',
        postnatalCare: 'Siga o calendário de cuidados pós-natais da OMS para mães e recém-nascidos.',
        ncd: 'Faça a gestão de pacientes com doenças crónicas usando as directrizes WHO PEN.',
        consultation: 'Inicie uma consulta clínica completa para este paciente.',
        syncData: 'Carregue dados recolhidos offline para o servidor central. Sincronize quando tiver ligação à internet.',
      },
      phc: {
        nav: {
          home: 'Início',
          register: 'Registar',
          sync: 'Sincronizar',
          help: 'Ajuda',
          referrals: 'Referências',
          aefi: 'Reportar AEFI',
          stats: 'Estatísticas',
          supervisor: 'Supervisor',
          surveillance: 'Vigilância',
          dhis2: 'DHIS2',
          settings: 'Definições',
          logout: 'Terminar sessão',
          helpTooltip: 'Encontre artigos de ajuda, guias de formação e instruções passo a passo. Funciona offline também!',
          surveillanceTooltip: 'Abrir o painel de vigilância governamental para monitorização de doenças em tempo real',
          dhis2Tooltip: 'Ver o painel de inteligência de saúde DHIS2',
        },
        toasts: {
          notAuthenticated: 'Não autenticado',
          syncFailed: 'Sincronização falhou',
          syncComplete: 'Sincronização concluída',
          syncCompleteDesc: 'Todos os dados estão actualizados.',
          syncError: 'Erro de sincronização',
          unknownError: 'Erro desconhecido',
          signedOut: 'Sessão terminada com sucesso',
          signOutError: 'Erro ao terminar sessão',
          surveillanceUnavailable: 'Portal de vigilância não disponível',
          surveillanceUnavailableDesc: 'Nenhum portal de vigilância governamental está ligado à sua unidade.',
        },
        knowledgeBase: {
          title: 'Ajuda e Base de Conhecimento - PHC',
          description: 'Encontre artigos de ajuda, guias de formação e instruções para agentes de saúde comunitária.',
          headerTitle: 'Ajuda e Formação',
        },
      },
      patientSelfTriage: {
        seo: {
          title: 'Verificar os meus sintomas | Auto-Triagem do Paciente',
          description: 'Verificação gratuita e privada de sintomas. Funciona offline. Conecta-o à clínica mais próxima.',
          resultTitle: 'O resultado da sua verificação | Passaporte de Saúde',
        },
        back: 'Voltar',
        disclaimer: 'Esta é uma auto-verificação, <strong>não um diagnóstico</strong>. Se se sentir muito doente, vá à clínica agora ou ligue ao seu agente comunitário de saúde.',
        voice: {
          cantRead: 'Não sabe ler? Fale em vez disso.',
          guide: 'Fale em inglês ou suaíli. O assistente irá guiá-lo.',
          start: 'Começar',
        },
        header: {
          title: 'Verificar os meus sintomas',
          subtitle: 'Marque o que sente de errado. Diremos o que fazer. Isto não é um médico.',
        },
        field: {
          age: 'A sua idade (anos)',
          sex: 'Sexo à nascença',
          sexUnknown: 'Prefiro não dizer',
          sexFemale: 'Feminino',
          sexMale: 'Masculino',
          pregnant: 'Estou grávida',
        },
        gps: {
          label: 'Encontrar a minha clínica',
          use: 'Usar a minha localização',
          update: 'Atualizar a minha localização',
          findingClinic: 'A procurar a clínica mais próxima…',
          notSupported: 'Localização não suportada neste dispositivo.',
          failed: 'Não foi possível obter a localização. Pode continuar.',
          listOffline: 'Lista de clínicas indisponível offline. Iremos guardar a sua verificação.',
        },
        callback: {
          label: 'Telefone para retorno (opcional)',
          placeholder: '+254 7...',
          help: 'Um agente de saúde poderá ligar ou enviar WhatsApp/SMS.',
        },
        consent: {
          prompt: 'Falem-me sobre estudos?',
          promptBody: 'Se um estudo de investigação na minha clínica me puder ajudar, quero saber. Posso recusar mais tarde. (Apenas consentimento geral.)',
          signedBy: 'Assinado por',
          change: 'alterar',
          open: 'Abrir consentimento (ouvir + assinar)',
        },
        submit: {
          button: 'Ver o meu resultado',
          signInTip: 'Dica: inicie sessão para guardar as suas verificações no seu Passaporte de Saúde.',
          signInToSend: 'A sua verificação está pronta. Inicie sessão para a enviar à sua clínica.',
          pickClinic: 'Por favor, escolha a sua clínica para que possamos enviar a sua verificação.',
          savedOffline: 'Guardado offline. Enviaremos quando estiver online.',
          sent: 'Enviado à sua clínica mais próxima.',
          savedRetry: 'Guardado neste telefone. Reenviaremos mais tarde.',
        },
        result: {
          notDiagnosis: 'Isto é uma verificação, não um diagnóstico médico.',
          dangerTitle: 'Sinais de perigo encontrados',
          dangerBody: 'Por favor, vá à clínica mais próxima imediatamente. Se tiver telefone, ligue ao seu agente comunitário de saúde.',
          callbackNote: 'Um agente de saúde irá ligar para <strong>{{phone}}</strong> se necessário.',
          trialConsentNote: 'Aceitou aprender sobre estudos. A clínica verá se algum estudo o pode ajudar.',
          checkAgain: 'Verificar novamente',
          downloadPdf: 'Descarregar PDF',
          backToPassport: 'Voltar ao meu passaporte',
          pdfSuccess: 'PDF descarregado',
          pdfError: 'Não foi possível gerar o PDF',
        },
      },
    }
  },
  ar: {
    translation: {
      common: {
        welcome: 'مرحباً',
        loading: 'جارٍ التحميل...',
        save: 'حفظ',
        cancel: 'إلغاء',
        submit: 'إرسال',
        search: 'بحث',
        filter: 'تصفية',
        success: 'نجاح',
        error: 'خطأ',
        back: 'رجوع',
        next: 'التالي',
        previous: 'السابق',
        close: 'إغلاق',
        open: 'فتح',
        view: 'عرض',
        edit: 'تعديل',
        delete: 'حذف',
        confirm: 'تأكيد',
        yes: 'نعم',
        no: 'لا',
        actions: 'إجراءات',
        status: 'الحالة',
        date: 'التاريخ',
        time: 'الوقت',
        download: 'تحميل',
        upload: 'رفع',
        refresh: 'تحديث',
        retry: 'إعادة المحاولة',
        details: 'التفاصيل',
        summary: 'ملخص',
      },
      navigation: {
        home: 'الرئيسية',
        trials: 'التجارب السريرية',
        about: 'من نحن',
        regions: 'المناطق',
        conditions: 'الحالات الطبية',
        contact: 'اتصل بنا',
        login: 'تسجيل الدخول',
        register: 'التسجيل',
        dashboard: 'لوحة المعلومات',
        profile: 'الملف الشخصي',
        settings: 'الإعدادات',
        logout: 'تسجيل الخروج',
      },
      trials: {
        clinicalTrial: 'تجربة سريرية',
        enrollment: 'التسجيل',
        recruiting: 'جارٍ التوظيف',
        phase1: 'المرحلة الأولى',
        phase2: 'المرحلة الثانية',
        phase3: 'المرحلة الثالثة',
        phase4: 'المرحلة الرابعة',
        eligibility: 'معايير الأهلية',
        sponsor: 'الراعي',
        location: 'الموقع',
        studyType: 'نوع الدراسة',
        status: 'الحالة',
        findTrials: 'البحث عن تجارب',
        matchMe: 'طابقني',
        learnMore: 'اعرف المزيد',
      },
      forms: {
        name: 'الاسم',
        email: 'البريد الإلكتروني',
        phone: 'الهاتف',
        dateOfBirth: 'تاريخ الميلاد',
        gender: 'الجنس',
        medicalCondition: 'الحالة الطبية',
        currentMedications: 'الأدوية الحالية',
        allergies: 'الحساسية',
        required: 'مطلوب',
        optional: 'اختياري',
        pleaseSelect: 'يرجى الاختيار',
        enterValue: 'أدخل القيمة',
      },
      onboarding: {
        welcomeTitle: 'مرحباً بك في التجارب السريرية',
        getStarted: 'ابدأ الآن',
        step1Title: 'المعلومات الشخصية',
        step2Title: 'التاريخ الطبي',
        step3Title: 'تفضيلاتك',
        completeProfile: 'أكمل ملفك الشخصي',
        skipForNow: 'تخطي الآن',
        almostDone: 'أوشكت على الانتهاء',
        profileComplete: 'اكتمل الملف الشخصي',
        continueToTrials: 'متابعة إلى التجارب',
      },
      matching: {
        foundTrials: 'تجارب مطابقة',
        matchScore: 'نسبة التطابق',
        eligibilityMatch: 'تطابق الأهلية',
        viewDetails: 'عرض التفاصيل',
        applyNow: 'التقديم الآن',
        noMatches: 'لا توجد تطابقات',
        refineSearch: 'تحسين البحث',
        highMatch: 'تطابق عالي',
        mediumMatch: 'تطابق متوسط',
        lowMatch: 'تطابق منخفض',
      },
      siteStaff: {
        patients: 'المرضى',
        visits: 'الزيارات',
        enrollment: 'التسجيل',
        activeTrials: 'التجارب النشطة',
        scheduleVisit: 'جدولة زيارة',
        viewPatient: 'عرض المريض',
        patientRecords: 'سجلات المرضى',
        addPatient: 'إضافة مريض',
        patientList: 'قائمة المرضى',
        upcomingVisits: 'الزيارات القادمة',
        completedVisits: 'الزيارات المكتملة',
        missedVisits: 'الزيارات الفائتة',
      },
      consent: {
        title: 'نموذج الموافقة',
        readCarefully: 'اقرأ بعناية',
        signature: 'التوقيع',
        agreeToTerms: 'أوافق على الشروط',
        signHere: 'وقّع هنا',
        dateOfConsent: 'تاريخ الموافقة',
        witnessSignature: 'توقيع الشاهد',
        consentGiven: 'تم منح الموافقة',
        consentPending: 'الموافقة معلقة',
        consentWithdrawn: 'تم سحب الموافقة',
      },
      dashboard: {
        overview: 'نظرة عامة',
        myTrials: 'تجاربي',
        upcomingVisits: 'الزيارات القادمة',
        messages: 'الرسائل',
        documents: 'المستندات',
        settings: 'الإعدادات',
        notifications: 'الإشعارات',
        quickActions: 'إجراءات سريعة',
        recentActivity: 'النشاط الأخير',
        helpCenter: 'مركز المساعدة',
      },
      errors: {
        genericError: 'حدث خطأ',
        networkError: 'خطأ في الشبكة. تحقق من اتصال الإنترنت.',
        authError: 'خطأ في المصادقة. يرجى تسجيل الدخول مجدداً.',
        validationError: 'خطأ في التحقق. تحقق من المدخلات.',
        notFound: 'غير موجود',
        forbidden: 'الوصول مرفوض',
        serverError: 'خطأ في الخادم. حاول مرة أخرى لاحقاً.',
        sessionExpired: 'انتهت الجلسة. يرجى تسجيل الدخول مجدداً.',
        invalidEmail: 'صيغة البريد الإلكتروني غير صالحة',
        invalidPhone: 'صيغة رقم الهاتف غير صالحة',
        requiredField: 'هذا الحقل مطلوب',
        minLength: 'مطلوب {min} حرف على الأقل',
        maxLength: 'الحد الأقصى {max} حرف',
        passwordMismatch: 'كلمتا المرور غير متطابقتين',
        invalidDate: 'صيغة التاريخ غير صالحة',
      },
      notifications: {
        newMessage: 'رسالة جديدة',
        visitReminder: 'تذكير بالزيارة',
        matchFound: 'تم العثور على تطابق',
        enrollmentApproved: 'تمت الموافقة على التسجيل',
        documentReady: 'المستند جاهز',
        consentRequired: 'الموافقة مطلوبة',
        profileIncomplete: 'الملف الشخصي غير مكتمل',
        trialUpdate: 'تحديث التجربة',
        medicationReminder: 'تذكير بالدواء',
        appointmentConfirmed: 'تم تأكيد الموعد',
        appointmentCancelled: 'تم إلغاء الموعد',
        markAsRead: 'تحديد كمقروء',
      },
      medical: {
        diagnosis: 'التشخيص',
        treatment: 'العلاج',
        medication: 'الدواء',
        dosage: 'الجرعة',
        frequency: 'التكرار',
        sideEffects: 'الآثار الجانبية',
        allergies: 'الحساسية',
        medicalHistory: 'التاريخ الطبي',
        currentConditions: 'الحالات الحالية',
        familyHistory: 'التاريخ العائلي',
      },
      admin: {
        userManagement: 'إدارة المستخدمين',
        siteManagement: 'إدارة المواقع',
        trialManagement: 'إدارة التجارب',
        reports: 'التقارير',
        analytics: 'التحليلات',
        systemHealth: 'صحة النظام',
        auditLog: 'سجل التدقيق',
        permissions: 'الصلاحيات',
        configuration: 'الإعدادات',
        maintenance: 'الصيانة',
      },
      regulatory: {
        sfdaApproval: 'موافقة الهيئة العامة للغذاء والدواء',
        ethicsCommittee: 'لجنة الأخلاقيات',
        ncbeApproval: 'موافقة اللجنة الوطنية للأخلاقيات الحيوية',
        regulatorySubmission: 'التقديم التنظيمي',
        complianceStatus: 'حالة الامتثال',
        documentSubmission: 'تقديم المستندات',
        approvalPending: 'الموافقة معلقة',
        approved: 'موافق عليه',
        rejected: 'مرفوض',
        underReview: 'قيد المراجعة',
        expirationDate: 'تاريخ الانتهاء',
        renewalRequired: 'التجديد مطلوب',
        ncaCompliance: 'الامتثال للهيئة الوطنية للأمن السيبراني',
        ecc2: 'ضوابط الأمن السيبراني الأساسية',
      },
      dataProtection: {
        pdpl: 'نظام حماية البيانات الشخصية',
        sdaia: 'الهيئة السعودية للبيانات والذكاء الاصطناعي',
        crossBorderTransfer: 'النقل عبر الحدود',
        dataResidency: 'إقامة البيانات',
        consentRequired: 'الموافقة مطلوبة',
        personalDataRights: 'حقوق البيانات الشخصية',
        dataProcessing: 'معالجة البيانات',
        inKingdomHosting: 'الاستضافة داخل المملكة',
      },
    }
  },
  pcm: {
    translation: {
      common: {
        welcome: 'Welcome o',
        loading: 'Dey load...',
        save: 'Save am',
        cancel: 'Cancel am',
        submit: 'Send am in',
        search: 'Search',
        filter: 'Filter',
        success: 'E don work!',
        error: 'Something do am',
        back: 'Go back',
        next: 'Next one',
        previous: 'Go before',
        close: 'Close am',
        open: 'Open am',
        view: 'See am',
        edit: 'Change am',
        delete: 'Comot am',
        confirm: 'Confirm am',
        yes: 'Yes',
        no: 'No',
      },
      navigation: {
        home: 'Home',
        trials: 'Clinical Trials',
        about: 'About Us',
        regions: 'Areas',
        conditions: 'Sickness dem',
        contact: 'Call us',
        login: 'Login',
        register: 'Register',
        dashboard: 'My Page',
        profile: 'My Profile',
      },
      trials: {
        clinicalTrial: 'Clinical Trial',
        enrollment: 'How dem dey join',
        recruiting: 'Dey find people',
        phase1: 'Phase 1',
        phase2: 'Phase 2',
        phase3: 'Phase 3',
        phase4: 'Phase 4',
        eligibility: 'Wetin you need to get inside',
        sponsor: 'Who dey pay',
        location: 'Where e dey',
        studyType: 'Wetin kind study',
        status: 'How e dey go',
        findTrials: 'Find trials',
        matchMe: 'Match me to trial',
        learnMore: 'Learn more',
      },
      forms: {
        name: 'Name',
        email: 'Email',
        phone: 'Phone',
        dateOfBirth: 'When dem born you',
        gender: 'Gender',
        medicalCondition: 'Sickness wey you get',
        currentMedications: 'Medicine you dey take now',
        allergies: 'Wetin dey give you reaction',
        required: 'You must put dis one',
      },
      trialSite: {
        tabs: {
          dashboard: 'Dashboard',
          ecrf: 'eCRF',
          ecoa: 'eCOA',
          analytics: 'Analytics',
          staff: 'Staff',
          patients: 'Patients',
          recruitment: 'CRM',
          econsent: 'eConsent',
          safety: 'Safety/PV',
          ip: 'IRT / IP Mgmt',
          queries: 'Questions',
          regulatory: 'Regulatory',
          rfis: 'RFIs',
          inspections: 'Inspections',
          settings: 'Settings',
        },
        tooltips: {
          dashboard: 'Your main page. See how many patient you get, visit wey dey happen, and important alerts.',
          ecrf: 'Electronic form dem where you record trial data for each patient visit.',
          ecoa: 'Health questionnaire wey patient dem go fill by demself about how dem dey feel.',
          analytics: 'Chart and numbers wey show how your site dey perform.',
          staff: 'Add team members, set dem password, and check dem training.',
          patients: 'All your patients. Add new ones, check dem status, and manage records.',
          recruitment: 'Find and register patients for trials. Track where each patient dey for di process.',
          econsent: 'Get patient consent electronically. Dem go sign for screen or via WhatsApp.',
          safety: 'Report health wahala wey happen during di trial. Very important — do am quick-quick.',
          ip: 'Manage study medicine. Track wetin dem receive, give out, and return.',
          queries: 'Questions from di sponsor about your data. Open each one and fix di matter.',
          regulatory: 'Upload and manage documents wey dem need like ethics approval and staff CV.',
          rfis: 'Information wey di sponsor dey ask for. Answer dem here.',
          inspections: 'Prepare for audit and inspection. Check if all documents dey ready.',
          settings: 'Change your site name, logo, and other settings.',
        },
        tabContent: {
          patients: {
            description: 'All people wey register for your site. Click any name make you see dia details, visits, and trial matches.',
          },
          staff: {
            description: 'People wey dey work for your site. Add new staff, change roles, and manage training certificates.',
          },
          regulatory: {
            description: 'Documents and approvals wey government dey require. Keep study setup, TMF documents, and data management up to date.',
            studySetup: 'Study Setup',
            tmfDocuments: 'TMF & Documents',
            dataManagement: 'Data Management',
            oversightStaff: 'Oversight & Staff',
          },
          safety: {
            description: 'Report and track if patient dey feel sick from di study medicine. Record side effects, review cases, and send safety reports.',
            adverseEvents: 'Adverse Events',
            adverseEventsShort: 'AE/SAE',
            reviewReporting: 'Review & Reporting',
            reviewShort: 'Review',
            medications: 'Medications',
            compliance: 'Compliance',
          },
          ip: {
            title: 'IP Management & Randomization',
            help: 'Manage Investigational Product (IP) inventory, IRT/RTSM for randomization and supply, and central lab kit tracking.',
            learnMore: 'Dis module cover three main area: IRT/RTSM for drug kit randomization, dispensing and emergency unblinding; IP Inventory for drug accountability and storage compliance; and Central Lab for specimen kit tracking and biomarker results. All operations get logged for ICH-GCP §5.14 compliance.',
            irtRtsm: 'IRT / RTSM',
            ipInventory: 'IP Inventory',
            centralLab: 'Central Lab',
          },
          ecoa: {
            tipQuestionnaires: 'Total active questionnaires wey dey ready for assignment',
            tipPending: 'Assignments wey dey wait for patients or observers to finish',
            tipCompleted: 'Questionnaires wey don submit successfully with scored results',
            tipTotal: 'Total questionnaire assignments across all patients',
            builderHelp: "Create custom eCOA questionnaires from scratch or customize imported instruments for your trial's specific needs.",
            builderDesc: 'Create custom eCOA questionnaires or import from di library',
          },
          etmf: {
            siteEtmfTitle: 'Site eTMF Documents',
            headerNote: "View and track your site's essential documents wey dem file against di DIA TMF Reference Model (v3.3). Documents wey dem upload here or through di CRO AI Filing panel go show organize by zone. Site-specific docs include IRB approvals, staff CVs, delegation logs, and training records.",
            overallCompleteness: 'Overall Completeness',
            totalDocuments: 'Total Documents',
            zonesWithGaps: 'Zones with Gaps',
            acrossZones: 'Across {{n}} of 13 zones',
            zonesBelowExpected: 'Zones wey dey below expected document count',
            zoneCompletenessTitle: 'Zone Completeness — DIA TMF v3.3',
            zoneLabel: 'Zone',
            docsCount: '{{actual}}/{{expected}} docs',
            docsFiled: '{{actual}} of {{expected}} expected documents don file',
            clickToFilter: 'Click to filter documents by dis zone',
            siteDocuments: 'Site Documents',
            searchPlaceholder: 'Search documents...',
            loading: 'Documents dey load...',
            noDocsFound: 'No documents wey we find',
            emptyHintNone: 'Documents wey dem file through di CRO AI Filing panel or upload directly go show here.',
            emptyHintFiltered: 'Try adjust your search or zone filter.',
            colDocument: 'Document',
            colType: 'Type',
            colZone: 'Zone',
            colStatus: 'Status',
            colDate: 'Date',
            unfiled: 'Never file',
          },
          dashboard: {
            fieldWorker: 'Field Worker',
            fieldWorkerTooltip: 'Simple mobile interface for field data collection — vitals, eCOA, documents, AI diagnosis',
          },
          settings: {
            title: 'Site Settings',
            selectCountry: 'Select country',
            statusActive: 'Active',
            reasonPlaceholder: 'Describe di reason for dis change...',
            brandingUpdatedTitle: 'Branding updated',
            brandingUpdatedDesc: 'Your site branding don update successfully',
            complianceInfra: 'Compliance & Infrastructure',
            assessmentsLifecycle: 'Assessments & Lifecycle',
            totRequiresOrg: 'Transfer of Obligations need organization link.',
          },
        },
      },
      cdst: {
        title: 'AI Clinical Decision Support',
        patientIntake: 'Patient Information',
        chiefComplaint: 'Main Problem',
        dangerSigns: 'Danger Signs',
        triageResult: 'Triage Result',
        emergency: 'Emergency',
        urgent: 'Urgent',
        standard: 'Normal',
        preventive: 'Prevention',
        referTo: 'Send go',
        trialEligible: 'Fit join Clinical Trial',
        suspectedCondition: 'Sickness wey dem suspect',
        recommendedAction: 'Wetin dem say make you do',
        offlineAssessment: 'Check am offline',
        fullAiAssessment: 'Full AI Check',
        connectivityRequired: 'You need internet to do full diagnosis',
        fever: 'Fever',
        cough: 'Cough',
        diarrhea: 'Diarrhea',
        headache: 'Headache',
        bodyPain: 'Body Pain',
        vomiting: 'Vomiting',
        breathingDifficulty: 'Hard to breathe',
        rash: 'Rash',
        convulsions: 'Convulsions',
        lethargy: 'Weak body',
      },
      chw: {
        aiDiagnosis: 'Use dis one to help know wetin dey do patient. Answer di questions about symptoms and di AI go suggest wetin e fit be. Always check with your oga for serious cases.',
        medicalInfo: 'Record di patient health history here — sickness wey dem get (like diabetes or HIV), medicine dem dey take, and wetin dey give dem reaction.',
        recordVitals: 'Measure and record di patient blood pressure, temperature, weight, height, and heart rate. Do am every visit.',
        labResults: 'Put laboratory test results here, or use di camera to scan printed lab report.',
        screening: 'Give patient clinical screening questionnaires. You fit give am for local language. Follow di steps well-well.',
        prescriptions: 'Give medicine from di clinic. Pick di drug, how much, and how long. Always check for allergies before you give am.',
        growth: 'Track pikin growth using WHO standards. Put weight, height, and head size.',
        immunizations: 'See and update pikin immunization schedule. Record vaccines wey dem give and book di next visit.',
        postnatalCare: 'Follow di WHO postnatal care schedule for mama and newborn dem.',
        ncd: 'Manage patients wey get long-time sickness using WHO PEN guidelines.',
        consultation: 'Start full clinical consultation for dis patient.',
      },
      checklist: {
        gettingStarted: 'How to Start',
        siteSetup: 'How to Start — Set up your place',
        phcFirstSteps: 'How to Start — Your First Steps',
        complete: 'complete',
        allSet: 'All good! You don ready.',
        dismiss: 'Leave am',
        addFirstPatient: 'Add your first patient',
        addFirstPatientDesc: 'Register or invite patient to your trial site.',
        inviteStaff: 'Invite staff member',
        inviteStaffDesc: 'Add coordinators, investigators, or nurses to your team.',
        scheduleVisit: 'Schedule visit for patient',
        scheduleVisitDesc: 'Create screening, follow-up, or completion visit.',
        registerFirstPatient: 'Register your first patient',
        registerFirstPatientDesc: 'Add new patient — put dem name, birthday, and consent.',
        completeConsultation: 'Finish consultation',
        completeConsultationDesc: 'Record symptoms, check-up, diagnosis, and treatment for patient.',
        syncData: 'Sync your data',
        syncDataDesc: 'Upload di data you collect offline go di main server.',
        completeTraining: 'Finish BlueCloudX training module',
        completeTrainingDesc: 'Learn clinical trial skills with free online courses.',
      },
      emptyStates: {
        noPatients: 'No patient don enrol yet',
        noPatientsDesc: 'Add your first patient to start.',
        noVisits: 'No visit dey scheduled',
        noVisitsDesc: 'Schedule screening, follow-up, or completion visit for patient.',
        noForms: 'No forms dem assign yet',
        noFormsDesc: 'Assign eCRF or eCOA forms to patients or set up auto-assignment.',
        noTrials: 'No active trials dey',
        noTrialsDesc: 'Add active trial from your sponsor contracts to begin enrollment.',
        noData: 'No data yet',
        noDataDesc: 'Data go show here as you use di platform.',
      },
      onboarding: {
        welcomePatient: 'Welcome to PTC BRIDGE™',
        welcomePatientDesc: 'Your platform for finding and joining clinical trials',
        completeProfile: 'Complete Your Profile',
        completeProfileDesc: 'Build your health profile for better trial matching',
        findTrials: 'Find Matching Trials',
        findTrialsDesc: 'Discover clinical trials using AI-powered matching',
        skipTutorial: 'Skip tutorial',
        getStarted: 'Start Now',
      },
      phc: {
        nav: {
          home: 'Home',
          register: 'Register',
          sync: 'Sync',
          help: 'Help',
          referrals: 'Referrals',
          aefi: 'Report AEFI',
          stats: 'Stats',
          supervisor: 'Supervisor',
          surveillance: 'Surveillance',
          dhis2: 'DHIS2',
          settings: 'Settings',
          logout: 'Comot',
          helpTooltip: 'Find help articles, training guides, and step-by-step instructions. E dey work even when network no dey!',
          surveillanceTooltip: 'Open di government surveillance dashboard for real-time disease monitoring and outbreak alerts',
          dhis2Tooltip: 'See di DHIS2 health intelligence dashboard',
        },
        toasts: {
          notAuthenticated: 'You never log in',
          syncFailed: 'Sync no work',
          syncComplete: 'Sync don finish',
          syncCompleteDesc: 'All data don update.',
          syncError: 'Sync get problem',
          unknownError: 'Problem wey we no sabi',
          signedOut: 'You don comot well well',
          signOutError: 'Wahala dey comot',
          surveillanceUnavailable: 'Surveillance portal no dey',
          surveillanceUnavailableDesc: 'No government surveillance portal connect to your facility.',
        },
        knowledgeBase: {
          title: 'Help & Knowledge Base - PHC',
          description: 'Find help articles, training guides, and step-by-step instructions for community health workers.',
          headerTitle: 'Help & Training',
        },
      },
      patientSelfTriage: {
        seo: {
          title: 'Check my symptoms | Patient Self-Check',
          description: 'Free symptom check wey private. E dey work even when network no dey. E go connect you to clinic wey near you.',
          resultTitle: 'Your check-up result | Health Passport',
        },
        back: 'Go back',
        disclaimer: 'Dis na self-check, <strong>e no be diagnosis</strong>. If you sick well well, go clinic now-now or call your community health worker.',
        voice: {
          cantRead: 'You no fit read? Talk instead.',
          guide: 'Talk for English or Swahili. Di helper go guide you.',
          start: 'Start',
        },
        header: {
          title: 'Check my symptoms',
          subtitle: 'Tick wetin dey worry you. We go tell you wetin to do. Dis no be doctor.',
        },
        field: {
          age: 'Your age (years)',
          sex: 'Sex wey you born with',
          sexUnknown: 'I no wan talk',
          sexFemale: 'Woman',
          sexMale: 'Man',
          pregnant: 'I get belle',
        },
        gps: {
          label: 'Find my clinic',
          use: 'Use my location',
          update: 'Update my location',
          findingClinic: 'We dey find clinic wey near you…',
          notSupported: 'Location no work for dis device.',
          failed: 'We no fit get location. You fit still continue.',
          listOffline: 'Clinic list no dey when network comot. We go save your check.',
        },
        callback: {
          label: 'Phone for call-back (no compulsory)',
          placeholder: '+234 8...',
          help: 'Health worker fit call you or send WhatsApp/SMS.',
        },
        consent: {
          prompt: 'Tell me about studies?',
          promptBody: 'If research study for my clinic fit help me, I wan know. I fit say no later. (Na general consent only.)',
          signedBy: 'Signed by',
          change: 'change am',
          open: 'Open consent (listen + sign)',
        },
        submit: {
          button: 'See my result',
          signInTip: 'Tip: sign in to save your checks for your Health Passport.',
          signInToSend: 'Your check don ready. Sign in make we fit send am to your clinic.',
          pickClinic: 'Abeg pick your clinic make we fit send your check.',
          savedOffline: 'We don save am offline. We go send when network come.',
          sent: 'We don send am to clinic wey near you.',
          savedRetry: 'We don save am for dis phone. We go try send am later.',
        },
        result: {
          notDiagnosis: 'Dis na check, no be doctor diagnosis.',
          dangerTitle: 'Danger signs dey',
          dangerBody: 'Abeg go clinic wey near you now-now. If you get phone, call your community health worker.',
          callbackNote: 'Health worker go call <strong>{{phone}}</strong> if e need.',
          trialConsentNote: 'You don gree learn about studies. Di clinic go check if any study fit help you.',
          checkAgain: 'Check again',
          downloadPdf: 'Download PDF',
          backToPassport: 'Go back to my passport',
          pdfSuccess: 'PDF don download',
          pdfError: 'We no fit make PDF',
        },
      },
    },
    // Amharic (am) — beta scaffold for AHRI / Ethiopia frontline CHW UX.
    // Minimal bundle: core common + navigation. All other keys fall back to EN.
    am: {
      translation: {
        common: {
          welcome: 'እንኳን ደህና መጡ',
          loading: 'በመጫን ላይ...',
          save: 'አስቀምጥ',
          cancel: 'ሰርዝ',
          submit: 'አስገባ',
          search: 'ፈልግ',
          success: 'ተሳክቷል',
          error: 'ስህተት',
          back: 'ተመለስ',
          next: 'ቀጥል',
          close: 'ዝጋ',
          yes: 'አዎ',
          no: 'አይ',
        },
        navigation: {
          home: 'መነሻ',
          dashboard: 'ዳሽቦርድ',
          profile: 'መገለጫ',
          login: 'ግባ',
          register: 'ተመዝገብ',
        },
      },
    },
    // Luganda (lg) — beta scaffold for MCRI / Uganda frontline CHW UX.
    // Minimal bundle: core common + navigation. All other keys fall back to EN.
    lg: {
      translation: {
        common: {
          welcome: 'Tukusanyukidde',
          loading: 'Tegeka...',
          save: 'Tereka',
          cancel: 'Sazaamu',
          submit: 'Weereza',
          search: 'Noonya',
          success: 'Kiwedde bulungi',
          error: 'Wabaddewo akakwate',
          back: 'Dda emabega',
          next: 'Eddako',
          close: 'Ggalawo',
          yes: 'Yee',
          no: 'Nedda',
        },
        navigation: {
          home: 'Awaka',
          dashboard: 'Daasibodi',
          profile: 'Pulofayilo',
          login: 'Yingira',
          register: 'Wewandiise',
        },
      },
    },
    // Dholuo (luo) — beta scaffold for K-IRDO / Kisumu OPTIMISE-TB frontline UX.
    // Minimal bundle: only well-attested Dholuo terms. All other keys fall back to EN
    // via fallbackLng. Expand as native-speaker review confirms additional strings.
    luo: {
      translation: {
        common: {
          welcome: 'Karibu',
          loading: 'Rito...',
          save: 'Kan',
          cancel: 'We',
          submit: 'Or',
          search: 'Manyo',
          success: 'Otimore maber',
          error: 'Bal',
          back: 'Dok chien',
          close: 'Lor',
          open: 'Yaw',
          yes: 'Ee',
          no: 'Ooyo',
        },
        navigation: {
          home: 'Dala',
          login: 'Donji',
          register: 'Ndik nyingi',
        },
        forms: {
          name: 'Nying',
          required: 'Dwarore',
        },
      },
    },
  },
};

// Get saved language from localStorage or default to 'en'
const getSavedLanguage = (): string => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('ptc-language') || 'en';
  }
  return 'en';
};

i18n
  .use(initReactI18next)
  .init({
    resources,
    lng: getSavedLanguage(),
    fallbackLng: 'en',
    interpolation: {
      escapeValue: false
    }
  });

// RTL languages list
const RTL_LANGUAGES = ['ar', 'he', 'fa', 'ur'];

// Set document direction based on language
function updateDocumentDirection(lang: string) {
  if (typeof document !== 'undefined') {
    const dir = RTL_LANGUAGES.includes(lang) ? 'rtl' : 'ltr';
    document.documentElement.dir = dir;
    document.documentElement.lang = lang;
  }
}

// Set initial direction
updateDocumentDirection(getSavedLanguage());

// Listen for language changes
i18n.on('languageChanged', (lang: string) => {
  updateDocumentDirection(lang);
});

// Dev helper for testing languages
if (typeof window !== 'undefined') {
  (window as any).setLang = (lang: string) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('ptc-language', lang);
    logger.debug(`Language changed to: ${lang}`);
  };
}

export default i18n;
